import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  alpha,
  useTheme,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Popover,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Controller, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import CalSSO from '../../utils/functions/CalSSO';
import CalTax from '../../utils/functions/CalTax';
import useFieldAllowanceDefaultPrefixes from '../../hooks/useFieldAllowanceDefaultPrefixes';
import { matchesProjectPrefix } from '../../utils/functions/projectNumberPrefix';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import EmployeeSalaryReport from '../Card/EmployeeSalaryReport';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmployeeName from '../EmployeeName';
import ProjectPopup from '../../views/Project/ProjectPopup';

const MONTH_NAMES = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
];

const fmtNum = (n, d = 2) => {
  if (n == null) return '-';
  return Number(n).toLocaleString('th-TH', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
};

// ── NetCell: subscribes only to this employee's form row — other rows never re-render on their keystrokes ──
const NetCell = React.memo(({ control, fi, fields }) => {
  const rowData = useWatch({ control, name: `salaryData.${fi}` }) || {};
  const f = fields[fi];
  if (!f) return null;
  // อ่าน salary จาก form (sync แล้วจาก displayAmt รวม override) fallback ไป totalAmount
  const sal = parseFloat(rowData?.revenue?.salary ?? f?.totalAmount ?? 0);
  const ot = parseFloat(rowData?.revenue?.overtime || 0);
  const all = parseFloat(
    rowData?.revenue?.allowances ??
      f?.revenue?.allowances ??
      f?.total_extra_day ??
      0,
  );
  const fieldAllow = parseFloat(rowData?.revenue?.field_allowance || 0);
  const bon = parseFloat(rowData?.revenue?.bonus || 0);
  const tax = parseFloat(rowData?.expenses?.tax ?? f?.expenses?.tax ?? 0);
  const sso = parseFloat(rowData?.expenses?.sso ?? f?.expenses?.sso ?? 0);
  const late = parseFloat(rowData?.expenses?.late ?? f?.expenses?.late ?? 0);
  const oth = parseFloat(rowData?.expenses?.other ?? f?.expenses?.other ?? 0);
  const net = sal + ot + all + fieldAllow + bon - tax - sso - late - oth;
  return (
    <span style={{ color: net >= 0 ? '#059669' : '#DC2626' }}>
      {fmtNum(net)}
    </span>
  );
});
NetCell.displayName = 'NetCell';

// ── TotalNetCell: subscribes to all salaryData for footer total — isolated from main table ──
const TotalNetCell = React.memo(
  ({ control, fields, fieldIndexMap, timestamp }) => {
    const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
    const totalNet = React.useMemo(() => {
      return (timestamp?.rows || []).reduce((acc, emp) => {
        const fi = fieldIndexMap[String(emp.employee_id)];
        if (fi === undefined) return acc;
        const f = fields[fi];
        const w = safeWatch[fi] || {};
        if (!f) return acc;
        const sal = parseFloat(w?.revenue?.salary ?? f?.totalAmount ?? 0);
        const ot = parseFloat(w?.revenue?.overtime || 0);
        const all = parseFloat(
          w?.revenue?.allowances ??
            f?.revenue?.allowances ??
            f?.total_extra_day ??
            0,
        );
        const fieldAllow = parseFloat(w?.revenue?.field_allowance || 0);
        const bon = parseFloat(w?.revenue?.bonus || 0);
        const tax = parseFloat(w?.expenses?.tax ?? f?.expenses?.tax ?? 0);
        const sso = parseFloat(w?.expenses?.sso ?? f?.expenses?.sso ?? 0);
        const late = parseFloat(w?.expenses?.late ?? f?.expenses?.late ?? 0);
        const oth = parseFloat(w?.expenses?.other ?? f?.expenses?.other ?? 0);
        return (
          acc + (sal + ot + all + fieldAllow + bon - tax - sso - late - oth)
        );
      }, 0);
    }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
    return (
      <span style={{ fontWeight: 700, fontSize: '14px' }}>
        {fmtNum(totalNet)}
      </span>
    );
  },
);
TotalNetCell.displayName = 'TotalNetCell';

// ── ActualNetCell: สุทธิจาก ค่าแรงชม.จริง + OT + เบี้ยเลี้ยง + โบนัส - ภาษี - sso - หักอื่นๆ (ไม่รวมหักสาย) ──
const ActualNetCell = React.memo(({ control, fi, fields, actualSalaryAmt }) => {
  const rowData = useWatch({ control, name: `salaryData.${fi}` }) || {};
  const f = fields[fi];
  if (!f) return null;
  const ot = parseFloat(rowData?.revenue?.overtime || 0);
  const all = parseFloat(
    rowData?.revenue?.allowances ??
      f?.revenue?.allowances ??
      f?.total_extra_day ??
      0,
  );
  const fieldAllow = parseFloat(rowData?.revenue?.field_allowance || 0);
  const bon = parseFloat(rowData?.revenue?.bonus || 0);
  const tax = parseFloat(rowData?.expenses?.tax ?? f?.expenses?.tax ?? 0);
  const sso = parseFloat(rowData?.expenses?.sso ?? f?.expenses?.sso ?? 0);
  const oth = parseFloat(rowData?.expenses?.other ?? f?.expenses?.other ?? 0);
  const net =
    (actualSalaryAmt || 0) + ot + all + fieldAllow + bon - tax - sso - oth;
  return (
    <span style={{ color: net >= 0 ? '#059669' : '#DC2626' }}>
      {fmtNum(net)}
    </span>
  );
});
ActualNetCell.displayName = 'ActualNetCell';

// ── TotalActualNetCell: ยอดรวมสุทธิ(ชม.จริง) ทุก row — isolated ──
const TotalActualNetCell = React.memo(
  ({ control, fields, fieldIndexMap, timestamp, empSummaryMap }) => {
    const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
    const total = React.useMemo(() => {
      return (timestamp?.rows || []).reduce((acc, emp) => {
        const fi = fieldIndexMap[String(emp.employee_id)];
        if (fi === undefined) return acc;
        const f = fields[fi];
        const w = safeWatch[fi] || {};
        if (!f) return acc;
        const actualSal =
          empSummaryMap?.[String(emp.employee_id)]?.actualSalaryAmt || 0;
        const ot = parseFloat(w?.revenue?.overtime || 0);
        const all = parseFloat(
          w?.revenue?.allowances ??
            f?.revenue?.allowances ??
            f?.total_extra_day ??
            0,
        );
        const fieldAllow = parseFloat(w?.revenue?.field_allowance || 0);
        const bon = parseFloat(w?.revenue?.bonus || 0);
        const tax = parseFloat(w?.expenses?.tax ?? f?.expenses?.tax ?? 0);
        const sso = parseFloat(w?.expenses?.sso ?? f?.expenses?.sso ?? 0);
        const oth = parseFloat(w?.expenses?.other ?? f?.expenses?.other ?? 0);
        return (
          acc + (actualSal + ot + all + fieldAllow + bon - tax - sso - oth)
        );
      }, 0);
    }, [timestamp, fields, safeWatch, fieldIndexMap, empSummaryMap]); // eslint-disable-line
    return (
      <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>
    );
  },
);
TotalActualNetCell.displayName = 'TotalActualNetCell';

// ── FieldAllowanceBadge: แสดง badge ค่าออกสนาม บน cell ชื่อพนักงาน — reactive กับ form ──
const FieldAllowanceBadge = React.memo(({ faEnabled, faAmt }) => {
  if (!faEnabled) return null;
  return (
    <div
      style={{
        position: 'absolute',
        top: 2,
        right: 4,
        background: '#dcfce7',
        border: '1px solid #86efac',
        borderRadius: 4,
        padding: '1px 5px',
        fontSize: '10px',
        fontWeight: 700,
        color: '#15803d',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        zIndex: 1,
      }}
    >
      +
      {faAmt > 0
        ? Number(faAmt).toLocaleString('th-TH', { maximumFractionDigits: 0 })
        : '?'}
    </div>
  );
});
FieldAllowanceBadge.displayName = 'FieldAllowanceBadge';

// ── ManualDot: orange dot แสดงว่า field นี้ถูก HR แก้ไขเองใน table ──
const ManualDot = React.memo(({ control, fi, fieldName }) => {
  const manualFields =
    useWatch({ control, name: `salaryData.${fi}.manual_fields` }) || [];
  if (!manualFields.includes(fieldName)) return null;
  return (
    <span
      title="แก้ไขโดย HR"
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: '#f97316',
        flexShrink: 0,
        marginLeft: 1,
        alignSelf: 'center',
      }}
    />
  );
});
ManualDot.displayName = 'ManualDot';

// ── column widths (px) ───────────────────────────────────────────────────
const RW = {
  manage: 44,
  note: 130,
  net: 120,
  other: 100,
  late: 100,
  sso: 100,
  tax: 100,
  bonus: 100,
  allow: 100,
  field: 100,
  ot: 100,
  actualNet: 120,
  actualSalary: 100,
  salary: 100,
  dayRate: 82,
  working: 66,
};
// right-offsets kept for reference (sticky disabled — functions return {})
const R = {
  manage: 0,
  note: RW.manage,
  net: RW.manage + RW.note,
  other: RW.manage + RW.note + RW.net,
  late: RW.manage + RW.note + RW.net + RW.other,
  sso: RW.manage + RW.note + RW.net + RW.other + RW.late,
  tax: RW.manage + RW.note + RW.net + RW.other + RW.late + RW.sso,
  bonus: RW.manage + RW.note + RW.net + RW.other + RW.late + RW.sso + RW.tax,
  allow:
    RW.manage +
    RW.note +
    RW.net +
    RW.other +
    RW.late +
    RW.sso +
    RW.tax +
    RW.bonus,
  ot:
    RW.manage +
    RW.note +
    RW.net +
    RW.other +
    RW.late +
    RW.sso +
    RW.tax +
    RW.bonus +
    RW.allow,
  salary:
    RW.manage +
    RW.note +
    RW.net +
    RW.other +
    RW.late +
    RW.sso +
    RW.tax +
    RW.bonus +
    RW.allow +
    RW.ot,
  dayRate:
    RW.manage +
    RW.note +
    RW.net +
    RW.other +
    RW.late +
    RW.sso +
    RW.tax +
    RW.bonus +
    RW.allow +
    RW.ot +
    RW.salary,
  working:
    RW.manage +
    RW.note +
    RW.net +
    RW.other +
    RW.late +
    RW.sso +
    RW.tax +
    RW.bonus +
    RW.allow +
    RW.ot +
    RW.salary +
    RW.dayRate,
};
const LW = { no: 38, name: 210, type: 48 };

const mkInput = (color = '#15803d') => ({
  width: '100%',
  textAlign: 'right',
  border: '1px solid #e2e8f0',
  borderRadius: 5,
  padding: '3px 6px',
  fontSize: '0.78rem',
  outline: 'none',
  color,
  background: 'transparent',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s',
});

const BG = {
  headerText: '#111827',
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  rowEven: '#FFFFFF',
  rowOdd: '#FFFFFF',
  rowHover: '#F9FAFB',
  saturdayBg: '#FFF5F5', // body — เสาร์ (แดงอ่อน)
  sundayBg: '#FEE2E2', // body — อาทิตย์ (แดงเข้มกว่าเสาร์)
  weekendText: '#DC2626',
  footer: '#D1D5DB',
  text: '#1F2937',
  textMuted: '#6B7280',
  subText: '#6B7280',
  // แถว group header บนสุด — กลุ่มละสี
  hInfo: 'rgb(var(--color-primary-200))', // ข้อมูลพนักงาน — indigo
  hDays: '#BAE6FD', // การลงเวลา — sky
  hSum: '#FDE68A', // สรุป — amber
  hIncome: '#BBF7D0', // รายได้ — green
  hDeduct: '#FECACA', // หัก — red
  hNet: '#DDD6FE', // สุทธิ / จัดการ — violet
  // แถว column header ล่าง — โทนเข้มกว่าแถวบนในกลุ่มเดียวกัน
  cInfo: 'rgb(var(--color-primary-300))', // No., ชื่อ, Type
  cDay: '#7DD3FC', // วันที่ 1–31 (วันธรรมดา)
  cSaturday: '#FECACA', // header — เสาร์ (แดงอ่อน)
  cSunday: '#FCA5A5', // header — อาทิตย์ (แดงเข้มกว่าเสาร์)
  cToday: '#FBBF24', // วันนี้ (header)
  cSum: '#FCD34D', // วันทำงาน, Day Rate
  cIncome: '#86EFAC', // ค่าแรง, OT, เบี้ยเลี้ยง, เงินได้อื่นๆ
  cDeduct: '#FCA5A5', // หักภาษี, ประกันสังคม, หักเงินสาย, หักอื่นๆ
  cNet: '#C4B5FD', // สุทธิ, หมายเหตุ, จัดการ
};

const th0 = (extra = {}) => ({
  fontWeight: 700,
  fontSize: '13px',
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  padding: '12px 8px',
  textAlign: 'center',
  color: BG.headerText,
  borderBottom: `1px solid ${BG.border}`,
  borderRight: `1px solid ${BG.border}`,
  lineHeight: 1.35,
  ...(extra.position === 'sticky' ? { willChange: 'transform' } : {}),
  ...extra,
});
const td0 = (extra = {}) => ({
  padding: '10px 6px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 400,
  borderRight: `1px solid ${BG.border}`,
  borderBottom: `1px solid ${BG.border}`,
  lineHeight: 1.4,
  color: BG.text,
  verticalAlign: 'middle',
  ...(extra.position === 'sticky' ? { willChange: 'transform' } : {}),
  ...extra,
});
const dayTh = (dow, isToday) =>
  th0({
    background: isToday
      ? BG.cToday
      : dow === 6
      ? BG.cSaturday
      : dow === 0
      ? BG.cSunday
      : BG.cDay,
    minWidth: 52,
    width: 52,
    padding: '10px 8px',
    fontWeight: 700,
    borderBottom: `2px solid ${BG.border}`,
    borderRight: `1px solid ${BG.border}`,
    position: 'sticky',
    top: 34,
    zIndex: 30,
  });
const weekendCellBg = (dow) =>
  dow === 6 ? BG.saturdayBg : dow === 0 ? BG.sundayBg : null;
const dayTdBase = (extra = {}) => ({
  padding: '4px 8px',
  textAlign: 'center',
  borderRight: `1px solid ${BG.border}`,
  borderBottom: `1px solid ${BG.border}`,
  verticalAlign: 'middle',
  ...extra,
});
const dayCellInner = (extra = {}) => ({
  padding: '6px 4px',
  minHeight: 24,
  fontSize: '13px',
  lineHeight: 1.35,
  ...extra,
});

const LEAVE_ABBR = {
  SICK: 'ป่วย',
  PERSONAL: 'กิจ',
  VACATION: 'พร.',
  MATERNITY: 'คลอด',
  ORDINATION: 'บวช',
  UNPAID: 'ไม่รับ',
};
const LEAVE_COLOR = {
  SICK: { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  PERSONAL: { bg: '#ede9fe', border: '#7c3aed', text: '#5b21b6' },
  VACATION: { bg: '#d1fae5', border: '#059669', text: '#065f46' },
  MATERNITY: { bg: '#fef9c3', border: '#d97706', text: '#92400e' },
  ORDINATION: { bg: '#ffedd5', border: '#ea580c', text: '#9a3412' },
  UNPAID: { bg: '#f1f5f9', border: '#64748b', text: '#334155' },
};

function MergedPayrollTable({
  timestamp,
  month,
  year,
  days,
  selectedOption,
  permission,
  me,
  onSalaryOverride,
  onRefreshTimestamp,
  onSkipLateDeduct,
  manualWorkData = [],
  leaveData = [],
  onManualSave,
  onManualDelete,
  fields = [],
  control,
  getValues,
  setValue,
  remove,
  onOpenCheckinModal,
  typeSelect = 'DAILY',
  attendanceRules = [],
  onAdjSave,
  onCheckInTimeUpdate,
  onCheckInTimeClear,
  onProjectUpdate,
  onProjectClear,
  onHourOverride,
  onOtOverride,
  isLoading = false,
  dataVersion = 0,
  projectList = [],
  employees = [],
  showFormulas = false,
  salaryMode = 'late-deduct',
}) {
  // รหัสโครงการที่ได้ค่าออกสนามเป็นค่าเริ่มต้น (ตั้งที่ /management/project-prefixes)
  const faDefaultPrefixes = useFieldAllowanceDefaultPrefixes();
  const theme = useTheme();
  const RENDER_CHUNK = 8;
  const [renderLimit, setRenderLimit] = useState(RENDER_CHUNK);

  // reset + progressive render เมื่อข้อมูลเปลี่ยน
  useEffect(() => {
    setRenderLimit(RENDER_CHUNK);
  }, [dataVersion, timestamp?.rows?.length]); // eslint-disable-line

  useEffect(() => {
    const total = timestamp?.rows?.length || 0;
    if (renderLimit >= total) return;
    const t = setTimeout(() => {
      setRenderLimit((p) => Math.min(p + RENDER_CHUNK, total));
    }, 20);
    return () => clearTimeout(t);
  }, [renderLimit, timestamp?.rows?.length]); // eslint-disable-line

  const totalEmp = timestamp?.rows?.length || 0;
  const isRendering = renderLimit < totalEmp;
  const renderPct =
    totalEmp > 0
      ? Math.round((Math.min(renderLimit, totalEmp) / totalEmp) * 100)
      : 100;

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [dlgFieldAllow, setDlgFieldAllow] = useState(false);
  const [forceFullHours, setForceFullHours] = useState(false);
  const [editAmtManual, setEditAmtManual] = useState(null); // null = auto-compute
  const [detailHours, setDetailHours] = useState('');
  const [detailRate, setDetailRate] = useState(1);
  const [saving, setSaving] = useState(false);
  const [skipLateSaving, setSkipLateSaving] = useState(false);
  const [skipLateDvSaving, setSkipLateDvSaving] = useState(false);
  const [editCheckIn, setEditCheckIn] = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [checkTimeSaving, setCheckTimeSaving] = useState(false);
  const [editProjectId, setEditProjectId] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [addCheckIn, setAddCheckIn] = useState('');
  const [addCheckOut, setAddCheckOut] = useState('');
  const [addAmt, setAddAmt] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addWorkRate, setAddWorkRate] = useState(1);
  const [addIncludeAllowance, setAddIncludeAllowance] = useState(false);
  const [addOtOpen, setAddOtOpen] = useState(false);
  const [addOtTarget, setAddOtTarget] = useState(null);
  const [addOtHours, setAddOtHours] = useState('');
  const [addOtRate, setAddOtRate] = useState(1.5);
  const [addOtSaving, setAddOtSaving] = useState(false);
  const [bulkDayOpen, setBulkDayOpen] = useState(false);
  const [bulkDay, setBulkDay] = useState(null);
  const [bulkTab, setBulkTab] = useState(0);
  const [bulkCheckIn, setBulkCheckIn] = useState('08:00');
  const [bulkCheckOut, setBulkCheckOut] = useState('17:00');
  const [bulkRate, setBulkRate] = useState(1);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [calcPopover, setCalcPopover] = useState({
    anchor: null,
    title: '',
    content: null,
  });
  const closeCp = () =>
    setCalcPopover({ anchor: null, title: '', content: null });
  const [reportEmp, setReportEmp] = useState(null);
  const [popupProjectId, setPopupProjectId] = useState(null);
  const [fillSaving, setFillSaving] = useState(false);
  const scrollRef = useRef(null);
  const drag = useRef({
    active: false,
    startX: 0,
    startY: 0,
    sL: 0,
    sT: 0,
    moved: false,
  });
  const adjDebounceRef = useRef({});
  const autoValuesRef = useRef({});
  const dirtyFieldsRef = useRef({});
  const lastRawValueRef = useRef({});
  const [grabbing, setGrabbing] = useState(false);
  const onScrollDown = (e) => {
    if (e.button !== 0 || e.target.closest('button,input,a,select')) return;
    const el = scrollRef.current;
    if (!el) return;
    drag.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      sL: el.scrollLeft,
      sT: el.scrollTop,
      moved: false,
    };
    setGrabbing(true);
  };
  const onScrollMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
    drag.current.moved = true;
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = drag.current.sL - dx;
      el.scrollTop = drag.current.sT - dy;
    }
  };
  const onScrollUp = () => {
    drag.current.active = false;
    setGrabbing(false);
  };
  const onScrollClickCapture = (e) => {
    if (drag.current.moved) {
      e.stopPropagation();
      drag.current.moved = false;
    }
  };
  const [dvOpen, setDvOpen] = useState(false);
  const [dvData, setDvData] = useState(null);
  const [otDvOpen, setOtDvOpen] = useState(false);
  const [otDvData, setOtDvData] = useState(null);
  const openOtDv = (e, otReq, manualEntry, empName, checkin, day) => {
    e.preventDefault();
    setOtDvData({ otReq, manualEntry, empName, checkin, day });
    setOtDvOpen(true);
  };
  const openDv = (
    e,
    checkin,
    empName,
    manualEntry,
    spd,
    leave,
    leaveAutoH,
    dayNum,
  ) => {
    e.preventDefault();
    const autoAmt =
      checkin?.totalTime != null
        ? Number((checkin.totalTime * (spd || 0)).toFixed(2))
        : checkin?.totalHour != null
        ? Number(((checkin.totalHour / 8) * (spd || 0)).toFixed(2))
        : null;
    setDvData({
      checkin,
      empName,
      manualEntry,
      autoAmt,
      leave,
      leaveAutoH: leaveAutoH || 0,
      dayNum,
    });
    setDvOpen(true);
  };

  const fillSundays = async () => {
    if (!onManualSave || !timestamp?.rows?.length) return;
    const sundays = days.filter(
      (d) => dayjs(`${year}-${month + 1}-${d}`).day() === 0,
    );
    if (!sundays.length) return;
    setFillSaving(true);
    try {
      const tasks = [];
      timestamp.rows.forEach((emp) => {
        const empId = String(emp.employee_id);
        sundays.forEach((d) => {
          const hasCheckin = emp.checkins?.some(
            (c) =>
              dayjs(c?.checkIn).format('D/M/YY') ===
              `${d}/${month + 1}/${year % 100}`,
          );
          const hasMn = getManualHours(empId, d) > 0;
          if (!hasCheckin && !hasMn) {
            const mm = String(month + 1).padStart(2, '0');
            const dd = String(d).padStart(2, '0');
            tasks.push(
              onManualSave({
                employee_id: empId,
                employee_name: emp.name,
                date: `${year}-${mm}-${dd}T00:00:00.000Z`,
                hours: 8,
                salary_per_day: emp.salary_per_day,
                amount: emp.salary_per_day || 0,
                edited_by: me?.firstname ? formatEmployeeThaiName(me) : 'ไม่ระบุ',
              }),
            );
          }
        });
      });
      await Promise.all(tasks);
    } finally {
      setFillSaving(false);
    }
  };

  const editorName = me?.firstname ? formatEmployeeThaiName(me) : 'ไม่ระบุ';
  const hrMgmt = permission?.hr_management;
  const today = dayjs();
  const isCurrentMonth = today.year() === year && today.month() === month;
  const todayDay = today.date();
  const DAY_TH_ABBR = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

  const fieldIndexMap = useMemo(() => {
    const m = {};
    fields.forEach((f, i) => {
      m[String(f.employee_id)] = i;
    });
    return m;
  }, [fields]);

  // O(1) lookup: key = `${empId}-${dayOfMonth}`
  const manualByKey = useMemo(() => {
    const map = {};
    manualWorkData.forEach((e) => {
      const d = new Date(e.date);
      if (d.getUTCFullYear() !== year || d.getUTCMonth() + 1 !== month + 1)
        return;
      map[`${e.employee_id}-${d.getUTCDate()}`] = e;
    });
    return map;
  }, [manualWorkData, year, month]);

  // O(1) lookup: checkin ต่อ empId+day
  const checkinByKey = useMemo(() => {
    const map = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      (emp.checkins || []).forEach((c) => {
        if (!c?.checkIn) return;
        const d = dayjs(c.checkIn);
        if (d.year() !== year || d.month() !== month) return;
        map[`${empId}-${d.date()}`] = c;
      });
    });
    return map;
  }, [timestamp, year, month]);

  // O(1) lookup: leave ต่อ empId+day (expand range ออกเป็น day keys)
  const leaveByKey = useMemo(() => {
    const map = {};
    const dim = dayjs(`${year}-${month + 1}-01`).daysInMonth();
    (leaveData || []).forEach((lr) => {
      const empId = String(lr.employee?._id || lr.employee);
      const s = dayjs(lr.start_date).startOf('day');
      const e = dayjs(lr.end_date).endOf('day');
      for (let d = 1; d <= dim; d++) {
        const t = dayjs(
          `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(
            2,
            '0',
          )}`,
        );
        if (!t.isBefore(s) && !t.isAfter(e)) map[`${empId}-${d}`] = lr;
      }
    });
    return map;
  }, [leaveData, year, month]);

  const getEntry = (empId, day) =>
    manualByKey[`${String(empId)}-${day}`] || null;
  const getManualHours = (empId, day) =>
    manualByKey[`${String(empId)}-${day}`]?.hours || 0;
  const getManualOtHours = (empId, day) =>
    manualByKey[`${String(empId)}-${day}`]?.ot_hours || 0;

  // Pre-compute day-of-week per day once — shared by header + every body row (replaces O(emp×day) dayjs calls)
  const dayMeta = useMemo(() => {
    return days.map((day) => {
      const dow = new Date(year, month, day).getDay();
      return {
        day,
        dow,
        isWeekend: dow === 0 || dow === 6,
        isToday: isCurrentMonth && day === todayDay,
      };
    });
  }, [days, year, month, isCurrentMonth, todayDay]);

  // sync ค่า OT column ใน form ทุกครั้งที่ manualWorkData เปลี่ยน (คำนวณ delta: override = แทนที่, addition = บวกเพิ่ม)
  useEffect(() => {
    if (!setValue || !fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      let delta = 0;
      days.forEach((d) => {
        const e = getEntry(empId, d);
        if (!e || !e.ot_hours) return;
        const manualAmt =
          e.ot_amount != null
            ? e.ot_amount
            : e.ot_hours * ((emp.salary_per_day || 0) / 8) * (e.ot_rate || 1.5);
        const ck = checkinByKey[`${String(emp.employee_id)}-${d}`];
        const approvedH = ck
          ? parseFloat(ck.otRequests?.total_hours?.toFixed(2)) || 0
          : 0;
        const approvedAmt =
          (ck?.otEffectiveHours || 0) * ((emp.salary_per_day || 0) / 8);
        delta += approvedH > 0 ? manualAmt - approvedAmt : manualAmt;
      });
      const total = Math.max(0, (emp.totalOtAmount || 0) + delta);
      setValue(
        `salaryData.${fi}.revenue.overtime`,
        parseFloat(total.toFixed(2)),
      );
    });
  }, [manualWorkData, fields]); // eslint-disable-line

  // sync ค่า หักสาย ทุกครั้งที่ data หรือ rules เปลี่ยน — คำนวณเหมือน calcDayLateDeduction ใน EmployeeSalaryReport
  useEffect(() => {
    if (!setValue || !fields.length) return;
    const lRule = (attendanceRules || []).find(
      (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
    );
    const WS = 8 * 60;
    (timestamp?.rows || []).forEach((emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return;
      const spm = emp.salary_per_month || 0;
      const spd = emp.salary_per_day || 0;
      let lateTotal = 0;
      // FULLTIME รายเดือน: ไม่หักสาย
      if (emp.emp_type === 'FULLTIME' && spm > 0) {
        setValue(`salaryData.${fi}.expenses.late`, 0);
        return;
      }
      const uniqueCk = [];
      const seen = new Set();
      [...(emp.checkins || [])].reverse().forEach((ck) => {
        const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
        if (!seen.has(dk)) {
          seen.add(dk);
          uniqueCk.unshift(ck);
        }
      });
      uniqueCk.forEach((ck) => {
        if (ck.is_manual || ck.skip_late_deduct?.enabled) return;
        if (ck.hour_override?.hours != null) return;
        const cit = ck.checkInTime;
        if (!cit) return;
        const [citH, citM] = cit.split(':').map(Number);
        const shiftStart = citH >= 12 ? 13 * 60 : WS;
        const lateMin = Math.max(0, citH * 60 + citM - shiftStart);
        if (!lateMin) return;
        let effMin;
        if (!lRule) {
          effMin = lateMin;
        } else {
          if (lateMin < lRule.threshold_minutes) return;
          effMin = Math.max(lateMin, lRule.deduct_minutes || 0);
        }
        const baseDr =
          ck.salary_override?.day_rate ?? (spm > 0 ? spm / 30 : spd);
        const ckRate =
          ck.salary_override?.rate ?? ck.effectiveRate ?? ck.rate ?? 1;
        const ckMinRate = (baseDr * ckRate) / 8 / 60;
        lateTotal += Number((effMin * ckMinRate).toFixed(2));
      });
      setValue(
        `salaryData.${fi}.expenses.late`,
        parseFloat(lateTotal.toFixed(2)),
      );
    });
  }, [timestamp, attendanceRules, fields]); // eslint-disable-line

  // DAILY: backend รวม manual ใน totalAmount ผ่าน totalWork → ข้าม timestamp_ref เพื่อกัน double-count
  // FULLTIME: backend ตั้ง checkinAmount=0 สำหรับ manual entry → ต้องนับที่นี่เสมอ
  const getManualAmt = (empId, spd, empType) =>
    days.reduce((s, d) => {
      const e = getEntry(empId, d);
      if (!e) return s;
      if (empType !== 'FULLTIME' && e.timestamp_ref) return s;
      return s + (e.amount != null ? e.amount : (e.hours / 8) * (spd || 0));
    }, 0);

  // O(1) lookup แทน O(n) array.find()
  const getLeaveForDay = (empId, day) =>
    leaveByKey[`${String(empId)}-${day}`] || null;

  // Pre-compute per-employee heavy values ทั้งหมดใน 1 pass (แทนการคำนวณซ้ำใน JSX loop)
  const empSummaryMap = useMemo(() => {
    const result = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const spd = emp.salary_per_day || 0;
      const hrR = spd / 8;
      const isFulltime = emp.emp_type === 'FULLTIME';

      // workingDays + leaveHalfDays
      const workingDays = [];
      let leaveHalfDays = 0;
      days.forEach((d) => {
        const lv = leaveByKey[`${empId}-${d}`];
        const ck = checkinByKey[`${empId}-${d}`];
        const entry = manualByKey[`${empId}-${d}`];
        if (lv && lv.period === 'FULL_DAY') {
          if (isFulltime) workingDays.push(d);
        } else {
          const mh = entry?.hours || 0;
          if (ck || (mh > 0 && !entry?.timestamp_ref)) workingDays.push(d);
          if (isFulltime && lv && lv.period !== 'FULL_DAY' && !ck)
            leaveHalfDays += 0.5;
        }
      });

      // displayCheckEff = sum of rate per working day + leaveHalfDays
      const displayCheckEff =
        workingDays.reduce((sum, d) => {
          const ck = checkinByKey[`${empId}-${d}`];
          return (
            sum +
            (ck ? (ck.is_manual ? 1 : ck.effectiveRate || ck.rate || 1) : 1)
          );
        }, 0) + leaveHalfDays;

      // leave salary contribution (FULLTIME เท่านั้น)
      // full-day = 8h credit, half-day = 4h credit (checkin จะบวกเพิ่มอีกไม่เกิน 4h)
      let leaveSalaryAmt = 0;
      if (isFulltime) {
        days.forEach((d) => {
          const lv = leaveByKey[`${empId}-${d}`];
          if (!lv) return;
          leaveSalaryAmt += lv.period === 'FULL_DAY' ? spd : spd / 2;
        });
      }

      // deduplicate checkins per day (last checkin per date wins) — ตรงกับ popup
      const seenCkDates = new Set();
      const uniqueCheckins = [];
      for (const ck of [...(emp.checkins || [])].reverse()) {
        const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
        if (!seenCkDates.has(dk)) {
          seenCkDates.add(dk);
          uniqueCheckins.unshift(ck);
        }
      }

      // actual-hours salary — ใช้ชม.เดียวกับ day cell (hour_override ถ้ามี, ไม่งั้น totalHour)
      // FULLTIME: full-day leave วันนั้น skip checkin (leave ครอบแล้ว), half-day leave cap checkin ที่ 4h
      const actualCheckinSalary = uniqueCheckins.reduce((s, ck) => {
        const ckDay = dayjs(ck.checkIn).date();
        const lv = isFulltime ? leaveByKey[`${empId}-${ckDay}`] : null;
        if (lv?.period === 'FULL_DAY') return s;
        let actualH = ck.hour_override?.hours ?? ck.totalHour ?? 0;
        if (lv) actualH = Math.min(actualH, 4);
        const er = ck.effectiveRate || ck.rate || 1;
        return s + (actualH / 8) * er * spd;
      }, 0);

      // checkin salary — นับทั้ง regular และ is_manual เพื่อให้ตรงกับ salaryBreakdownTotal ใน popup
      // FULLTIME: full-day leave วันนั้น skip checkin, half-day leave cap checkin ที่ 4h
      const checkinSalary = uniqueCheckins.reduce((s, ck) => {
        const ckDay = dayjs(ck.checkIn).date();
        const lv = isFulltime ? leaveByKey[`${empId}-${ckDay}`] : null;
        if (lv?.period === 'FULL_DAY') return s;
        if (ck.salary_override?.amount != null) {
          const ovrR = ck.salary_override.rate ?? ck.effectiveRate ?? 1;
          const ovrDr = ck.salary_override.day_rate ?? spd;
          const amt = ovrR * ovrDr;
          return s + (lv ? Math.min(amt, spd / 2) : amt);
        }
        const citH2 = ck.checkInTime
          ? parseInt(ck.checkInTime.split(':')[0], 10)
          : 0;
        const displayH = lv ? 4 : citH2 >= 12 ? 4 : 8;
        const er = ck.effectiveRate || ck.rate || 1;
        return s + (displayH / 8) * er * spd;
      }, 0);

      // manual salary amount
      const manualAmt = days.reduce((s, d) => {
        const entry = manualByKey[`${empId}-${d}`];
        if (!entry) return s;
        if (!isFulltime && entry.timestamp_ref) return s;
        return (
          s + (entry.amount != null ? entry.amount : (entry.hours / 8) * spd)
        );
      }, 0);

      // net OT
      let netOtH = emp.totalOtHour || 0;
      let netOtEffH = (emp.checkins || []).reduce(
        (s, c) => s + (c?.otEffectiveHours || 0),
        0,
      );
      let netOtAmtDelta = 0;
      days.forEach((d) => {
        const entry = manualByKey[`${empId}-${d}`];
        if (!entry?.ot_hours) return;
        const rate = entry.ot_rate || 1.5;
        const ck = checkinByKey[`${empId}-${d}`];
        const approvedH = ck
          ? parseFloat(ck.otRequests?.total_hours?.toFixed(2)) || 0
          : 0;
        const approvedEffH = ck?.otEffectiveHours || 0;
        const manOtAmt =
          entry.ot_amount != null
            ? entry.ot_amount
            : entry.ot_hours * hrR * rate;
        const approvedAmt = approvedEffH * hrR;
        netOtH += approvedH > 0 ? entry.ot_hours - approvedH : entry.ot_hours;
        netOtEffH +=
          approvedH > 0
            ? entry.ot_hours * rate - approvedEffH
            : entry.ot_hours * rate;
        netOtAmtDelta += approvedH > 0 ? manOtAmt - approvedAmt : manOtAmt;
      });

      result[empId] = {
        workingDays,
        leaveHalfDays,
        displayCheck: workingDays.length + leaveHalfDays,
        displayCheckEff,
        manualAmt,
        // FULLTIME (รายเดือน): ได้รับ salary_per_month เต็มเสมอ ไม่ขึ้นกับ checkin/ลา/สาย
        displayAmt: isFulltime && emp.salary_per_month > 0
          ? emp.salary_per_month
          : checkinSalary + leaveSalaryAmt + manualAmt,
        actualSalaryAmt: isFulltime && emp.salary_per_month > 0
          ? emp.salary_per_month
          : actualCheckinSalary + leaveSalaryAmt + manualAmt,
        netOtH: Math.max(0, netOtH),
        netOtEffH: Math.max(0, netOtEffH),
        netOtAmtDelta,
      };
    });
    return result;
  }, [timestamp, days, manualByKey, checkinByKey, leaveByKey]); // eslint-disable-line

  // sync SSO + salary column ให้ตรงกับค่าที่คำนวณจริง (displayAmt รวม salary_override) — ต้องอยู่หลัง empSummaryMap
  useEffect(() => {
    if (!setValue || !fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const f = fields[fi];
      const mf = f?.manual_fields || [];
      const summary = empSummaryMap[empId] || {};
      const displayAmt = summary.displayAmt || 0;
      const salaryBase =
        salaryMode === 'work-time' ? summary.actualSalaryAmt || 0 : displayAmt;
      setValue(`salaryData.${fi}.revenue.salary`, salaryBase.toFixed(2));
      if (!f?.sso_tax?.status) return;
      const ssoPaid = f.sso_paid || 0;
      const ssoRate = f.sso_tax?.number || 5;
      let ssoAuto = salaryBase * (ssoRate / 100);
      if (ssoAuto > 0) {
        if (ssoAuto + ssoPaid > 875) ssoAuto = Math.max(0, 875 - ssoPaid);
        if (ssoAuto + ssoPaid < 83) ssoAuto = Math.max(0, 83 - ssoPaid);
      }
      const ssoVal = ssoAuto.toFixed(2);
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      autoValuesRef.current[empId].sso = ssoVal;
      if (!mf.includes('sso'))
        setValue(`salaryData.${fi}.expenses.sso`, ssoVal);
    });
  }, [empSummaryMap, fields, salaryMode]); // eslint-disable-line

  // sync allowances column ให้ตรงกับสูตรเดียวกับ popup (buildAllowanceContent)
  useEffect(() => {
    if (!setValue || !fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const mf = fields[fi]?.manual_fields || [];
      const rateFromCheckin =
        (emp.checkins || []).find((c) => (c.extra_day || 0) > 0)?.extra_day ||
        0;
      const rateFromManual =
        days
          .map((d) => manualByKey[`${empId}-${d}`]?.allowance_amount || 0)
          .find((a) => a > 0) || 0;
      const dailyRate = rateFromCheckin || rateFromManual;
      let allowTotal = 0;
      days.forEach((d) => {
        const ck = checkinByKey[`${empId}-${d}`];
        const entry = manualByKey[`${empId}-${d}`];
        const hasWork = ck || (entry?.hours > 0 && !entry?.timestamp_ref);
        if (!hasWork) return;
        const amt = ck?.extra_day || entry?.allowance_amount || dailyRate;
        if (!amt) return;
        allowTotal += amt;
      });
      const allowVal = allowTotal.toFixed(2);
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      autoValuesRef.current[empId].allowances = allowVal;
      if (!mf.includes('allowances'))
        setValue(`salaryData.${fi}.revenue.allowances`, allowVal);
    });
  }, [checkinByKey, manualByKey, fields]); // eslint-disable-line

  // sync field_allowance column — include ทุกวันที่มี checkin เมื่อ faEnabled=true
  // เว้นแต่ HR ปิดเองผ่าน dialog (field_allowance_day === false)
  useEffect(() => {
    if (!setValue || !fields.length) return;
    const faGlobalRate =
      timestamp?.company_setting?.payroll?.field_allowance_rate || 0;
    const empMap = {};
    employees.forEach((e) => {
      empMap[String(e._id || e.id)] = e;
    });
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const empFA2 =
        emp.field_allowance ||
        empMap[String(emp.employee_id)]?.field_allowance ||
        null;
      const faDay =
        empFA2?.override_amount != null ? empFA2.override_amount : faGlobalRate;
      if (!faDay) return;
      const faHrRate = faDay / 8;
      let total = 0;
      const seenDates = new Set();
      const uniqueCheckins = [];
      [...(emp.checkins || [])].reverse().forEach((ck) => {
        const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
        if (!seenDates.has(dk)) {
          seenDates.add(dk);
          uniqueCheckins.unshift(ck);
        }
      });
      uniqueCheckins.forEach((ck) => {
        const savedFA = ck.salary_override?.field_allowance_day;
        const pn = (ck.project_number || '').toLowerCase().trim();
        const defaultFA = matchesProjectPrefix(pn, faDefaultPrefixes);
        const isFA = savedFA != null ? savedFA : defaultFA;
        if (!isFA) return;
        const er = ck.effectiveRate || ck.rate || 1;
        const actualH = ck.hour_override?.hours ?? ck.totalHour ?? 0;
        const faHours = Math.min(actualH > 0 ? actualH : 8, 8);
        total += Number((faHours * er * faHrRate).toFixed(2));
        // OT field allowance
        const day = dayjs(ck.checkIn).date();
        const manEntry = manualByKey[`${empId}-${day}`];
        const approvedOtH = ck.otRequests?.total_hours || 0;
        const effH = ck.otEffectiveHours || 0;
        let otHours = 0;
        let otRate = 1.5;
        if (approvedOtH > 0) {
          if (manEntry?.ot_hours) {
            otHours = manEntry.ot_hours;
            otRate = manEntry.ot_rate || 1.5;
          } else {
            otHours = approvedOtH;
            otRate =
              approvedOtH > 0
                ? Math.round((effH / approvedOtH) * 100) / 100
                : 1.5;
          }
        } else if (manEntry?.ot_hours) {
          otHours = manEntry.ot_hours;
          otRate = manEntry.ot_rate || 1.5;
        }
        if (otHours > 0)
          total += Number((otHours * otRate * faHrRate).toFixed(2));
      });
      const faVal = total.toFixed(2);
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      autoValuesRef.current[empId].field_allowance = faVal;
      if (!(fields[fi]?.manual_fields || []).includes('field_allowance')) {
        setValue(`salaryData.${fi}.revenue.field_allowance`, faVal);
      }
    });
  }, [timestamp, fields, employees, manualByKey, faDefaultPrefixes]); // eslint-disable-line

  // populate autoValuesRef สำหรับ fields ที่ไม่มี useEffect sync (OT, tax, late, bonus, other)
  useEffect(() => {
    if (!fields.length) return;
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      const fi = fieldIndexMap[empId];
      if (fi === undefined) return;
      const f = fields[fi];
      if (!autoValuesRef.current[empId]) autoValuesRef.current[empId] = {};
      const av = autoValuesRef.current[empId];
      const mf = f?.manual_fields || [];
      if (!mf.includes('overtime'))
        av.overtime = Number(
          f?.revenue?.overtime ?? f?.totalOtAmount ?? 0,
        ).toFixed(2);
      if (!mf.includes('tax'))
        av.tax = Number(
          CalTax(f?.salary_per_day * f?.totalWork, f?.tax_type) || 0,
        ).toFixed(2);
      if (!mf.includes('late'))
        av.late = Number(f?.expenses?.late ?? 0).toFixed(2);
      if (!mf.includes('bonus'))
        av.bonus = Number(f?.revenue?.bonus ?? 0).toFixed(2);
      if (!mf.includes('other'))
        av.other = Number(f?.expenses?.other ?? 0).toFixed(2);
    });
  }, [fields, timestamp, fieldIndexMap]); // eslint-disable-line

  // totalsBase: work/salary/OT only — net is handled by TotalNetCell sub-component (avoids watch subscription here)
  const totalsBase = useMemo(() => {
    return (timestamp?.rows || []).reduce(
      (acc, emp) => {
        const id = String(emp.employee_id);
        const ma = getManualAmt(id, emp.salary_per_day, emp.emp_type);
        acc.work += emp.totalWork || 0;
        acc.salary += (emp.totalAmount || 0) + ma;
        acc.ot += emp.totalOtAmount || 0;
        acc.actualSalary += empSummaryMap[id]?.actualSalaryAmt || 0;
        return acc;
      },
      { work: 0, salary: 0, ot: 0, actualSalary: 0 },
    );
    // eslint-disable-next-line
  }, [timestamp, fieldIndexMap, manualWorkData, empSummaryMap]);

  const employeeMap = useMemo(() => {
    const m = {};
    employees.forEach((e) => {
      m[String(e._id || e.id)] = e;
    });
    return m;
  }, [employees]);

  const fmt = (n, d = 2) =>
    n != null
      ? Number(n).toLocaleString('th-TH', {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        })
      : '-';

  // ── salary override dialog ────────────────────────────────────────────
  const openDetail = (checkin, name, spd, showLate = false, empId = null) => {
    const dr = spd || checkin?.salary_per_day || 0;
    const ovr = checkin?.salary_override;
    const initRate = ovr?.rate ?? checkin?.effectiveRate ?? checkin?.rate ?? 1;
    const initHours = ovr?.hours ?? Math.max(checkin?.totalHour ?? 8, 8);
    const realH = checkin?.totalHour ?? 0;
    setForceFullHours(realH < 8 && (ovr?.hours ?? null) === 8);

    // หา field_allowance จาก timestamp.rows ก่อน ถ้าไม่เจอ fallback employeeMap
    const empRow = (timestamp?.rows || []).find(
      (r) => String(r.employee_id) === String(empId),
    );
    const empFA =
      empRow?.field_allowance ||
      employeeMap[String(empId)]?.field_allowance ||
      null;
    const faEnabled = !!empFA?.enabled;
    const faAmt =
      empFA?.override_amount != null ? empFA.override_amount : globalFieldRate;

    // default checkbox: saved value → project_number logic → false
    const savedFA = ovr?.field_allowance_day;
    const pn = (checkin?.project_number || '').toLowerCase().trim();
    const defaultFA = matchesProjectPrefix(pn, faDefaultPrefixes);
    setDlgFieldAllow(savedFA != null ? savedFA : defaultFA);

    setDetailData({
      checkin,
      name,
      spd: dr,
      showLate,
      empId,
      faEnabled,
      faAmt,
    });
    setEditAmtManual(ovr?.day_rate != null ? String(ovr.day_rate) : null);
    setDetailHours(String(initHours));
    setDetailRate(initRate);
    setEditCheckIn(checkin?.checkInTime || '');
    setEditCheckOut(checkin?.checkOutTime || '');
    setEditProjectId(checkin?.project_id ? String(checkin.project_id) : '');
    setDetailOpen(true);
  };
  const saveOverride = async () => {
    if (!detailData?.checkin?._id || !onSalaryOverride) return;
    setSaving(true);
    try {
      const dayRateToSave = parseFloat(
        editAmtManual ?? String(detailData?.spd || 0),
      );
      const totalToSave = Number((detailRate * dayRateToSave).toFixed(2));
      await onSalaryOverride(detailData.checkin._id, {
        amount: totalToSave,
        rate: detailRate,
        day_rate: dayRateToSave,
        ...(forceFullHours && { hours: 8 }),
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setSaving(false);
    }
  };
  const saveFieldAllowDay = async (checked) => {
    if (!detailData?.checkin?._id || !onSalaryOverride) return;
    setDlgFieldAllow(checked);
    try {
      await onSalaryOverride(detailData.checkin._id, {
        field_allowance_day: checked,
        edited_by: editorName,
      });
      onRefreshTimestamp?.();
    } catch (e) {
      setDlgFieldAllow(!checked);
    }
  };

  const saveSkipLate = async (newEnabled) => {
    if (!detailData?.checkin?._id || !onSkipLateDeduct) return;
    setSkipLateSaving(true);
    try {
      await onSkipLateDeduct(detailData.checkin._id, {
        enabled: newEnabled,
        edited_by: editorName,
      });
      setDetailData((prev) => ({
        ...prev,
        checkin: { ...prev.checkin, skip_late_deduct: newEnabled },
        showLate: newEnabled ? true : prev.showLate,
      }));
      onRefreshTimestamp?.();
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setSkipLateSaving(false);
    }
  };

  const saveSkipLateDv = async (newEnabled) => {
    if (!dvData?.checkin?._id || !onSkipLateDeduct) return;
    setSkipLateDvSaving(true);
    try {
      await onSkipLateDeduct(dvData.checkin._id, {
        enabled: newEnabled,
        edited_by: editorName,
      });
      setDvData((prev) => ({
        ...prev,
        checkin: { ...prev.checkin, skip_late_deduct: newEnabled },
      }));
      onRefreshTimestamp?.();
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setSkipLateDvSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!(await confirmAsync('ล้างการแก้ไขและใช้ค่าคำนวณอัตโนมัติ?'))) return;
    setSaving(true);
    try {
      await onSalaryOverride(detailData.checkin._id, { amount: null });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ล้างไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const clearTimeOverride = async () => {
    if (!onCheckInTimeClear) return;
    if (!(await confirmAsync('คืนค่าเวลาเข้าออกกลับเป็นค่าเดิม?'))) return;
    setSaving(true);
    try {
      await onCheckInTimeClear(detailData.checkin._id);
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ล้างไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const clearProjectOverride = async () => {
    if (!onProjectClear) return;
    if (!(await confirmAsync('คืนค่าโปรเจคกลับเป็นค่าเดิม?'))) return;
    setSaving(true);
    try {
      await onProjectClear(detailData.checkin._id);
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ล้างไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkHours = async () => {
    if (!onHourOverride) return;
    if (
      !(await confirmAsync(
        'ลบชั่วโมงทำงานออกจากการคำนวณเงินเดือน? (สามารถคืนกลับได้)',
      ))
    )
      return;
    setSaving(true);
    try {
      await onHourOverride(detailData.checkin._id, {
        hours: 0,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const restoreWorkHours = async () => {
    if (!onHourOverride) return;
    if (!(await confirmAsync('คืนชั่วโมงทำงานกลับ (ใช้ค่าคำนวณอัตโนมัติ)?'))) return;
    setSaving(true);
    try {
      await onHourOverride(detailData.checkin._id, {
        hours: null,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const deleteOtHours = async () => {
    if (!onOtOverride) return;
    if (
      !(await confirmAsync(
        'ลบชั่วโมง OT ออกจากการคำนวณเงินเดือน? (สามารถคืนกลับได้)',
      ))
    )
      return;
    setSaving(true);
    try {
      await onOtOverride(detailData.checkin._id, {
        disabled: true,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const restoreOtHours = async () => {
    if (!onOtOverride) return;
    if (!(await confirmAsync('คืนชั่วโมง OT กลับ?'))) return;
    setSaving(true);
    try {
      await onOtOverride(detailData.checkin._id, {
        disabled: false,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const deleteManualFromDialog = async () => {
    if (!(await confirmAsync('ลบชั่วโมงที่เพิ่มออก?'))) return;
    const empId = detailData?.empId;
    const day = dayjs(dlgC?.checkIn).date();
    const entry = empId ? getEntry(empId, day) : null;
    if (!entry?._id) {
      showAlert('ไม่พบข้อมูลชั่วโมงที่เพิ่ม');
      return;
    }
    setSaving(true);
    try {
      if (onManualDelete) await onManualDelete(entry._id);
      setDetailOpen(false);
    } catch (e) {
      showAlert(e?.response?.data?.message || e?.message || 'ลบไม่สำเร็จ', { status: e?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const saveCheckInTime = async () => {
    if (!detailData?.checkin?._id || !onCheckInTimeUpdate) return;
    if (!editCheckIn && !editCheckOut) return;
    setCheckTimeSaving(true);
    try {
      await onCheckInTimeUpdate(detailData.checkin._id, {
        ...(editCheckIn && { checkInTime: editCheckIn }),
        ...(editCheckOut && { checkOutTime: editCheckOut }),
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setCheckTimeSaving(false);
    }
  };

  const saveProject = async () => {
    if (!detailData?.checkin?._id || !onProjectUpdate) return;
    setCheckTimeSaving(true);
    try {
      await onProjectUpdate(detailData.checkin._id, {
        project_id: editProjectId || null,
        edited_by: editorName,
      });
      await onRefreshTimestamp?.();
      setDetailOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setCheckTimeSaving(false);
    }
  };

  // ── add hours dialog ─────────────────────────────────────────────────
  const timeToMins = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0);
  };
  const minsToTime = (mins) =>
    `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(
      mins % 60,
    ).padStart(2, '0')}`;
  const openAddHours = (empId, empName, day, spd, allowancePerDay = 0) => {
    const ex = getEntry(empId, day);
    const dow = dayjs(`${year}-${month + 1}-${day}`).day();
    const isWeekend = dow === 0;
    const resolvedAllowance =
      allowancePerDay > 0 ? allowancePerDay : ex?.allowance_amount ?? 0;
    setAddTarget({
      empId,
      empName,
      day,
      spd,
      isWeekend,
      allowancePerDay: resolvedAllowance,
    });
    const initHours = ex?.hours != null ? ex.hours : null;
    const initRate = ex?.work_rate ?? 1;
    const initAutoAmt =
      initHours != null ? ((initHours / 8) * spd * initRate).toFixed(2) : '';
    // pre-fill times: ถ้ามี entry เดิม back-calc จาก hours, ไม่มีใช้ default 08:00-17:00
    setAddCheckIn('08:00');
    setAddCheckOut(
      initHours != null
        ? minsToTime(480 + Math.round(initHours * 60))
        : '17:00',
    );
    setAddAmt(ex?.amount != null ? String(ex.amount) : initAutoAmt);
    setAddWorkRate(initRate);
    setAddIncludeAllowance((ex?.allowance_amount ?? 0) > 0);
    setAddOpen(true);
  };
  const saveAddHours = async () => {
    const inMins = timeToMins(addCheckIn);
    const outMins = timeToMins(addCheckOut);
    const h = outMins > inMins ? (outMins - inMins) / 60 : 0;
    if (h <= 0) return;
    const rate = addWorkRate;
    const auto = (h / 8) * (addTarget?.spd || 0) * rate;
    const ovr = parseFloat(addAmt);
    const payload = {
      employee_id: String(addTarget.empId),
      employee_name: addTarget.empName,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(
        addTarget.day,
      ).padStart(2, '0')}`,
      hours: h,
      salary_per_day: addTarget.spd,
      edited_by: editorName,
      work_rate: rate,
      allowance_amount: addIncludeAllowance
        ? addTarget.allowancePerDay || 0
        : 0,
      amount:
        !isNaN(ovr) && ovr >= 0 && Math.abs(ovr - auto) > 0.01 ? ovr : null,
    };
    setAddSaving(true);
    try {
      if (onManualSave) await onManualSave(payload);
    } finally {
      setAddSaving(false);
    }
    setAddOpen(false);
    setAddTarget(null);
    setAddCheckIn('');
    setAddCheckOut('');
    setAddAmt('');
  };
  const deleteManual = async () => {
    const ex = getEntry(addTarget.empId, addTarget.day);
    if (!ex?._id) return;
    setAddSaving(true);
    try {
      if (onManualDelete) await onManualDelete(ex._id);
    } finally {
      setAddSaving(false);
    }
    setAddOpen(false);
    setAddTarget(null);
    setAddCheckIn('');
    setAddCheckOut('');
    setAddAmt('');
  };

  // ── Bulk day dialog ───────────────────────────────────────────────────────
  const openBulkDay = (day) => {
    setBulkDay(day);
    setBulkTab(0);
    setBulkCheckIn('08:00');
    setBulkCheckOut('17:00');
    setBulkRate(1);
    setBulkDayOpen(true);
  };
  const saveBulkDay = async () => {
    const inMins = timeToMins(bulkCheckIn);
    const outMins = timeToMins(bulkCheckOut);
    const h = outMins > inMins ? (outMins - inMins) / 60 : 0;
    if (h <= 0 || !onManualSave) return;
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(
      bulkDay,
    ).padStart(2, '0')}`;
    setBulkSaving(true);
    try {
      const empRows = timestamp?.rows || [];
      for (const emp of empRows) {
        const empId = String(emp.employee_id);
        const hasCheckin = !!checkinByKey[`${empId}-${bulkDay}`];
        const hasManual = !!manualByKey[`${empId}-${bulkDay}`];
        if (!hasCheckin && !hasManual) {
          // eslint-disable-next-line no-await-in-loop
          await onManualSave({
            employee_id: empId,
            employee_name: emp.name || '',
            date,
            hours: h,
            salary_per_day: emp.salary_per_day || 0,
            edited_by: editorName,
            work_rate: bulkRate,
            allowance_amount: 0,
            amount: null,
          });
        }
      }
      await onRefreshTimestamp?.();
      setBulkDayOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const saveBulkDayRate = async () => {
    if (!onSalaryOverride && !onManualSave) return;
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(
      bulkDay,
    ).padStart(2, '0')}`;
    setBulkSaving(true);
    try {
      const empRows = timestamp?.rows || [];
      for (const emp of empRows) {
        const empId = String(emp.employee_id);
        const ck = checkinByKey[`${empId}-${bulkDay}`];
        const manEntry = manualByKey[`${empId}-${bulkDay}`];
        if (ck && onSalaryOverride) {
          const spd = emp.salary_per_day || 0;
          // eslint-disable-next-line no-await-in-loop
          await onSalaryOverride(ck._id, {
            amount: bulkRate * spd,
            rate: bulkRate,
            day_rate: spd,
            edited_by: editorName,
          });
        } else if (manEntry && onManualSave) {
          // eslint-disable-next-line no-await-in-loop
          await onManualSave({
            employee_id: empId,
            employee_name: emp.name || '',
            date,
            hours: manEntry.hours,
            salary_per_day: manEntry.salary_per_day || emp.salary_per_day || 0,
            edited_by: editorName,
            work_rate: bulkRate,
            allowance_amount: manEntry.allowance_amount || 0,
            amount: null,
          });
        }
      }
      await onRefreshTimestamp?.();
      setBulkDayOpen(false);
    } catch (e) {
      showAlert(
        e?.response?.data?.message || e?.message || 'บันทึกไม่สำเร็จ',
        { status: e?.response?.status },
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // ── OT dialog ────────────────────────────────────────────────────────────
  const openAddOt = (empId, empName, day, spd, approvedOt = null) => {
    const ex = getEntry(empId, day);
    const dow = dayjs(`${year}-${month + 1}-${day}`).day();
    const isWeekend = dow === 0 || dow === 6;
    const rate = ex?.ot_rate != null ? ex.ot_rate : isWeekend ? 3.0 : 1.5;
    // ชั่วโมง: ใช้ manual entry ก่อน → ถ้าไม่มีใช้ approved OT จาก checkin
    const hours =
      ex?.ot_hours != null ? ex.ot_hours : approvedOt?.hours || null;
    setAddOtTarget({ empId, empName, day, spd });
    setAddOtHours(hours != null ? String(hours) : '');
    setAddOtRate(rate);
    setAddOtOpen(true);
  };
  const saveAddOt = async () => {
    const h = parseFloat(addOtHours);
    if (isNaN(h) || h <= 0) return;
    const payload = {
      employee_id: String(addOtTarget.empId),
      employee_name: addOtTarget.empName,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(
        addOtTarget.day,
      ).padStart(2, '0')}`,
      ot_hours: h,
      ot_rate: addOtRate,
      ot_amount: null,
      edited_by: editorName,
    };
    setAddOtSaving(true);
    try {
      if (onManualSave) await onManualSave(payload);
    } finally {
      setAddOtSaving(false);
    }
    setAddOtOpen(false);
    setAddOtTarget(null);
    setAddOtHours('');
    setAddOtRate(1.5);
  };
  const clearManualOt = async () => {
    const payload = {
      employee_id: String(addOtTarget.empId),
      employee_name: addOtTarget.empName,
      date: `${year}-${String(month + 1).padStart(2, '0')}-${String(
        addOtTarget.day,
      ).padStart(2, '0')}`,
      ot_hours: null,
      ot_rate: null,
      ot_amount: null,
      edited_by: editorName,
    };
    setAddOtSaving(true);
    try {
      if (onManualSave) await onManualSave(payload);
    } finally {
      setAddOtSaving(false);
    }
    setAddOtOpen(false);
    setAddOtTarget(null);
    setAddOtHours('');
    setAddOtRate(1.5);
  };

  if (!timestamp?.rows?.length) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          ไม่มีข้อมูลพนักงานในช่วงเวลานี้
        </Typography>
      </Box>
    );
  }

  // ── computed vars for inline dialogs (ไม่ define เป็น nested component เพื่อป้องกัน flicker) ──
  const dlgC = detailData?.checkin;
  const dlgDr = detailData?.spd || dlgC?.salary_per_day || 0;
  const dlgAuto =
    dlgC?.salary_override?.amount != null
      ? dlgC.salary_override.amount
      : dlgC?.totalHour != null
      ? Number(
          ((dlgC.totalHour / 8) * (dlgC?.effectiveRate || 1) * dlgDr).toFixed(
            2,
          ),
        )
      : 0;
  const dlgOvr = dlgC?.salary_override?.amount != null;
  const dlgTimeOvr = dlgC?.time_override != null;
  const dlgProjOvr = dlgC?.project_override != null;
  const dlgAnyOvr = dlgOvr || dlgTimeOvr || dlgProjOvr;
  const dlgHoursDeleted = dlgC?.hour_override?.hours === 0;
  const dlgOtDisabled = dlgC?.ot_override?.disabled === true;
  const dlgHasOt = (dlgC?.otRequests?.total_hours || 0) > 0;
  const dlgIsSkipped = dlgC?.skip_late_deduct === true;

  const addInMins = timeToMins(addCheckIn);
  const addOutMins = timeToMins(addCheckOut);
  const addH =
    addCheckIn && addCheckOut && addOutMins > addInMins
      ? (addOutMins - addInMins) / 60
      : 0;
  const addRate = addWorkRate;
  const addAutoAmt =
    addH > 0 ? (addH / 8) * (addTarget?.spd || 0) * addRate : 0;
  const addOvrAmt = parseFloat(addAmt);
  const addEff = !isNaN(addOvrAmt) && addAmt !== '' ? addOvrAmt : addAutoAmt;
  const addEx = addTarget ? getEntry(addTarget.empId, addTarget.day) : null;
  const addAllowanceAmt = addTarget?.allowancePerDay || 0;

  // ── Dialogs (inline JSX — ไม่ใช้ nested function components) ─────────

  // DetailDialog
  const detailDialogJsx = (() => {
    const currentDr =
      parseFloat(editAmtManual ?? String(detailData?.spd || dlgDr || 0)) || 0;
    const calcAmt = detailRate * currentDr;
    const origDr = detailData?.spd || dlgDr || 0;
    const origAmt = dlgAuto;
    const drChanged = Math.abs(currentDr - origDr) > 0.01;
    const origRate = dlgC?.effectiveRate || dlgC?.rate || 1;
    const effChanged = detailRate !== origRate;
    const isModified = drChanged || effChanged;
    const hrRate = (currentDr * detailRate) / 8;
    const minRate = hrRate / 60;

    // late calc
    const dlgCit = dlgC?.checkInTime;
    const actualHr = dlgC?.totalHour ?? null;
    const lateRuleD = (attendanceRules || []).find(
      (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
    );
    let lateMin = 0;
    let effMin = 0;
    let lateAmt = 0;
    if (dlgCit && !dlgC?.is_manual) {
      const [ch, cm] = dlgCit.split(':').map(Number);
      if (ch < 12) {
        lateMin = Math.max(0, ch * 60 + cm - 8 * 60);
        if (lateMin > 0) {
          if (!lateRuleD) {
            effMin = lateMin;
          } else if (lateMin >= lateRuleD.threshold_minutes) {
            effMin = Math.max(lateMin, lateRuleD.deduct_minutes || 0);
          }
          if (effMin > 0 && !dlgIsSkipped) {
            lateAmt = Number((effMin * minRate).toFixed(2));
          }
        }
      }
    }
    const netAmt = calcAmt - lateAmt;
    const hasLate = effMin > 0 && !dlgC?.is_manual;

    const sectionLabel = (txt, icon, color = '#64748b', helper = '') => {
      const iconNode =
        icon === true ? (
          <Box
            sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }}
          />
        ) : (
          icon
        );
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {iconNode && (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '999px',
                  bgcolor: alpha(color, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color,
                  flexShrink: 0,
                }}
              >
                {iconNode}
              </Box>
            )}
            <Box>
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{
                  color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontSize: '0.62rem',
                }}
              >
                {txt}
              </Typography>
              {helper && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    display: 'block',
                    fontSize: '0.62rem',
                    mt: 0.2,
                  }}
                >
                  {helper}
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{ flex: 1, height: '1px', bgcolor: alpha(color, 0.15), ml: 1 }}
          />
        </Box>
      );
    };
    const row2 = (l, v) => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.65rem' }}
        >
          {l}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {v}
        </Typography>
      </Box>
    );
    const statCard = (label, amount, formula, extra, color, strikeAmt) => (
      <Box
        sx={{
          borderRadius: 1.75,
          px: 1.15,
          py: 0.95,
          minHeight: 92,
          bgcolor:
            'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.96) 100%)',
          border: '1.5px solid',
          borderColor: alpha(color, 0.2),
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
          background: `linear-gradient(135deg, ${alpha(
            color,
            0.08,
          )} 0%, #ffffff 100%)`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 4,
            bottom: 0,
            bgcolor: color,
            borderRadius: '8px 0 0 8px',
          }}
        />
        <Typography
          sx={{
            fontSize: '0.58rem',
            fontWeight: 800,
            color: alpha(color, 0.75),
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 0.35,
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.55,
            flexWrap: 'wrap',
          }}
        >
          {strikeAmt != null && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: '#9ca3af',
                textDecoration: 'line-through',
              }}
            >
              ฿{fmt(strikeAmt)}
            </Typography>
          )}
          <Typography
            sx={{ fontSize: '1.05rem', fontWeight: 800, color, lineHeight: 1 }}
          >
            {fmt(amount)}
          </Typography>
        </Box>
        {formula && (
          <Typography
            sx={{
              fontSize: '0.58rem',
              color: alpha(color, 0.6),
              mt: 0.3,
              fontFamily: 'monospace',
              lineHeight: 1.35,
            }}
          >
            {formula}
          </Typography>
        )}
        {extra && (
          <Typography
            sx={{
              fontSize: '0.58rem',
              color: '#64748b',
              mt: 0.2,
              lineHeight: 1.35,
            }}
          >
            {extra}
          </Typography>
        )}
      </Box>
    );

    return (
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            overflow: 'hidden',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.24)',
          },
        }}
      >
        {/* ── S1: Header ── */}
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background:
                'linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #334155 100%)',
              px: 3,
              py: 2.75,
            }}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.75, flexWrap: 'wrap' }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.66rem',
                      fontWeight: 800,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                    }}
                  >
                    แก้ไขข้อมูลการลงเวลา
                  </Typography>
                  {dlgOvr && (
                    <Chip
                      label="มีการแก้ไข"
                      size="small"
                      sx={{
                        fontSize: '0.6rem',
                        height: 20,
                        bgcolor: '#f59e0b',
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  <Chip
                    label="ดูง่าย • เข้าใจไว"
                    size="small"
                    sx={{
                      fontSize: '0.6rem',
                      height: 20,
                      bgcolor: alpha('#38bdf8', 0.18),
                      color: '#bae6fd',
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <Typography
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    color: '#f8fafc',
                    lineHeight: 1.2,
                  }}
                >
                  {detailData?.name || '—'}
                </Typography>
                {dlgC?.checkIn && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 0.9, flexWrap: 'wrap' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        color: '#cbd5e1',
                        fontWeight: 600,
                      }}
                    >
                      {dayjs(dlgC.checkIn).format('dddd DD MMMM YYYY')}
                    </Typography>
                    {actualHr != null && (
                      <Box
                        sx={{
                          px: 1.1,
                          py: 0.2,
                          borderRadius: 1.25,
                          bgcolor:
                            actualHr >= 8
                              ? alpha('#22c55e', 0.2)
                              : alpha('#f59e0b', 0.2),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: actualHr >= 8 ? '#4ade80' : '#fbbf24',
                          }}
                        >
                          {fmt(actualHr, 2)} ชม.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => setDetailOpen(false)}
                sx={{
                  color: '#cbd5e1',
                  bgcolor: alpha('#fff', 0.08),
                  '&:hover': { color: '#f8fafc', bgcolor: alpha('#fff', 0.16) },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 1.5,
            pb: 1,
            px: 2.25,
            bgcolor: '#f8fafc',
            overflow: 'hidden',
          }}
        >
          <Stack spacing={1.75}>
            {/* ── S2: เวลาเข้าออก + ชั่วโมง + โปรเจค ── */}
            <Box>
              {sectionLabel('เวลาเข้าออก & โปรเจค', true, '#3b82f6')}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1.5,
                }}
              >
                {/* เวลา */}
                <Box
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    px: 1.75,
                    py: 1.25,
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                    <Typography variant="caption" color="text.secondary">
                      เวลาเข้า - ออก
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {dlgC?.checkInTime || '-'} — {dlgC?.checkOutTime || '?'}
                  </Typography>
                  {dlgC?.checkIn &&
                    (() => {
                      const ci = dayjs(dlgC.checkIn);
                      const ciH = ci.hour();
                      const ciM2 = ci.minute();
                      const ciMin = ciH * 60 + ciM2;
                      const W_START = 8 * 60;
                      const W_END = 17 * 60;
                      const isLunchCheckin = ciH === 12;
                      const effStart = isLunchCheckin
                        ? 13 * 60
                        : Math.max(ciMin, W_START);
                      const isNextDayOrNoCo =
                        !dlgC.checkOut ||
                        dayjs(dlgC.checkOut).format('YYYY-MM-DD') !==
                          ci.format('YYYY-MM-DD');
                      const coMin2 = dlgC.checkOut
                        ? dayjs(dlgC.checkOut).hour() * 60 +
                          dayjs(dlgC.checkOut).minute()
                        : null;
                      const rawEffEnd = isNextDayOrNoCo
                        ? W_END
                        : Math.min(coMin2, W_END);
                      const isAfterLunch = ciH >= 12;
                      const isEarlyCheckout =
                        !isAfterLunch &&
                        !isNextDayOrNoCo &&
                        coMin2 != null &&
                        coMin2 < 13 * 60;
                      const effEnd = isEarlyCheckout
                        ? Math.min(rawEffEnd, 12 * 60)
                        : rawEffEnd;
                      const grossMin = Math.max(0, effEnd - effStart);
                      const afterLunchMin =
                        isAfterLunch || isEarlyCheckout
                          ? grossMin
                          : Math.max(0, grossMin - 60);
                      const lateRuleM1 = (attendanceRules || []).find(
                        (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
                      );
                      const lateMinM1 = Math.max(0, ciMin - W_START);
                      const isLateD =
                        !isAfterLunch &&
                        lateRuleM1 &&
                        lateMinM1 >= lateRuleM1.threshold_minutes &&
                        !dlgIsSkipped;
                      const lateDeductM1 = isLateD
                        ? lateRuleM1.deduct_minutes || 0
                        : 0;
                      const nextDayRuleM1 = (attendanceRules || []).find(
                        (r) => r.type === 'LATE_CHECKOUT_NEXT_DAY' && r.enabled,
                      );
                      const noCoDeductM1 =
                        isNextDayOrNoCo && nextDayRuleM1
                          ? nextDayRuleM1.deduct_minutes
                          : 0;
                      const finalMin = Math.max(
                        0,
                        afterLunchMin - lateDeductM1 - noCoDeductM1,
                      );
                      const hrs = finalMin / 60;
                      const notes = [];
                      if (isLunchCheckin) notes.push('นับจาก 13:00');
                      else if (ciMin < W_START) notes.push('นับจาก 08:00');
                      if (isNextDayOrNoCo) notes.push('สิ้นสุด 17:00');
                      if (!isAfterLunch && !isEarlyCheckout)
                        notes.push('หักพัก 1 ชม.');
                      if (isLateD) notes.push(`หักสาย ${lateDeductM1} นาที`);
                      return (
                        <>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              bgcolor: '#f0fdf4',
                              border: '1px solid #86efac',
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.15,
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                color: '#15803d',
                              }}
                            >
                              {Number.isInteger(hrs) ? hrs : hrs.toFixed(2)} ชม.
                            </Typography>
                            <Typography
                              sx={{ fontSize: '0.65rem', color: '#4ade80' }}
                            >
                              ({finalMin} นาที)
                            </Typography>
                          </Box>
                          {notes.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#9ca3af',
                                fontSize: '0.62rem',
                                display: 'block',
                                mt: 0.25,
                              }}
                            >
                              {notes.join(' · ')}
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  {actualHr != null && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontSize: '0.65rem',
                        display: 'block',
                        mt: 0.25,
                      }}
                    >
                      ทำงานจริง {fmt(actualHr, 2)} ชม. (
                      {Math.round(actualHr * 60)} นาที)
                      {lateMin > 0 ? ` · สาย ${lateMin} นาที` : ''}
                    </Typography>
                  )}
                </Box>
                {/* โปรเจค */}
                <Box
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    px: 1.75,
                    py: 1.25,
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <WorkIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                    <Typography variant="caption" color="text.secondary">
                      โปรเจค
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {dlgC?.project_name
                      ? `${dlgC.project_name}${
                          dlgC?.project_number
                            ? ` (${dlgC.project_number})`
                            : ''
                        }`
                      : 'ไม่ระบุ'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ── S3: แก้ไขเวลาเข้าออก + โปรเจค + ตั้งค่าค่าแรง ── */}
            {hrMgmt && dlgC ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.95fr' },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                {hrMgmt && dlgC && (
                  <Box
                    sx={{
                      border: '1px solid #dbeafe',
                      borderRadius: 3,
                      p: 2.25,
                      bgcolor:
                        'linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)',
                      boxShadow: '0 12px 28px rgba(59, 130, 246, 0.08)',
                    }}
                  >
                    {sectionLabel(
                      'แก้ไขเวลาเข้า-ออก & โปรเจค',
                      true,
                      '#0ea5e9',
                      'กรอกข้อมูลแล้วกดบันทึกทีละส่วน',
                    )}
                    <Stack spacing={1.25}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                          gap: 1,
                          alignItems: 'start',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            label="เวลาเข้า"
                            type="time"
                            value={editCheckIn}
                            onChange={(e) => setEditCheckIn(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={checkTimeSaving}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="เวลาออก"
                            type="time"
                            value={editCheckOut}
                            onChange={(e) => setEditCheckOut(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={checkTimeSaving}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={saveCheckInTime}
                          disabled={
                            checkTimeSaving || (!editCheckIn && !editCheckOut)
                          }
                          sx={{
                            height: 40,
                            minWidth: 126,
                            borderRadius: 2,
                            fontWeight: 700,
                          }}
                        >
                          {checkTimeSaving ? 'กำลังบันทึก...' : 'บันทึกเวลา'}
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                          gap: 1,
                          alignItems: 'start',
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="โปรเจค"
                          value={
                            projectList.some(
                              (p) => String(p._id) === editProjectId,
                            )
                              ? editProjectId
                              : ''
                          }
                          onChange={(e) => setEditProjectId(e.target.value)}
                          disabled={checkTimeSaving}
                          helperText={
                            editProjectId &&
                            !projectList.some(
                              (p) => String(p._id) === editProjectId,
                            )
                              ? `ปัจจุบัน: ${
                                  dlgC?.project_name || editProjectId
                                }`
                              : ' '
                          }
                        >
                          <MenuItem value="">— ไม่ระบุ —</MenuItem>
                          {projectList.map((p) => (
                            <MenuItem key={p._id} value={String(p._id)}>
                              {p.project_number ? `${p.project_number} — ` : ''}
                              {p.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={saveProject}
                          disabled={checkTimeSaving}
                          sx={{
                            height: 40,
                            minWidth: 126,
                            borderRadius: 2,
                            fontWeight: 700,
                          }}
                        >
                          {checkTimeSaving ? 'กำลังบันทึก...' : 'บันทึกโปรเจค'}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                )}

                {hrMgmt && (onSalaryOverride || onSkipLateDeduct) && (
                  <Box
                    sx={{
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 3,
                      p: 2.25,
                      boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    {sectionLabel(
                      'ตั้งค่าค่าแรง',
                      true,
                      '#6366f1',
                      'ปรับค่าแรงและกฎการหักสายให้ตรงกับข้อมูลนี้',
                    )}
                    <Stack spacing={1}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          bgcolor: '#fff',
                          px: 1.5,
                          py: 1,
                          borderRadius: 1.5,
                          border: '1.5px solid #e5e7eb',
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ color: '#374151', fontSize: '0.82rem' }}
                          >
                            รับค่าออกสนาม
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.68rem',
                              color: '#9ca3af',
                              mt: 0.15,
                            }}
                          >
                            เปิดเมื่อต้องการให้ค่าเดินทาง/ค่าออกสนามสำหรับวันนี้
                          </Typography>
                        </Box>
                        <Checkbox
                          size="small"
                          checked={dlgFieldAllow}
                          onChange={(e) => saveFieldAllowDay(e.target.checked)}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 1,
                        }}
                      >
                        {onSkipLateDeduct &&
                          dlgC &&
                          salaryMode !== 'work-time' && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: dlgIsSkipped
                                  ? alpha('#f97316', 0.08)
                                  : '#fff',
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                border: '1.5px solid',
                                borderColor: dlgIsSkipped
                                  ? '#f97316'
                                  : '#e5e7eb',
                                transition: 'all 0.2s',
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{
                                    color: dlgIsSkipped ? '#ea580c' : '#374151',
                                    fontSize: '0.82rem',
                                  }}
                                >
                                  {dlgIsSkipped
                                    ? '✓ ยกเว้นหักสายแล้ว'
                                    : 'ยกเว้นหักสาย'}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.68rem',
                                    color: '#9ca3af',
                                    mt: 0.15,
                                  }}
                                >
                                  {dlgIsSkipped
                                    ? 'tap เพื่อกลับมาหักสายตามปกติ'
                                    : 'เปิดเพื่อไม่หักเงินกรณีมาสายวันนี้'}
                                </Typography>
                              </Box>
                              <Switch
                                checked={dlgIsSkipped}
                                onChange={(e) => saveSkipLate(e.target.checked)}
                                disabled={skipLateSaving}
                                color="warning"
                              />
                            </Box>
                          )}
                        {(actualHr ?? 0) < 8 && salaryMode !== 'late-deduct' && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              bgcolor: forceFullHours
                                ? alpha('#7c3aed', 0.08)
                                : '#fff',
                              px: 1.5,
                              py: 1,
                              borderRadius: 1.5,
                              border: '1.5px solid',
                              borderColor: forceFullHours
                                ? '#7c3aed'
                                : '#e5e7eb',
                              transition: 'all 0.2s',
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{
                                  color: forceFullHours ? '#7c3aed' : '#374151',
                                  fontSize: '0.82rem',
                                }}
                              >
                                {forceFullHours
                                  ? '✓ นับเป็น 8 ชม. (เหตุฉุกเฉิน)'
                                  : 'นับชั่วโมงเป็น 8 ชม.'}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  color: '#9ca3af',
                                  mt: 0.15,
                                }}
                              >
                                {forceFullHours
                                  ? `ทำงานจริง ${fmt(
                                      actualHr ?? 0,
                                      2,
                                    )} ชม. → คิดเงินเป็น 8 ชม.`
                                  : `ทำงานจริง ${fmt(
                                      actualHr ?? 0,
                                      2,
                                    )} ชม. — เปิดถ้าลงเวลาไม่ได้เพราะเหตุฉุกเฉิน`}
                              </Typography>
                            </Box>
                            <Switch
                              checked={forceFullHours}
                              onChange={(e) =>
                                setForceFullHours(e.target.checked)
                              }
                              color="secondary"
                            />
                          </Box>
                        )}
                      </Box>
                      {onSalaryOverride && (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 1.25,
                            pt: 0.5,
                          }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            label="อัตราค่าแรง/วัน (฿)"
                            type="number"
                            value={
                              editAmtManual ?? String(detailData?.spd || 0)
                            }
                            onChange={(e) => setEditAmtManual(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ฿
                                </InputAdornment>
                              ),
                            }}
                            helperText={
                              editAmtManual == null
                                ? `ค่าเริ่มต้น ฿${fmt(detailData?.spd)}`
                                : ' '
                            }
                            disabled={saving}
                          />
                          <TextField
                            select
                            size="small"
                            fullWidth
                            label="ตัวคูณ (วันหยุด/OT)"
                            value={detailRate}
                            onChange={(e) =>
                              setDetailRate(Number(e.target.value))
                            }
                            disabled={saving}
                          >
                            {[
                              { value: 1, label: '×1.0 — วันทำงานปกติ' },
                              { value: 1.5, label: '×1.5 — อัตราพิเศษ' },
                              { value: 2, label: '×2.0 — วันหยุด' },
                              { value: 3, label: '×3.0 — วันหยุดนักขัตฤกษ์' },
                            ].map((r) => (
                              <MenuItem key={r.value} value={r.value}>
                                {r.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>
            ) : null}

            {/* ── S4 + S5: ค่าแรงเต็มวัน | หักมาสาย + ค่าออกสนาม ── */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: detailData?.faEnabled
                  ? { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }
                  : '1fr',
                gap: 1.25,
                alignItems: 'start',
              }}
            >
              <Box sx={{ minWidth: 0, width: '100%' }}>
                {sectionLabel('สรุปค่าแรง', true, '#2563eb')}
                {(() => {
                  const realH = forceFullHours ? 8 : actualHr ?? 0;
                  const actualWage = Number(
                    (Math.min(realH, 8) * detailRate * (currentDr / 8)).toFixed(
                      2,
                    ),
                  );
                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          hasLate && salaryMode !== 'work-time'
                            ? 'repeat(3, minmax(0, 1fr))'
                            : 'repeat(2, minmax(0, 1fr))',
                        gap: 0.75,
                      }}
                    >
                      {salaryMode !== 'work-time' &&
                        statCard(
                          'ค่าแรงเต็มวัน (8 ชม.)',
                          calcAmt,
                          `8h × ×${detailRate} × ฿${fmt(currentDr, 0)}/วัน`,
                          `฿${fmt(hrRate)}/ชม. · ฿${fmt(minRate, 4)}/นาที`,
                          '#2563eb',
                          isModified ? origAmt : null,
                        )}
                      {salaryMode !== 'late-deduct' &&
                        statCard(
                          `ค่าแรงตามชั่วโมงจริง (${fmt(realH, 2)} ชม.)`,
                          actualWage,
                          `${Math.min(realH, 8)}h × ×${detailRate} × ฿${fmt(
                            currentDr,
                            0,
                          )}/วัน`,
                          forceFullHours ? 'นับเป็น 8 ชม. (เหตุฉุกเฉิน)' : null,
                          '#7c3aed',
                        )}
                      {hasLate &&
                        salaryMode !== 'work-time' &&
                        (dlgIsSkipped
                          ? statCard(
                              'หลังหักสาย',
                              calcAmt,
                              '✓ ยกเว้นหักสายแล้ว',
                              null,
                              '#16a34a',
                            )
                          : statCard(
                              'หลังหักสาย',
                              netAmt,
                              `−฿${fmt(lateAmt)} (สาย ${effMin} นาที)`,
                              `฿${fmt(calcAmt)} − ฿${fmt(lateAmt)} = ฿${fmt(
                                netAmt,
                              )}`,
                              '#f97316',
                            ))}
                    </Box>
                  );
                })()}
              </Box>

              {/* ── ค่าออกสนาม column (เฉพาะพนักงานที่มีสิทธิ์) ── */}
              {detailData?.faEnabled && (
                <Box sx={{ minWidth: 0, width: '100%' }}>
                  {sectionLabel('ค่าออกสนาม', true, '#15803d')}
                  {dlgFieldAllow &&
                    (() => {
                      const realHours = forceFullHours
                        ? 8
                        : actualHr ?? (parseFloat(detailHours) || 0);
                      const faDay = detailData?.faAmt || 0;
                      const faHrRate = faDay / 8;
                      const faHours = Math.min(realHours, 8);
                      const fieldAmt = Number(
                        (faHours * detailRate * faHrRate).toFixed(2),
                      );

                      // OT — same logic as day cell
                      const dlgDay = dlgC?.checkIn
                        ? dayjs(dlgC.checkIn).date()
                        : null;
                      const manEntry =
                        dlgDay != null
                          ? getEntry(detailData?.empId, dlgDay)
                          : null;
                      const approvedOtH = dlgC?.otRequests?.total_hours || 0;
                      const manOtH = manEntry?.ot_hours || 0;
                      let otHours = 0;
                      let otRate = 1.5;
                      let otFieldAmt = 0;
                      if (approvedOtH > 0) {
                        if (manOtH > 0) {
                          otHours = manOtH;
                          otRate = manEntry.ot_rate || 1.5;
                        } else {
                          const effH = dlgC?.otEffectiveHours || 0;
                          otHours = approvedOtH;
                          otRate =
                            approvedOtH > 0
                              ? Math.round((effH / approvedOtH) * 100) / 100
                              : 1.5;
                        }
                      } else if (manOtH > 0) {
                        otHours = manOtH;
                        otRate = manEntry.ot_rate || 1.5;
                      }
                      if (otHours > 0)
                        otFieldAmt = Number(
                          (otHours * otRate * faHrRate).toFixed(2),
                        );
                      const hasOt = otHours > 0;
                      const totalFieldAmt = Number(
                        (fieldAmt + otFieldAmt).toFixed(2),
                      );

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.6,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: hasOt
                                ? 'repeat(2, minmax(0, 1fr))'
                                : '1fr',
                              gap: 0.6,
                            }}
                          >
                            {statCard(
                              `ค่าแรงออกสนาม (${faHours}h)`,
                              fieldAmt,
                              `${faHours}h × ×${detailRate} × ฿${fmt(
                                faDay,
                                0,
                              )}/วัน`,
                              null,
                              '#15803d',
                            )}
                            {hasOt &&
                              statCard(
                                'OT ออกสนาม',
                                otFieldAmt,
                                `${fmt(otHours, 2)}h × ×${otRate} × ฿${fmt(
                                  faHrRate,
                                  2,
                                )}/ชม.`,
                                `ค่าแรงวัน ฿${fmt(faDay, 0)}`,
                                '#0d9488',
                              )}
                          </Box>
                          <Box
                            sx={{ display: 'grid', gridTemplateColumns: '1fr' }}
                          >
                            {statCard(
                              'รวมค่าออกสนาม',
                              totalFieldAmt,
                              hasOt
                                ? `฿${fmt(fieldAmt)} + ฿${fmt(otFieldAmt)}`
                                : `฿${fmt(fieldAmt)}`,
                              null,
                              '#065f46',
                            )}
                          </Box>
                        </Box>
                      );
                    })()}
                </Box>
              )}
            </Box>

            {/* แก้ไขโดย */}
            {dlgOvr && (
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  bgcolor: alpha('#f59e0b', 0.08),
                  borderRadius: 2,
                  border: '1.5px solid',
                  borderColor: alpha('#f59e0b', 0.35),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ mb: 0.75 }}
                >
                  <EditNoteIcon sx={{ fontSize: 15, color: '#d97706' }} />
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      color: '#92400e',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {dlgC?.is_manual
                      ? 'เพิ่มชั่วโมงการทำงาน'
                      : 'ประวัติการแก้ไข'}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: '0.6rem', color: '#78716c', mb: 0.25 }}
                    >
                      {dlgC?.is_manual ? 'เพิ่มโดย' : 'แก้ไขโดย'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: '#92400e',
                      }}
                    >
                      {dlgC.salary_override.edited_by || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: '0.6rem', color: '#78716c', mb: 0.25 }}
                    >
                      เมื่อ
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        color: '#92400e',
                      }}
                    >
                      {dlgC.salary_override.edited_at
                        ? dayjs(dlgC.salary_override.edited_at).format(
                            'DD/MM/YY HH:mm',
                          )
                        : '—'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'space-between',
            px: 3,
            py: 2.25,
            borderTop: '1px solid #e2e8f0',
            bgcolor: '#fcfdff',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {dlgOvr &&
              hrMgmt &&
              (dlgC?.is_manual ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={deleteManualFromDialog}
                  disabled={saving}
                  sx={{ borderRadius: 999, px: 1.5 }}
                >
                  ลบชั่วโมงที่เพิ่ม
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={clearOverride}
                  disabled={saving}
                  sx={{ borderRadius: 999, px: 1.5 }}
                >
                  ล้างการแก้ไข
                </Button>
              ))}
            {dlgTimeOvr && hrMgmt && onCheckInTimeClear && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={clearTimeOverride}
                disabled={saving}
                sx={{ borderRadius: 999, px: 1.5 }}
              >
                ล้างการแก้ไขเวลา
              </Button>
            )}
            {dlgProjOvr && hrMgmt && onProjectClear && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={clearProjectOverride}
                disabled={saving}
                sx={{ borderRadius: 999, px: 1.5 }}
              >
                ล้างการแก้ไขโปรเจค
              </Button>
            )}
            {!dlgOvr &&
              hrMgmt &&
              onManualDelete &&
              dlgC &&
              detailData?.empId &&
              getEntry(detailData.empId, dayjs(dlgC.checkIn).date()) && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={deleteManualFromDialog}
                  disabled={saving}
                  sx={{ borderRadius: 999, px: 1.5 }}
                >
                  ลบชั่วโมงการทำงาน
                </Button>
              )}
            {hrMgmt &&
              onHourOverride &&
              dlgC &&
              !dlgC?.is_manual &&
              !dlgHoursDeleted &&
              (dlgC?.totalHour || 0) > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={deleteWorkHours}
                  disabled={saving}
                  sx={{ borderRadius: 999, px: 1.5 }}
                >
                  ลบชั่วโมงทำงาน
                </Button>
              )}
            {hrMgmt && onHourOverride && dlgHoursDeleted && (
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={restoreWorkHours}
                disabled={saving}
                sx={{ borderRadius: 999, px: 1.5 }}
              >
                คืนชั่วโมงทำงาน
              </Button>
            )}
            {hrMgmt && onOtOverride && dlgHasOt && !dlgOtDisabled && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={deleteOtHours}
                disabled={saving}
                sx={{ borderRadius: 999, px: 1.5 }}
              >
                ลบชั่วโมง OT
              </Button>
            )}
            {hrMgmt && onOtOverride && dlgOtDisabled && (
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={restoreOtHours}
                disabled={saving}
                sx={{ borderRadius: 999, px: 1.5 }}
              >
                คืนชั่วโมง OT
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setDetailOpen(false)}
              disabled={saving}
              size="small"
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: 999, px: 1.5 }}
            >
              ปิด
            </Button>
            {hrMgmt && onSalaryOverride && (
              <Button
                variant="contained"
                size="small"
                onClick={saveOverride}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={14} /> : null}
                sx={{
                  minWidth: 112,
                  borderRadius: 999,
                  boxShadow: 'none',
                  bgcolor: '#2563eb',
                  '&:hover': { bgcolor: '#1d4ed8' },
                }}
              >
                {saving ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    );
  })();

  // AddHoursDialog
  const addHoursDialogJsx = (
    <Dialog
      open={addOpen}
      onClose={() => setAddOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AddCircleOutlineIcon fontSize="small" color="primary" />
          <Typography variant="h6" fontWeight={600}>
            เพิ่มเวลาเข้า-ออกงาน
          </Typography>
        </Stack>
        {addTarget?.empName && (
          <Typography variant="body2" color="text.secondary">
            {addTarget.empName}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          วันที่ {addTarget?.day}/{month + 1}/{year}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {/* ── 1. เวลาเข้า-ออก ── */}
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label="เวลาเข้างาน"
              type="time"
              value={addCheckIn}
              onChange={(e) => {
                setAddCheckIn(e.target.value);
                const h =
                  timeToMins(e.target.value) < timeToMins(addCheckOut)
                    ? (timeToMins(addCheckOut) - timeToMins(e.target.value)) /
                      60
                    : 0;
                if (h > 0)
                  setAddAmt(
                    ((h / 8) * (addTarget?.spd || 0) * addWorkRate).toFixed(2),
                  );
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              fullWidth
              label="เวลาออกงาน"
              type="time"
              value={addCheckOut}
              onChange={(e) => {
                setAddCheckOut(e.target.value);
                const h =
                  timeToMins(addCheckIn) < timeToMins(e.target.value)
                    ? (timeToMins(e.target.value) - timeToMins(addCheckIn)) / 60
                    : 0;
                if (h > 0)
                  setAddAmt(
                    ((h / 8) * (addTarget?.spd || 0) * addWorkRate).toFixed(2),
                  );
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          {/* ── ชั่วโมงที่คำนวณได้ ── */}
          {addH > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: -1 }}
            >
              รวม {addH % 1 === 0 ? addH : addH.toFixed(1)} ชม.
            </Typography>
          )}

          {/* ── 2. ค่าแรง (auto-fill จากเวลา × rate) ── */}
          <TextField
            size="small"
            fullWidth
            label="ค่าแรง (บาท)"
            type="number"
            value={addAmt}
            onChange={(e) => setAddAmt(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">฿</InputAdornment>
              ),
            }}
            helperText={
              addH > 0
                ? `คำนวณอัตโนมัติ: ${
                    addH % 1 === 0 ? addH : addH.toFixed(1)
                  } ชม. ÷ 8 × ฿${
                    addTarget?.spd || 0
                  } × ${addRate} = ฿${addAutoAmt.toFixed(2)}`
                : 'จะคำนวณอัตโนมัติเมื่อเลือกเวลา'
            }
          />

          {/* ── 3. อัตราค่าแรง ── */}
          <TextField
            select
            size="small"
            fullWidth
            label="อัตราค่าแรง"
            value={addWorkRate}
            onChange={(e) => {
              const r = Number(e.target.value);
              setAddWorkRate(r);
              if (addH > 0)
                setAddAmt(((addH / 8) * (addTarget?.spd || 0) * r).toFixed(2));
            }}
          >
            {[
              { value: 1, label: '×1.0 — ปกติ' },
              { value: 1.5, label: '×1.5 — พิเศษ' },
              { value: 2, label: '×2.0 — วันหยุด' },
              { value: 3, label: '×3.0 — วันหยุดนักขัตฤกษ์' },
            ].map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          {/* เบี้ยเลี้ยง */}
          {addAllowanceAmt > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={addIncludeAllowance}
                  onChange={(e) => setAddIncludeAllowance(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  ให้เบี้ยเลี้ยง <strong>+฿{addAllowanceAmt.toFixed(2)}</strong>
                </Typography>
              }
            />
          )}

          {addH > 0 && (
            <Box
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="primary.main" fontWeight={600}>
                ค่าแรงที่จะบันทึก: ฿{addEff.toFixed(2)}
                {addIncludeAllowance && addAllowanceAmt > 0 && (
                  <span style={{ color: '#0369a1', marginLeft: 6 }}>
                    + เบี้ยเลี้ยง ฿{addAllowanceAmt.toFixed(2)}
                  </span>
                )}
              </Typography>
            </Box>
          )}
          {addEx?.edited_by && (
            <Typography variant="caption" color="text.secondary">
              บันทึกโดย: {addEx.edited_by}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
          {addEx && (
            <Button
              size="small"
              color="error"
              onClick={deleteManual}
              disabled={addSaving}
            >
              ลบ
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => setAddOpen(false)}
            disabled={addSaving}
            size="small"
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={saveAddHours}
            disabled={addSaving || addH <= 0}
            startIcon={addSaving ? <CircularProgress size={14} /> : null}
          >
            {addSaving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );

  // ── Bulk day modal JSX ───────────────────────────────────────────────────
  const bulkInMins = timeToMins(bulkCheckIn);
  const bulkOutMins = timeToMins(bulkCheckOut);
  const bulkH =
    bulkCheckIn && bulkCheckOut && bulkOutMins > bulkInMins
      ? (bulkOutMins - bulkInMins) / 60
      : 0;
  const bulkEmpCount = timestamp?.rows?.length || 0;
  const bulkRateOptions = [
    { value: 1, label: '×1.0 — ปกติ' },
    { value: 1.5, label: '×1.5 — พิเศษ' },
    { value: 2, label: '×2.0 — วันหยุด' },
    { value: 3, label: '×3.0 — วันหยุดนักขัตฤกษ์' },
  ];
  const bulkDayJsx = (
    <Dialog
      open={bulkDayOpen}
      onClose={() => setBulkDayOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AddCircleOutlineIcon fontSize="small" color="primary" />
          <Typography variant="h6" fontWeight={600}>
            จัดการวันที่ {bulkDay}/{month + 1}/{year}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          พนักงาน {bulkEmpCount} คน
        </Typography>
      </DialogTitle>
      <Divider />
      <Box sx={{ px: 2, pt: 1.5 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={bulkTab}
          onChange={(_, v) => {
            if (v !== null) setBulkTab(v);
          }}
          sx={{ mb: 0 }}
        >
          <ToggleButton value={0} sx={{ px: 2, fontSize: 13 }}>
            เพิ่มเวลา
          </ToggleButton>
          <ToggleButton value={1} sx={{ px: 2, fontSize: 13 }}>
            แก้ Rate
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <DialogContent sx={{ pt: 1.5 }}>
        {bulkTab === 0 ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label="เวลาเข้างาน"
                type="time"
                value={bulkCheckIn}
                onChange={(e) => setBulkCheckIn(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                fullWidth
                label="เวลาออกงาน"
                type="time"
                value={bulkCheckOut}
                onChange={(e) => setBulkCheckOut(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            {bulkH > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: -1 }}
              >
                รวม {bulkH % 1 === 0 ? bulkH : bulkH.toFixed(1)} ชม.
              </Typography>
            )}
            <TextField
              select
              size="small"
              fullWidth
              label="อัตราค่าแรง"
              value={bulkRate}
              onChange={(e) => setBulkRate(Number(e.target.value))}
            >
              {bulkRateOptions.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            {bulkH > 0 && (
              <Box
                sx={{
                  bgcolor: alpha('#2563eb', 0.06),
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="primary.main"
                  fontWeight={600}
                >
                  เพิ่มให้เฉพาะคนที่ยังไม่มีเวลา:{' '}
                  {bulkH % 1 === 0 ? bulkH : bulkH.toFixed(1)} ชม. × ×{bulkRate}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  คนที่มีเวลาอยู่แล้วจะถูกข้ามไป
                </Typography>
              </Box>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <TextField
              select
              size="small"
              fullWidth
              label="อัตราค่าแรง"
              value={bulkRate}
              onChange={(e) => setBulkRate(Number(e.target.value))}
            >
              {bulkRateOptions.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                bgcolor: alpha('#f59e0b', 0.07),
                px: 2,
                py: 1,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#b45309', fontWeight: 600 }}
              >
                แก้ Rate ให้ทุกคนที่มีชั่วโมงทำงานวันนี้
              </Typography>
              <Typography variant="caption" color="text.secondary">
                คนที่ไม่มีการลงเวลาจะถูกข้ามไป
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          onClick={() => setBulkDayOpen(false)}
          disabled={bulkSaving}
          size="small"
        >
          ยกเลิก
        </Button>
        {bulkTab === 0 ? (
          <Button
            variant="contained"
            size="small"
            onClick={saveBulkDay}
            disabled={bulkSaving || bulkH <= 0}
            startIcon={bulkSaving ? <CircularProgress size={14} /> : null}
          >
            {bulkSaving ? 'กำลังบันทึก...' : 'บันทึกเวลา'}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={saveBulkDayRate}
            disabled={bulkSaving || bulkRate <= 1}
            startIcon={bulkSaving ? <CircularProgress size={14} /> : null}
          >
            {bulkSaving ? 'กำลังบันทึก...' : `ตั้ง ×${bulkRate} ให้ทุกคน`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  // ── computed values for OT dialog (inline — ไม่ define เป็น nested component เพื่อป้องกัน flicker) ──
  const OT_RATES = [
    { value: 1.0, label: 'x1.0 — OT อัตราปกติ' },
    { value: 1.5, label: 'x1.5 — วันทำงานปกติ' },
    { value: 2.0, label: 'x2.0 — OT ต่อจากวันหยุด' },
    { value: 3.0, label: 'x3.0 — วันหยุด / นักขัตฤกษ์' },
  ];
  const otDlgH = parseFloat(addOtHours) || 0;
  const otDlgSpd = addOtTarget?.spd || 0;
  const otDlgAutoAmt = otDlgH > 0 ? otDlgH * (otDlgSpd / 8) * addOtRate : 0;
  const otDlgEx = addOtTarget
    ? getEntry(addOtTarget.empId, addOtTarget.day)
    : null;
  const otDlgHasEx = otDlgEx?.ot_hours != null;

  // ── Breakdown row renderer — defined once at component scope ────────────
  const bRow = (label, sub, amt, note, noteColor = '#f59e0b') => (
    <Box
      key={label}
      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.3 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: '#475569', display: 'block' }}
        >
          {label}
        </Typography>
        {sub && (
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontSize: '0.68rem',
              display: 'block',
              whiteSpace: 'pre-line',
            }}
          >
            {sub}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {fmt(amt)}
        </Typography>
        {note && (
          <Typography
            variant="caption"
            sx={{ color: noteColor, fontSize: '0.68rem', display: 'block' }}
          >
            {note}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // ── shared styles — NO sticky anywhere ────────────────────────────────
  const stickyRight = () => ({});
  const stickyRightTh = () => ({});
  const stickyLeft = () => ({});
  const globalFieldRate =
    timestamp?.company_setting?.payroll?.field_allowance_rate || 200;

  return (
    <Box>
      {detailDialogJsx}
      {addHoursDialogJsx}
      {bulkDayJsx}
      {/* ── OT Dialog (inline — ไม่ใช้ nested component เพื่อป้องกัน flicker) ── */}
      <Dialog
        open={addOtOpen}
        onClose={() => setAddOtOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddCircleOutlineIcon fontSize="small" sx={{ color: '#ea580c' }} />
            <Typography variant="h6" fontWeight={600}>
              เพิ่ม / แก้ไข OT
            </Typography>
          </Stack>
          {addOtTarget?.empName && (
            <Typography variant="body2" color="text.secondary">
              {addOtTarget.empName}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            วันที่ {addOtTarget?.day}/{month + 1}/{year}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              select
              size="small"
              fullWidth
              label="OT Rate"
              value={addOtRate}
              onChange={(e) => setAddOtRate(Number(e.target.value))}
            >
              {OT_RATES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              fullWidth
              label="จำนวนชั่วโมง OT"
              type="number"
              value={addOtHours}
              onChange={(e) => setAddOtHours(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">ชม.</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, max: 24, step: 0.5 }}
            />
            <Box
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                px: 2,
                py: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: alpha(theme.palette.warning.main, 0.25),
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.25}
              >
                ค่า OT (x{addOtRate})
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#ea580c', fontWeight: 700, fontSize: '1.1rem' }}
              >
                ฿
                {otDlgAutoAmt.toLocaleString('th-TH', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Box>
            {otDlgEx?.edited_by && (
              <Typography variant="caption" color="text.secondary">
                บันทึกโดย: {otDlgEx.edited_by}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Box>
            {otDlgHasEx && (
              <Button
                size="small"
                color="error"
                onClick={clearManualOt}
                disabled={addOtSaving}
              >
                ลบ OT
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setAddOtOpen(false)}
              disabled={addOtSaving}
              size="small"
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={saveAddOt}
              disabled={addOtSaving || !addOtHours}
              sx={{ bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' } }}
              startIcon={addOtSaving ? <CircularProgress size={14} /> : null}
            >
              {addOtSaving ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Right-click Detail View ── */}
      <Dialog
        open={dvOpen}
        onClose={() => setDvOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Typography
            fontWeight={700}
            sx={{ fontSize: '1rem', color: '#111827' }}
          >
            {dvData?.checkin ? 'รายละเอียดการลงเวลา' : 'รายละเอียดการลา'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
            {dvData?.empName}
          </Typography>
          {!dvData?.checkin && dvData?.dayNum && (
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {`วันที่ ${String(dvData.dayNum).padStart(2, '0')}/${String(
                month + 1,
              ).padStart(2, '0')}/${year}`}
            </Typography>
          )}
        </Box>
        <Divider />
        <DialogContent sx={{ p: 3, pt: 2 }}>
          {/* Checkin detail — ซ่อนเมื่อเป็น leave-only */}
          {dvData?.checkin && (
            <>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#9ca3af', fontWeight: 500 }}
                  >
                    โปรเจค
                  </Typography>
                  <Typography
                    fontWeight={700}
                    sx={{ mt: 0.25, fontSize: '0.9rem' }}
                  >
                    {dvData.checkin.project_number || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {dvData.checkin.project_name || '—'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#9ca3af', fontWeight: 500 }}
                  >
                    วันที่
                  </Typography>
                  <Typography fontWeight={700} sx={{ mt: 0.25 }}>
                    {dvData.checkin.checkIn
                      ? dayjs(dvData.checkin.checkIn).format('DD/MM/YYYY')
                      : '—'}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {/* Times + Rate */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 3,
                  mb: 2.5,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#9ca3af', fontWeight: 500, display: 'block' }}
                  >
                    เวลาเข้า
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.4rem',
                      fontWeight: 700,
                      color: '#111827',
                      lineHeight: 1.3,
                    }}
                  >
                    {dvData?.checkin?.checkInTime || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#9ca3af', fontWeight: 500, display: 'block' }}
                  >
                    เวลาออก
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1.4rem',
                      fontWeight: 700,
                      color: '#111827',
                      lineHeight: 1.3,
                    }}
                  >
                    {dvData?.checkin?.checkOutTime || '(ยังไม่เช็คเอาท์)'}
                  </Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#9ca3af', fontWeight: 500, display: 'block' }}
                  >
                    Rate
                  </Typography>
                  <Chip
                    label={`${
                      dvData?.checkin?.effectiveRate ||
                      dvData?.checkin?.rate ||
                      1
                    }x`}
                    size="small"
                    sx={{
                      bgcolor: '#7c3aed',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      height: 28,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {/* Photos + Calculation */}
              <Box sx={{ display: 'flex', gap: 3 }}>
                {/* Photos */}
                <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  {[
                    {
                      label: 'Check In',
                      photo:
                        dvData?.checkin?.checkInPhoto ||
                        dvData?.checkin?.imageIn,
                    },
                    {
                      label: 'Check Out',
                      photo:
                        dvData?.checkin?.checkOutPhoto ||
                        dvData?.checkin?.imageOut,
                    },
                  ].map(({ label, photo }) => (
                    <Box key={label} sx={{ textAlign: 'center' }}>
                      {photo ? (
                        <Box
                          component="img"
                          src={photo}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1.5,
                            objectFit: 'cover',
                            border: '1px solid #e5e7eb',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: '#f8fafc',
                            borderRadius: 1.5,
                            border: '1px dashed #cbd5e1',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            px: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 18,
                              color: '#94a3b8',
                              lineHeight: 1,
                            }}
                          >
                            📷
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              color: '#94a3b8',
                              textAlign: 'center',
                              lineHeight: 1.3,
                            }}
                          >
                            ไม่มีรูป
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ color: '#6b7280', display: 'block', mt: 0.75 }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {/* Calculation */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ mb: 1.25, color: '#374151' }}
                  >
                    วิธีคำนวณ
                  </Typography>
                  {(() => {
                    const checkin = dvData?.checkin;
                    if (!checkin?.checkIn) return null;
                    const ci = dayjs(checkin.checkIn);
                    const ciH = ci.hour(),
                      ciM = ci.minute();
                    const ciMin = ciH * 60 + ciM;
                    const workStartMin = 8 * 60;
                    const workEndMin = 17 * 60;
                    const isNextDayOrNoCheckout =
                      !checkin.checkOut ||
                      dayjs(checkin.checkOut).format('YYYY-MM-DD') !==
                        ci.format('YYYY-MM-DD');
                    const isLunchPeriodCheckin = ciH === 12;
                    const effectiveStartMin = isLunchPeriodCheckin
                      ? 13 * 60
                      : Math.max(ciMin, workStartMin);
                    const coMin = checkin.checkOut
                      ? dayjs(checkin.checkOut).hour() * 60 +
                        dayjs(checkin.checkOut).minute()
                      : null;
                    const rawEffectiveEndMin = isNextDayOrNoCheckout
                      ? workEndMin
                      : Math.min(coMin, workEndMin);
                    const fmtMin = (m) =>
                      `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(
                        m % 60,
                      ).padStart(2, '0')}`;
                    const isAfterLunch = ciH >= 12;
                    const isEarlyCheckout =
                      !isAfterLunch &&
                      !isNextDayOrNoCheckout &&
                      coMin != null &&
                      coMin < 13 * 60;
                    const effectiveEndMin = isEarlyCheckout
                      ? Math.min(rawEffectiveEndMin, 12 * 60)
                      : rawEffectiveEndMin;
                    const grossMin = Math.max(
                      0,
                      effectiveEndMin - effectiveStartMin,
                    );
                    const afterLunchMin =
                      isAfterLunch || isEarlyCheckout
                        ? grossMin
                        : Math.max(0, grossMin - 60);
                    const lateMin = Math.max(0, ciMin - workStartMin);
                    const lateRule = (attendanceRules || []).find(
                      (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
                    );
                    const isLateDeducted =
                      !isAfterLunch &&
                      lateRule &&
                      lateMin >= lateRule.threshold_minutes;
                    const lateDeductMin = isLateDeducted
                      ? lateRule.deduct_minutes
                      : 0;
                    const nextDayRule = (attendanceRules || []).find(
                      (r) => r.type === 'LATE_CHECKOUT_NEXT_DAY' && r.enabled,
                    );
                    const noCoDeductMin =
                      isNextDayOrNoCheckout && nextDayRule
                        ? nextDayRule.deduct_minutes
                        : 0;
                    const finalMin = Math.max(
                      0,
                      afterLunchMin - lateDeductMin - noCoDeductMin,
                    );
                    const finalHr = Math.round((finalMin / 60) * 100) / 100;
                    const startNote = isLunchPeriodCheckin
                      ? `(เช็คอิน ${fmtMin(ciMin)} → เริ่มนับ 13:00)`
                      : effectiveStartMin > workStartMin
                      ? '(หลัง 08:00)'
                      : '';
                    const rows = [
                      {
                        label: 'เริ่มนับ',
                        value: fmtMin(effectiveStartMin),
                        note: startNote,
                      },
                      {
                        label: 'สิ้นสุด',
                        value: fmtMin(effectiveEndMin),
                        note: isNextDayOrNoCheckout
                          ? '(cap 17:00)'
                          : isEarlyCheckout
                          ? '(cap 12:00)'
                          : '',
                      },
                      {
                        label: 'รวม',
                        value: `${(grossMin / 60).toFixed(
                          2,
                        )} ชม. (${grossMin} นาที)`,
                      },
                      isAfterLunch
                        ? {
                            label: 'พักเที่ยง',
                            value: 'ไม่หัก (เข้างานหลัง 12:00)',
                            color: '#16a34a',
                          }
                        : isEarlyCheckout
                        ? {
                            label: 'พักเที่ยง',
                            value: 'ไม่หัก (ออกก่อน 13:00)',
                            color: '#16a34a',
                          }
                        : {
                            label: 'หักพักเที่ยง',
                            value: '−1.00 ชม. (−60 นาที)',
                            color: '#6b7280',
                          },
                      ...(isLateDeducted
                        ? [
                            {
                              label: 'หักสาย',
                              value: `−${(lateDeductMin / 60).toFixed(
                                2,
                              )} ชม. (−${lateDeductMin} นาที)`,
                              color: '#f97316',
                            },
                          ]
                        : []),
                      ...(noCoDeductMin > 0
                        ? [
                            {
                              label: !checkin.checkOut
                                ? 'ไม่เช็คเอาท์'
                                : 'เช็คออกข้ามวัน',
                              value: `−${(noCoDeductMin / 60).toFixed(
                                2,
                              )} ชม. (−${noCoDeductMin} นาที)`,
                              color: '#ef4444',
                            },
                          ]
                        : []),
                      {
                        label: 'ชม.ที่ใช้',
                        value: `${finalHr} ชม. (${finalMin} นาที)`,
                        bold: !dvData?.leaveAutoH,
                      },
                      ...(dvData?.leaveAutoH > 0
                        ? [
                            {
                              label: '+ลาครึ่งวัน',
                              value: `+${dvData.leaveAutoH} ชม.`,
                              color: '#15803d',
                            },
                            {
                              label: 'รวมทั้งหมด',
                              value: `${
                                Math.round(
                                  (finalHr + dvData.leaveAutoH) * 100,
                                ) / 100
                              } ชม. (${Math.round(
                                finalMin + dvData.leaveAutoH * 60,
                              )} นาที)`,
                              bold: true,
                              color: '#15803d',
                            },
                          ]
                        : []),
                    ];
                    return (
                      <table
                        style={{ borderCollapse: 'collapse', width: '100%' }}
                      >
                        <tbody>
                          {rows.map((row, i) => (
                            <tr
                              key={i}
                              style={
                                row.bold
                                  ? { borderTop: '1px solid #e5e7eb' }
                                  : {}
                              }
                            >
                              <td
                                style={{
                                  padding: '3px 6px 3px 0',
                                  fontSize: '0.72rem',
                                  color:
                                    row.color ||
                                    (row.bold ? 'inherit' : '#6b7280'),
                                  fontWeight: row.bold ? 700 : 400,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {row.label}
                              </td>
                              <td
                                style={{
                                  padding: '3px 0',
                                  fontSize: '0.72rem',
                                  fontWeight: row.bold ? 700 : 400,
                                  color: row.color || 'inherit',
                                  textAlign: 'right',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {row.value}
                              </td>
                              {row.note && (
                                <td
                                  style={{
                                    padding: '3px 0 3px 4px',
                                    fontSize: '0.65rem',
                                    color: '#9ca3af',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {row.note}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </Box>
              </Box>
            </>
          )}

          {/* ── Leave info ── */}
          {dvData?.leaveAutoH > 0 &&
            (() => {
              const lv = dvData.leave;
              const periodLabel =
                lv?.period === 'FULL_DAY'
                  ? 'เต็มวัน'
                  : lv?.period === 'MORNING'
                  ? 'ครึ่งวันเช้า'
                  : 'ครึ่งวันบ่าย';
              const lvName =
                LEAVE_ABBR[lv?.leave_type] || lv?.leave_type || 'ลา';
              return (
                <>
                  <Divider sx={{ mt: 2, mb: 1.5 }} />
                  <Box
                    sx={{
                      bgcolor: '#f0fdf4',
                      border: '1px solid #86efac',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: '#15803d',
                        display: 'block',
                        mb: 0.75,
                      }}
                    >
                      📋 การลา — ลา{lvName} ({periodLabel})
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.4,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        ชั่วโมงที่ได้จากการลา
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#15803d' }}
                      >
                        +{dvData.leaveAutoH} ชม.
                      </Typography>
                    </Box>
                    {lv?.reason && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          เหตุผล
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#374151',
                            maxWidth: 180,
                            textAlign: 'right',
                          }}
                        >
                          {lv.reason}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {/* วิธีคำนวณ — แสดงเฉพาะ leave-only (ไม่มี checkin) */}
                  {!dvData?.checkin && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ mb: 1, color: '#374151' }}
                      >
                        วิธีคำนวณ
                      </Typography>
                      <table
                        style={{ borderCollapse: 'collapse', width: '100%' }}
                      >
                        <tbody>
                          <tr>
                            <td
                              style={{
                                padding: '3px 6px 3px 0',
                                fontSize: '0.72rem',
                                color: '#15803d',
                                fontWeight: 400,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              +ชั่วโมงการลา
                            </td>
                            <td
                              style={{
                                padding: '3px 0',
                                fontSize: '0.72rem',
                                fontWeight: 400,
                                color: '#15803d',
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              +{dvData.leaveAutoH} ชม.
                            </td>
                          </tr>
                          <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                            <td
                              style={{
                                padding: '3px 6px 3px 0',
                                fontSize: '0.72rem',
                                color: '#15803d',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              รวม
                            </td>
                            <td
                              style={{
                                padding: '3px 0',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                color: '#15803d',
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {dvData.leaveAutoH} ชม.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Box>
                  )}
                </>
              );
            })()}

          {/* ── HR override / manual info ── */}
          {(dvData?.manualEntry ||
            dvData?.checkin?.salary_override?.amount != null) && (
            <>
              <Divider sx={{ mt: 2, mb: 1.5 }} />
              <Stack spacing={1}>
                {dvData?.manualEntry &&
                  (() => {
                    const mn = dvData.manualEntry;
                    const effAmt =
                      mn.amount != null
                        ? Number(mn.amount)
                        : (Number(mn.hours || 0) / 8) *
                          Number(mn.salary_per_day || 0);
                    return (
                      <Box
                        sx={{
                          bgcolor: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: 1.5,
                          p: 1.25,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: '#1d4ed8',
                            display: 'block',
                            mb: 0.75,
                          }}
                        >
                          ✎ เพิ่มชั่วโมงโดย HR
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.4,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            โดย
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ color: '#1e293b' }}
                          >
                            {mn.edited_by || '—'}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.4,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            เพิ่มชั่วโมง
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{ color: '#1d4ed8' }}
                          >
                            {mn.hours} ชม.
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            ค่าแรง
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#1d4ed8' }}
                          >
                            ฿
                            {effAmt.toLocaleString('th-TH', {
                              minimumFractionDigits: 2,
                            })}
                            <span
                              style={{
                                color: '#93c5fd',
                                fontWeight: 400,
                                marginLeft: 4,
                                fontSize: '0.68rem',
                              }}
                            >
                              {mn.amount != null ? '(กำหนดเอง)' : '(อัตโนมัติ)'}
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                {(() => {
                  if (dvData?.checkin?.salary_override?.amount == null)
                    return false;
                  if (!dvData?.manualEntry) return true;
                  const mn = dvData.manualEntry;
                  const effAmt =
                    mn.amount != null
                      ? mn.amount
                      : (mn.hours / 8) * (mn.salary_per_day || 0);
                  return (
                    Math.abs(dvData.checkin.salary_override.amount - effAmt) >
                    0.01
                  );
                })() && (
                  <Box
                    sx={{
                      bgcolor: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: '#b45309',
                        display: 'block',
                        mb: 0.75,
                      }}
                    >
                      ✎ แก้ไขค่าแรงโดย HR
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.4,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        โดย
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ color: '#1e293b' }}
                      >
                        {dvData.checkin.salary_override.edited_by || '—'}
                      </Typography>
                    </Box>
                    {dvData.checkin.salary_override.edited_at && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.4,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          เมื่อ
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {dayjs(
                            dvData.checkin.salary_override.edited_at,
                          ).format('DD/MM/YY HH:mm')}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        ค่าแรง
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9ca3af',
                            textDecoration: 'line-through',
                          }}
                        >
                          ฿
                          {dvData.autoAmt != null
                            ? Number(dvData.autoAmt).toLocaleString('th-TH', {
                                minimumFractionDigits: 2,
                              })
                            : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          →
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: '#b45309' }}
                        >
                          ฿
                          {Number(
                            dvData.checkin.salary_override.amount,
                          ).toLocaleString('th-TH', {
                            minimumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Stack>
            </>
          )}
        </DialogContent>
        {/* ── ยกเว้นหักสาย (DvDialog) ── */}
        {hrMgmt && onSkipLateDeduct && dvData?.checkin && (
          <Box sx={{ px: 3, pb: 1.5 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: dvData.checkin.skip_late_deduct
                  ? alpha('#f97316', 0.07)
                  : 'transparent',
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                border: '1px solid',
                borderColor: dvData.checkin.skip_late_deduct
                  ? alpha('#f97316', 0.35)
                  : '#e5e7eb',
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: dvData.checkin.skip_late_deduct
                      ? '#ea580c'
                      : '#6b7280',
                  }}
                >
                  {dvData.checkin.skip_late_deduct
                    ? '✓ ยกเว้นหักสายแล้ว'
                    : 'ยกเว้นหักสาย'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dvData.checkin.skip_late_deduct
                    ? 'คลิก toggle เพื่อกลับมาหักสายตามปกติ'
                    : 'เปิดเพื่อไม่หักเงินมาสายวันนี้'}
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={!!dvData.checkin.skip_late_deduct}
                onChange={(e) => saveSkipLateDv(e.target.checked)}
                disabled={skipLateDvSaving}
                color="warning"
              />
            </Box>
          </Box>
        )}
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={() => setDvOpen(false)}
            sx={{ color: '#6366f1', fontWeight: 600 }}
          >
            ปิด
          </Button>
        </DialogActions>
      </Dialog>

      {typeSelect === 'FULLTIME' && hrMgmt && (
        <Box
          sx={{
            px: 1,
            py: 0.75,
            display: 'flex',
            justifyContent: 'flex-end',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <Button
            size="small"
            variant="outlined"
            disabled={fillSaving}
            onClick={fillSundays}
            startIcon={fillSaving ? <CircularProgress size={14} /> : null}
            sx={{
              fontSize: '0.75rem',
              color: '#7c3aed',
              borderColor: '#c4b5fd',
              '&:hover': { borderColor: '#7c3aed', bgcolor: '#f5f3ff' },
            }}
          >
            {fillSaving ? 'กำลังเติม...' : 'เติม 8 ชม. วันอาทิตย์ทั้งเดือน'}
          </Button>
        </Box>
      )}

      {/* ── Loading / Render Progress overlay ── */}
      {(isLoading || isRendering) && (
        <Box sx={{ mb: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              py: 0.5,
              bgcolor: isLoading ? '#eff6ff' : '#f0fdf4',
              borderRadius: 1,
              border: `1px solid ${isLoading ? '#bfdbfe' : '#bbf7d0'}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: isLoading ? '#1d4ed8' : '#15803d' }}
            >
              {isLoading
                ? 'กำลังโหลดข้อมูล...'
                : `กำลัง render ตาราง ${renderPct}%`}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {isLoading
                ? ''
                : `${Math.min(renderLimit, totalEmp)} / ${totalEmp} พนักงาน`}
            </Typography>
          </Box>
          <LinearProgress
            variant={isLoading ? 'indeterminate' : 'determinate'}
            value={isLoading ? undefined : renderPct}
            sx={{
              height: 4,
              borderRadius: 0,
              '& .MuiLinearProgress-bar': {
                bgcolor: isLoading ? '#3b82f6' : '#22c55e',
              },
            }}
          />
        </Box>
      )}

      <Box
        ref={scrollRef}
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 260px)',
          WebkitOverflowScrolling: 'touch',
          borderRadius: '8px',
          bgcolor: '#FFFFFF',
          border: `1px solid ${BG.border}`,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)',
          cursor: grabbing ? 'grabbing' : 'grab',
          userSelect: 'none',
          contain: 'layout',
        }}
        onMouseDown={onScrollDown}
        onMouseMove={onScrollMove}
        onMouseUp={onScrollUp}
        onMouseLeave={onScrollUp}
        onClickCapture={onScrollClickCapture}
      >
        <table
          style={{
            borderCollapse: 'collapse',
            fontSize: '13px',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            width: '100%',
            minWidth: 480,
            background: '#FFFFFF',
            tableLayout: 'auto',
            color: BG.text,
          }}
        >
          <thead>
            {/* ── GROUP HEADER ROW (sticky top:0) ── */}
            <tr>
              <th
                colSpan={3}
                style={th0({
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 50,
                  background: BG.hInfo,
                  fontSize: '12px',
                })}
              >
                ข้อมูลพนักงาน
              </th>

              <th
                colSpan={days.length}
                style={th0({
                  position: 'sticky',
                  top: 0,
                  zIndex: 40,
                  background: BG.hDays,
                  fontSize: '12px',
                })}
              >
                การลงเวลา — {MONTH_NAMES[month]} {year}
              </th>

              <th
                colSpan={hrMgmt ? 2 : 1}
                style={th0({
                  position: 'sticky',
                  top: 0,
                  zIndex: 40,
                  background: BG.hSum,
                  borderRight: hrMgmt ? undefined : 'none',
                  fontSize: '12px',
                })}
              >
                สรุป
              </th>

              {hrMgmt && (
                <>
                  <th
                    colSpan={6}
                    style={th0({
                      position: 'sticky',
                      top: 0,
                      zIndex: 40,
                      background: BG.hIncome,
                      fontSize: '12px',
                    })}
                  >
                    รายได้
                  </th>

                  <th
                    colSpan={4}
                    style={th0({
                      position: 'sticky',
                      top: 0,
                      zIndex: 40,
                      background: BG.hDeduct,
                      fontSize: '12px',
                    })}
                  >
                    หัก
                  </th>

                  <th
                    colSpan={4}
                    style={th0({
                      position: 'sticky',
                      top: 0,
                      zIndex: 40,
                      background: BG.hNet,
                      borderRight: 'none',
                      fontSize: '12px',
                    })}
                  >
                    สุทธิ / จัดการ
                  </th>
                </>
              )}
            </tr>

            {/* ── COLUMN HEADER ROW (sticky top:32px) ── */}
            <tr>
              <th
                style={th0({
                  position: 'sticky',
                  top: 34,
                  left: 0,
                  zIndex: 50,
                  background: BG.cInfo,
                  minWidth: LW.no,
                  width: LW.no,
                  borderBottom: `2px solid ${BG.border}`,
                })}
              >
                No.
              </th>
              <th
                style={th0({
                  position: 'sticky',
                  top: 34,
                  left: LW.no,
                  zIndex: 50,
                  background: BG.cInfo,
                  minWidth: LW.name,
                  textAlign: 'left',
                  paddingLeft: 12,
                  borderBottom: `2px solid ${BG.border}`,
                })}
              >
                ชื่อ - สกุล
              </th>
              <th
                style={th0({
                  position: 'sticky',
                  top: 34,
                  left: LW.no + LW.name,
                  zIndex: 50,
                  background: BG.cInfo,
                  minWidth: LW.type,
                  borderBottom: `2px solid ${BG.border}`,
                  fontSize: '12px',
                })}
              >
                <div
                  style={{
                    borderBottom: `1px solid ${BG.border}`,
                    padding: '4px 2px',
                    marginBottom: 4,
                    fontWeight: 500,
                    color: BG.headerText,
                  }}
                >
                  ปกติ
                </div>
                <div
                  style={{
                    color: '#EA580C',
                    fontWeight: 600,
                    padding: '2px 0',
                  }}
                >
                  OT
                </div>
              </th>

              {/* SCROLLABLE: day columns */}
              {dayMeta.map(({ day, dow, isWeekend, isToday: td }) => (
                <th
                  key={day}
                  style={{
                    ...dayTh(dow, td),
                    cursor: hrMgmt ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  title={
                    hrMgmt
                      ? `กำหนดเวลาเข้า-ออกงานให้พนักงานทุกคน วันที่ ${day}/${
                          month + 1
                        }`
                      : undefined
                  }
                  onClick={hrMgmt ? () => openBulkDay(day) : undefined}
                >
                  <div
                    style={{
                      fontSize: '11px',
                      color: BG.textMuted,
                      lineHeight: 1.2,
                      marginBottom: 4,
                      letterSpacing: '0.02em',
                    }}
                  >
                    {DAY_TH_ABBR[dow]}.
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      lineHeight: 1.2,
                      fontWeight: 700,
                      color: isWeekend ? BG.weekendText : BG.headerText,
                    }}
                  >
                    {day}
                  </div>
                </th>
              ))}

              {/* column headers — right section (all sticky top:34) */}
              <th
                style={th0({
                  position: 'sticky',
                  top: 34,
                  zIndex: 30,
                  background: BG.cSum,
                  minWidth: RW.working,
                  borderBottom: `2px solid ${BG.border}`,
                  fontSize: '12px',
                })}
              >
                <div
                  style={{
                    borderBottom: `1px solid ${BG.border}`,
                    padding: '4px 2px',
                    marginBottom: 4,
                    color: BG.headerText,
                  }}
                >
                  วันทำงาน
                </div>
                <div
                  style={{
                    color: '#EA580C',
                    fontSize: '11px',
                    fontWeight: 600,
                  }}
                >
                  OT ชม.
                </div>
              </th>
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cSum,
                    minWidth: RW.dayRate,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  Day Rate
                </th>
              )}
              {hrMgmt && salaryMode !== 'work-time' && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cIncome,
                    minWidth: RW.salary,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  ค่าแรง
                </th>
              )}
              {hrMgmt && salaryMode !== 'late-deduct' && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cIncome,
                    minWidth: RW.actualSalary,
                    borderBottom: `2px solid ${BG.border}`,
                    fontSize: '12px',
                  })}
                >
                  <div>ค่าแรง</div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    (ชม.จริง)
                  </div>
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cIncome,
                    minWidth: RW.ot,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  OT
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cIncome,
                    minWidth: RW.allow,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  เบี้ยเลี้ยง
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cIncome,
                    minWidth: RW.field,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  ค่าออกสนาม
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cIncome,
                    minWidth: RW.bonus,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  เงินได้อื่นๆ
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cDeduct,
                    minWidth: RW.tax,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  หักภาษี
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cDeduct,
                    minWidth: RW.sso,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  ประกันสังคม
                </th>
              )}
              {hrMgmt && salaryMode !== 'work-time' && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cDeduct,
                    minWidth: RW.late,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  หักเงินสาย
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cDeduct,
                    minWidth: RW.other,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  หักอื่นๆ
                </th>
              )}
              {hrMgmt && salaryMode !== 'work-time' && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cNet,
                    minWidth: RW.net,
                    fontWeight: 700,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  สุทธิ
                </th>
              )}
              {hrMgmt && salaryMode !== 'late-deduct' && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cNet,
                    minWidth: RW.actualNet,
                    borderBottom: `2px solid ${BG.border}`,
                    fontWeight: 700,
                    fontSize: '12px',
                  })}
                >
                  <div>สุทธิ</div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    (ชม.จริง)
                  </div>
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cNet,
                    minWidth: RW.note,
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                >
                  หมายเหตุ
                </th>
              )}
              {hrMgmt && (
                <th
                  style={th0({
                    position: 'sticky',
                    top: 34,
                    zIndex: 30,
                    background: BG.cNet,
                    minWidth: RW.manage,
                    borderRight: 'none',
                    borderBottom: `2px solid ${BG.border}`,
                  })}
                ></th>
              )}
            </tr>
          </thead>

          <tbody>
            {timestamp.rows.slice(0, renderLimit).map((emp, idx) => {
              const isEven = idx % 2 === 0;
              const rowBg = isEven ? BG.rowEven : BG.rowOdd;
              const empId = String(emp.employee_id);
              const fi = fieldIndexMap[empId];
              const ff = fi !== undefined ? fields[fi] : null;
              const empFA =
                emp.field_allowance ||
                employeeMap[String(emp.employee_id)]?.field_allowance ||
                null;
              const faEnabled = !!empFA?.enabled;
              const faAmt =
                empFA?.override_amount != null
                  ? empFA.override_amount
                  : globalFieldRate;

              // ── manual_fields helpers (per-row) ──────────────────────────
              const _markField = (fieldName) => {
                if (fi === undefined) return;
                const mf = getValues(`salaryData.${fi}.manual_fields`) || [];
                if (!mf.includes(fieldName))
                  setValue(`salaryData.${fi}.manual_fields`, [
                    ...mf,
                    fieldName,
                  ]);
              };
              const _unmarkField = (fieldName) => {
                if (fi === undefined) return;
                const mf = getValues(`salaryData.${fi}.manual_fields`) || [];
                setValue(
                  `salaryData.${fi}.manual_fields`,
                  mf.filter((f) => f !== fieldName),
                );
              };
              const _applyMarkUnmark = (rawValue, formPath, fieldName) => {
                const autoVal = autoValuesRef.current[empId]?.[fieldName] ?? 0;
                const isEmpty =
                  rawValue === '' ||
                  rawValue === null ||
                  rawValue === undefined;
                if (isEmpty) {
                  if (fi !== undefined)
                    setValue(`salaryData.${fi}.${formPath}`, autoVal);
                  _unmarkField(fieldName);
                } else {
                  const numVal = parseFloat(rawValue);
                  // ถ้าพิมพ์ค่าเดียวกับ auto → ถือว่าไม่ได้แก้จริง → unmark
                  if (!isNaN(numVal) && Math.abs(numVal - autoVal) < 0.005) {
                    _unmarkField(fieldName);
                  } else {
                    _markField(fieldName);
                  }
                }
              };
              const _handleChange = (e, cf, timerKey, fieldName, formPath) => {
                cf.onChange(e);
                const rawValue = e.target.value;
                lastRawValueRef.current[timerKey] = rawValue; // เก็บค่าล่าสุดจาก onChange
                dirtyFieldsRef.current[timerKey] = true;
                clearTimeout(adjDebounceRef.current[timerKey]);
                adjDebounceRef.current[timerKey] = setTimeout(() => {
                  // ไม่ reset dirty ที่นี่ — ให้ blur เป็นคนรับผิดชอบ reset เพียงที่เดียว
                  // ถ้า reset dirty ใน debounce: type → debounce fires → dirty=false → ลบ → blur → wasDirty=false → unmark ไม่ถูกเรียก (bug!)
                  _applyMarkUnmark(rawValue, formPath, fieldName);
                  onAdjSave?.(empId);
                }, 800);
              };
              const _handleBlur = (e, cf, formPath, fieldName, timerKey) => {
                let rawValue =
                  lastRawValueRef.current[timerKey] ?? e.target.value;

                if (rawValue !== '' && rawValue != null) {
                  const num = Number(rawValue);
                  if (!isNaN(num)) {
                    rawValue = num.toFixed(2);
                    if (fi !== undefined) {
                      setValue(`salaryData.${fi}.${formPath}`, rawValue);
                    }
                  }
                }

                cf.onBlur(e);
                clearTimeout(adjDebounceRef.current[timerKey]);
                const wasDirty = dirtyFieldsRef.current[timerKey];
                dirtyFieldsRef.current[timerKey] = false;
                if (!wasDirty) return;

                _applyMarkUnmark(rawValue, formPath, fieldName);
                onAdjSave?.(empId);
              };

              // ดึงจาก pre-computed map (คำนวณไว้แล้วใน empSummaryMap)
              const empSum = empSummaryMap[empId] || {};
              const {
                displayCheck,
                displayCheckEff,
                displayAmt,
                manualAmt = 0,
                netOtH: displayNetOtH,
                netOtEffH: displayNetOtEffH,
                netOtAmtDelta = 0,
              } = empSum;

              // ── Breakdown popover builders (lazy — computed only on click, not every render) ─
              const hrR = (emp.salary_per_day || 0) / 8;
              const getDayHrRate = (ck) => {
                if (!ck) return hrR;
                if (ck.salary_override?.amount != null)
                  return ck.salary_override.amount / 8;
                const er2 = ck.effectiveRate || ck.rate || 1;
                return (er2 * (emp.salary_per_day || 0)) / 8;
              };

              const buildSalaryContent = () => {
                let salaryBreakdownTotal = 0;
                const isFulltimeEmp = emp.emp_type === 'FULLTIME';
                // deduplicate: keep only one checkin per calendar date (last one wins, same as checkinByKey)
                const seenDates = new Set();
                const uniqueCheckins = [];
                for (const ck of [...(emp.checkins || [])].reverse()) {
                  const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
                  if (!seenDates.has(dk)) {
                    seenDates.add(dk);
                    uniqueCheckins.unshift(ck);
                  }
                }
                const salaryRows = [
                  ...uniqueCheckins
                    .map((ck) => {
                      const d2 = dayjs(ck.checkIn);
                      const ckDay = d2.date();
                      const lv = isFulltimeEmp
                        ? leaveByKey[`${empId}-${ckDay}`]
                        : null;
                      if (lv?.period === 'FULL_DAY') return null; // leave ครอบ — ไม่นับ checkin
                      const lbl = `${ckDay}/${month + 1} (${
                        DAY_TH_ABBR[d2.day()]
                      })`;
                      const er = ck.effectiveRate || 1;
                      if (ck.salary_override?.amount != null) {
                        const ovr = ck.salary_override;
                        const ovrR = ovr.rate ?? er;
                        const ovrDr = ovr.day_rate ?? emp.salary_per_day ?? 0;
                        const ovrH = ovr.hours ?? ck.totalHour ?? 8;
                        const ovrAmt = Number(ovr.amount);
                        const sub = `${fmt(ovrH, 2)}h × ×${ovrR} × ฿${fmt(
                          ovrDr,
                          0,
                        )}/วัน (${fmt(ovrDr / 8)}/ชม.)`;
                        salaryBreakdownTotal += ovrAmt;
                        return bRow(
                          lbl,
                          sub,
                          lv
                            ? Math.min(ovrAmt, (emp.salary_per_day || 0) / 2)
                            : ovrAmt,
                          `✎ ${ovr.edited_by || 'HR'}`,
                          '#f59e0b',
                        );
                      }
                      const citH3 = ck.checkInTime
                        ? parseInt(ck.checkInTime.split(':')[0], 10)
                        : 0;
                      const isAft = citH3 >= 12;
                      const displayH = lv ? 4 : isAft ? 4 : 8;
                      const totalAmt =
                        (displayH / 8) * er * (emp.salary_per_day || 0);
                      const sub = `${displayH}h × ×${er} × ฿${fmt(
                        emp.salary_per_day,
                        0,
                      )}/วัน (${fmt(hrR)}/ชม.)`;
                      salaryBreakdownTotal += totalAmt;
                      return bRow(
                        lbl,
                        sub,
                        totalAmt,
                        er > 1 ? `[×${er} วันหยุด]` : null,
                        '#10b981',
                      );
                    })
                    .filter(Boolean),
                  // leave rows (FULLTIME only)
                  ...(!isFulltimeEmp
                    ? []
                    : days.reduce((acc, d2) => {
                        const lv = leaveByKey[`${empId}-${d2}`];
                        if (!lv) return acc;
                        const lvH = lv.period === 'FULL_DAY' ? 8 : 4;
                        const lvAmt = (lvH / 8) * (emp.salary_per_day || 0);
                        const d2js = dayjs(
                          `${year}-${String(month + 1).padStart(
                            2,
                            '0',
                          )}-${String(d2).padStart(2, '0')}`,
                        );
                        const lbl = `${d2}/${month + 1} (${
                          DAY_TH_ABBR[d2js.day()]
                        })`;
                        salaryBreakdownTotal += lvAmt;
                        acc.push(
                          bRow(
                            lbl,
                            `${lvH}h (ลา${
                              lv.period === 'FULL_DAY' ? 'เต็มวัน' : 'ครึ่งวัน'
                            }) × ฿${fmt(emp.salary_per_day, 0)}/วัน`,
                            lvAmt,
                            'ลา',
                            '#6366f1',
                          ),
                        );
                        return acc;
                      }, [])),
                  ...days.reduce((acc, d2) => {
                    const e2 = manualByKey[`${empId}-${d2}`];
                    if (!e2 || !e2.hours || e2.timestamp_ref) return acc;
                    const amt2 =
                      e2.amount != null
                        ? e2.amount
                        : (e2.hours / 8) * (emp.salary_per_day || 0);
                    salaryBreakdownTotal += amt2;
                    acc.push(
                      bRow(
                        `${d2}/${month + 1} (manual)`,
                        `${e2.hours}h ÷ 8 × ${fmt(emp.salary_per_day)}`,
                        amt2,
                        '✎ manual',
                        '#3b82f6',
                      ),
                    );
                    return acc;
                  }, []),
                ];
                return (
                  <Box sx={{ minWidth: 250 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      hrRate = {fmt(emp.salary_per_day)} ÷ 8 = {fmt(hrR)}
                    </Typography>
                    {salaryRows}
                    <Box
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        mt: 0.75,
                        pt: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        รวม
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#065f46' }}
                      >
                        {fmt(salaryBreakdownTotal)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildOtContent = () => {
                let otBreakdownTotal = 0;
                const otBdRows = [
                  ...(emp.checkins || [])
                    .filter((ck) => ck.otRequests?.total_hours)
                    .map((ck) => {
                      const d2 = dayjs(ck.checkIn);
                      const lbl = `${d2.date()}/${month + 1} (${
                        DAY_TH_ABBR[d2.day()]
                      })`;
                      const rawH = ck.otRequests.total_hours || 0;
                      const effH = ck.otEffectiveHours || 0;
                      const otRate =
                        rawH > 0
                          ? Math.round((effH / rawH) * 100) / 100
                          : ck.otRequests.rate || 1.5;
                      const dayHrR = getDayHrRate(ck);
                      const manE = manualByKey[`${empId}-${d2.date()}`];
                      const daySalary = dayHrR * 8;
                      if (manE?.ot_hours) {
                        const r2 = manE.ot_rate || 1.5;
                        const manAmt =
                          manE.ot_amount != null
                            ? manE.ot_amount
                            : manE.ot_hours * dayHrR * r2;
                        otBreakdownTotal += manAmt;
                        return bRow(
                          lbl,
                          `${manE.ot_hours}h × ×${r2} × ฿${fmt(
                            dayHrR,
                          )}/ชม.  |  ค่าแรงวัน ฿${fmt(daySalary, 0)}`,
                          manAmt,
                          `✎ override (แทน ${rawH}h ×${otRate})`,
                          '#f59e0b',
                        );
                      }
                      const otAmt = effH * dayHrR;
                      otBreakdownTotal += otAmt;
                      return bRow(
                        lbl,
                        `${rawH}h × ×${otRate} × ฿${fmt(
                          dayHrR,
                        )}/ชม.  |  ค่าแรงวัน ฿${fmt(daySalary, 0)}`,
                        otAmt,
                        '[อนุมัติ]',
                        '#10b981',
                      );
                    }),
                  ...days.reduce((acc, d2) => {
                    const e2 = manualByKey[`${empId}-${d2}`];
                    if (!e2?.ot_hours) return acc;
                    const ckForDay = checkinByKey[`${empId}-${d2}`];
                    if (ckForDay?.otRequests?.total_hours) return acc;
                    const dayHrR = getDayHrRate(ckForDay);
                    const r2 = e2.ot_rate || 1.5;
                    const amt2 =
                      e2.ot_amount != null
                        ? e2.ot_amount
                        : e2.ot_hours * dayHrR * r2;
                    otBreakdownTotal += amt2;
                    acc.push(
                      bRow(
                        `${d2}/${month + 1} (+OT)`,
                        `${e2.ot_hours}h × ×${r2} × ฿${fmt(dayHrR)}/ชม.`,
                        amt2,
                        '✎ เพิ่มเอง',
                        '#3b82f6',
                      ),
                    );
                    return acc;
                  }, []),
                ];
                return (
                  <Box sx={{ minWidth: 250 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      OT = ชม. × ×rate × (ค่าแรงวันนั้น ÷ 8)
                    </Typography>
                    {otBdRows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มี OT
                      </Typography>
                    ) : (
                      otBdRows
                    )}
                    <Box
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        mt: 0.75,
                        pt: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        รวม
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#92400e' }}
                      >
                        {fmt(otBreakdownTotal)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildAllowanceContent = () => {
                // อัตราเบี้ยเลี้ยงต่อวัน — ดูจาก checkin ที่มี extra_day ก่อน ถ้าไม่มีดู manual entry
                const rateFromCheckin =
                  (emp.checkins || []).find((c) => (c.extra_day || 0) > 0)
                    ?.extra_day || 0;
                const rateFromManual =
                  days
                    .map((d) => getEntry(empId, d)?.allowance_amount || 0)
                    .find((a) => a > 0) || 0;
                const dailyRate = rateFromCheckin || rateFromManual;

                let allowTotal = 0;
                const allowRows = [];

                days.forEach((d) => {
                  const ck = checkinByKey[`${empId}-${d}`];
                  const entry = getEntry(empId, d);
                  const hasWork =
                    ck || (entry?.hours > 0 && !entry?.timestamp_ref);
                  if (!hasWork) return;

                  // ใช้ rate จาก checkin/entry วันนั้น ถ้ามี ไม่งั้นใช้ dailyRate
                  const amt =
                    ck?.extra_day || entry?.allowance_amount || dailyRate;
                  if (!amt) return;

                  const dt = dayjs(
                    `${year}-${String(month + 1).padStart(2, '0')}-${String(
                      d,
                    ).padStart(2, '0')}`,
                  );
                  const lbl = `${d}/${month + 1} (${DAY_TH_ABBR[dt.day()]})`;
                  const src = entry?.allowance_amount ? 'manual' : 'checkin';
                  allowTotal += amt;
                  allowRows.push(
                    bRow(
                      lbl,
                      `฿${fmt(amt)} / วัน (${src})`,
                      amt,
                      null,
                      '#0ea5e9',
                    ),
                  );
                });

                return (
                  <Box sx={{ minWidth: 240 }}>
                    {dailyRate > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                      >
                        {`อัตรา ฿${fmt(dailyRate)}/วันทำงาน`}
                      </Typography>
                    )}
                    {allowRows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มีรายการเบี้ยเลี้ยง
                      </Typography>
                    ) : (
                      allowRows
                    )}
                    {allowRows.length > 0 && (
                      <Box
                        sx={{
                          borderTop: '1px solid #e5e7eb',
                          mt: 0.75,
                          pt: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          รวม {allowRows.length} วัน
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: '#0369a1' }}
                        >
                          +{fmt(allowTotal)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              };

              const buildLateContent = () => {
                const W_START_L = 8 * 60;
                const lateRule = (attendanceRules || []).find(
                  (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
                );
                const lateSpm = emp.salary_per_month || 0;
                const lateSrpd = emp.salary_per_day || 0;
                let lateBreakdownTotal = 0;
                const uniqueCk = [];
                const seen = new Set();
                [...(emp.checkins || [])].reverse().forEach((ck) => {
                  const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
                  if (!seen.has(dk)) {
                    seen.add(dk);
                    uniqueCk.unshift(ck);
                  }
                });
                const lateBdRows = uniqueCk.flatMap((ck) => {
                  if (ck.is_manual) return [];
                  if (ck.skip_late_deduct?.enabled) return [];
                  if (ck.hour_override?.hours != null) return [];
                  const cit = ck.checkInTime;
                  if (!cit) return [];
                  const [citH, citM] = cit.split(':').map(Number);
                  const isAfternoon = citH >= 12;
                  const shiftStartL = isAfternoon ? 13 * 60 : W_START_L;
                  const ciMin = citH * 60 + citM;
                  const lateMin = Math.max(0, ciMin - shiftStartL);
                  if (!lateMin) return [];
                  let effectiveMin;
                  if (!lateRule) {
                    effectiveMin = lateMin;
                  } else {
                    if (lateMin < lateRule.threshold_minutes) return [];
                    effectiveMin = Math.max(
                      lateMin,
                      lateRule.deduct_minutes || 0,
                    );
                  }
                  const baseDr =
                    ck.salary_override?.day_rate ??
                    (lateSrpd > 0 ? lateSrpd : lateSpm / 30);
                  const ckRate =
                    ck.salary_override?.rate ??
                    ck.effectiveRate ??
                    ck.rate ??
                    1;
                  const effectiveDr = baseDr * ckRate;
                  const ckMinRate = effectiveDr / 8 / 60;
                  const ckHrRate = ckMinRate * 60;
                  const deductAmt = Number(
                    (effectiveMin * ckMinRate).toFixed(2),
                  );
                  lateBreakdownTotal += deductAmt;
                  const ci = dayjs(ck.checkIn);
                  const shiftLabel = isAfternoon ? '13:00' : '08:00';
                  const lbl = `${ci.date()}/${month + 1} (${
                    DAY_TH_ABBR[ci.day()]
                  }) [${shiftLabel}] (สาย ${lateMin} นาที) (฿${fmt(
                    effectiveDr,
                    0,
                  )}/วัน)`;
                  const ruleNote = lateRule
                    ? `เข้า ${cit} [${shiftLabel}] → max(${lateMin}, ${lateRule.deduct_minutes}) = ${effectiveMin} นาที`
                    : `เข้า ${cit} [${shiftLabel}]`;
                  const sub = `฿${fmt(ckHrRate)}/ชม. , ฿${fmt(
                    ckMinRate,
                    4,
                  )}/นาที\n= ${effectiveMin} × ${fmt(ckMinRate, 4)}/นาที`;
                  return [bRow(lbl, sub, deductAmt, ruleNote, '#f97316')];
                });
                return (
                  <Box sx={{ minWidth: 260 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      {lateRule
                        ? `กฎ: สาย ≥ ${lateRule.threshold_minutes} นาที → หัก ${lateRule.deduct_minutes} นาที (เช้า 08:00 / บ่าย 13:00)`
                        : 'ไม่มีกฎ: คำนวณนาทีสาย (เช้า 08:00 / บ่าย 13:00)'}
                    </Typography>
                    {lateBdRows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มีรายการหักสาย
                      </Typography>
                    ) : (
                      lateBdRows
                    )}
                    {lateBdRows.length > 0 && (
                      <Box
                        sx={{
                          borderTop: '1px solid #e5e7eb',
                          mt: 0.75,
                          pt: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          รวมหักสาย
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: '#ea580c' }}
                        >
                          −{fmt(lateBreakdownTotal)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              };

              // ── Build dailyRows สำหรับส่งให้ modal รายงานค่าแรง ──
              // ใช้ logic เดียวกับ buildSalaryContent + buildOtContent + buildLateContent + buildAllowanceContent
              const buildDailyRowsForReport = () => {
                const spd2 = emp.salary_per_day || 0;
                const isFulltime = emp.emp_type === 'FULLTIME';
                const lateRule2 = (attendanceRules || []).find(
                  (r) => r.type === 'LATE_ARRIVAL' && r.enabled,
                );

                const getLeaveAutoH2 = (d, ck) => {
                  const lv = leaveByKey[`${empId}-${d}`];
                  if (!lv || !isFulltime) return 0;
                  const ch = ck ? dayjs(ck.checkIn).hour() : null;
                  if (lv.period === 'FULL_DAY' && !ck) return 8;
                  if (lv.period === 'MORNING' && (!ck || ch >= 12)) return 4;
                  if (lv.period === 'AFTERNOON' && (!ck || ch < 12)) return 4;
                  return 0;
                };

                const calcLateDed2 = (ck) => {
                  if (
                    !ck ||
                    ck.is_manual ||
                    ck.skip_late_deduct?.enabled ||
                    ck.hour_override?.hours != null
                  )
                    return 0;
                  const cit = ck.checkInTime;
                  if (!cit) return 0;
                  const [citH, citM] = cit.split(':').map(Number);
                  const shiftStart2 = citH >= 12 ? 13 * 60 : 8 * 60;
                  const lateMin = Math.max(0, citH * 60 + citM - shiftStart2);
                  if (!lateMin) return 0;
                  if (lateRule2 && lateMin < lateRule2.threshold_minutes)
                    return 0;
                  const effectiveMin = lateRule2
                    ? Math.max(lateMin, lateRule2.deduct_minutes || 0)
                    : lateMin;
                  const baseDr = ck.salary_override?.day_rate ?? spd2;
                  const ckRate =
                    ck.salary_override?.rate ??
                    ck.effectiveRate ??
                    ck.rate ??
                    1;
                  return Number(
                    ((effectiveMin * baseDr * ckRate) / 8 / 60).toFixed(2),
                  );
                };

                const rows = [];
                for (const d of days) {
                  const ck = checkinByKey[`${empId}-${d}`] || null;
                  const mn = manualByKey[`${empId}-${d}`] || null;
                  const leave = leaveByKey[`${empId}-${d}`] || null;
                  const leaveAutoH = getLeaveAutoH2(d, ck);
                  const date = dayjs(
                    `${year}-${String(month + 1).padStart(2, '0')}-${String(
                      d,
                    ).padStart(2, '0')}`,
                  );
                  const dow = date.day();
                  const isSun = dow === 0;
                  const isSat = dow === 6;

                  const validManual = mn && !mn.timestamp_ref ? mn : null;
                  if (!ck && !validManual && leaveAutoH === 0) {
                    rows.push({
                      d,
                      dow,
                      date,
                      checkInTime: null,
                      checkOutTime: null,
                      hours: 0,
                      wage: 0,
                      actualH: 0,
                      actualWage: 0,
                      allowance: 0,
                      fieldAllowance: 0,
                      notes: [],
                      er: 1,
                      otHours: 0,
                      otRate: 1.5,
                      otAmt: 0,
                      lateDed: 0,
                      leaveAutoH: 0,
                      isSun,
                      isSat,
                      isManual: false,
                      isLeaveOnly: false,
                      isEmpty: true,
                      project: null,
                    });
                    continue;
                  }

                  const er = ck
                    ? ck.is_manual
                      ? 1
                      : ck.effectiveRate || ck.rate || 1
                    : validManual?.work_rate || 1;
                  let wage = 0;
                  let hours = 0;
                  let actualH = 0;
                  let actualWage = 0;
                  const notes = [];

                  // salary (buildSalaryContent)
                  if (ck) {
                    const citH4 = ck.checkInTime
                      ? parseInt(ck.checkInTime.split(':')[0], 10)
                      : 0;
                    const isAft4 = citH4 >= 12;
                    actualH = isAft4
                      ? Math.min(ck.totalHour ?? 0, 4)
                      : Math.min(ck.totalHour ?? 0, 8);
                    actualWage = (actualH / 8) * er * spd2;

                    if (ck.salary_override?.amount != null) {
                      wage = Number(ck.salary_override.amount);
                      hours = ck.salary_override.hours ?? ck.totalHour ?? 8;
                      notes.push({
                        text: `แก้ค่าแรงโดย: ${
                          ck.salary_override.edited_by || 'HR'
                        }`,
                        type: 'wage',
                      });
                    } else {
                      hours = isAft4 ? 4 : Math.max(ck.totalHour ?? 8, 8);
                      wage = (hours / 8) * er * spd2;
                      if (er > 1)
                        notes.push({ text: `×${er} วันหยุด`, type: 'holiday' });
                      if (isAft4)
                        notes.push({
                          text: 'ลงเวลาช่วงบ่าย',
                          type: 'afternoon',
                        });
                    }
                    if (
                      ck.deductionReasons?.includes('late') &&
                      !ck.skip_late_deduct?.enabled
                    )
                      notes.push({ text: 'สาย ส', type: 'late' });
                    if (ck.deductionReasons?.includes('no_checkout'))
                      notes.push({
                        text: 'ไม่เช็คเอาท์ อ',
                        type: 'nocheckout',
                      });
                    if (ck.skip_late_deduct?.enabled)
                      notes.push({ text: 'ยกเว้นหักสาย', type: 'skip_late' });
                  }
                  if (validManual) {
                    const mRate = validManual.work_rate || 1;
                    const mWage =
                      validManual.amount != null
                        ? Number(validManual.amount)
                        : (validManual.hours / 8) * spd2 * mRate;
                    const rateLabel = mRate > 1 ? ` (×${mRate} วันหยุด)` : '';
                    if (!ck) {
                      hours = validManual.hours || 0;
                      wage = mWage;
                      actualH = validManual.hours || 0;
                      actualWage = mWage;
                      notes.push({
                        text: `เพิ่มชั่วโมง ${
                          validManual.hours
                        } ชม.${rateLabel} ค่าแรง ${fmt(mWage)} บาท โดย ${
                          validManual.edited_by || 'HR'
                        }`,
                        type: 'hour',
                      });
                    } else {
                      wage += mWage;
                      actualWage += mWage;
                      notes.push({
                        text: `+เพิ่มชั่วโมง ${
                          validManual.hours
                        } ชม.${rateLabel} +${fmt(mWage)} บาท โดย ${
                          validManual.edited_by || 'HR'
                        }`,
                        type: 'hour',
                      });
                    }
                  }
                  if (leaveAutoH > 0) {
                    const leaveWage = (leaveAutoH / 8) * spd2;
                    const leaveName =
                      LEAVE_ABBR[leave?.leave_type] ||
                      leave?.leave_type ||
                      'ลา';
                    const pLbl =
                      leave?.period === 'FULL_DAY'
                        ? 'เต็มวัน'
                        : leave?.period === 'MORNING'
                        ? 'ครึ่งวันเช้า'
                        : 'ครึ่งวันบ่าย';
                    hours += leaveAutoH;
                    wage += leaveWage;
                    actualH += leaveAutoH;
                    actualWage += leaveWage;
                    notes.push({
                      text: `ลา${leaveName} (${pLbl}) +${leaveAutoH} ชม. = +${fmt(
                        leaveWage,
                      )} บาท`,
                      type: 'leave',
                    });
                  }

                  // OT (buildOtContent)
                  const dayHrR2 = getDayHrRate(ck);
                  const approvedOtH = ck?.otRequests?.total_hours || 0;
                  const manOtH = mn?.ot_hours || 0;
                  let otHours = 0;
                  let otRate = 1.5;
                  let otAmt = 0;
                  let isManualOt = false;
                  if (approvedOtH > 0) {
                    if (manOtH > 0) {
                      const r2 = mn.ot_rate || 1.5;
                      otHours = manOtH;
                      otRate = r2;
                      otAmt =
                        mn.ot_amount != null
                          ? mn.ot_amount
                          : manOtH * dayHrR2 * r2;
                      isManualOt = true;
                    } else {
                      const effH = ck.otEffectiveHours || 0;
                      otHours = approvedOtH;
                      otRate =
                        approvedOtH > 0
                          ? Math.round((effH / approvedOtH) * 100) / 100
                          : 1.5;
                      otAmt = effH * dayHrR2;
                    }
                  } else if (manOtH > 0) {
                    const r2 = mn.ot_rate || 1.5;
                    otHours = manOtH;
                    otRate = r2;
                    otAmt =
                      mn.ot_amount != null
                        ? mn.ot_amount
                        : manOtH * dayHrR2 * r2;
                    isManualOt = true;
                  }

                  // allowance + late (buildAllowanceContent / buildLateContent)
                  const allowance = ck?.extra_day || mn?.allowance_amount || 0;
                  const lateDed = calcLateDed2(ck);

                  // field allowance
                  const faGlobalRate =
                    timestamp?.company_setting?.payroll?.field_allowance_rate ||
                    0;
                  const empMap2 = {};
                  employees.forEach((e) => {
                    empMap2[String(e._id || e.id)] = e;
                  });
                  const empFA2 =
                    emp.field_allowance ||
                    empMap2[String(emp.employee_id)]?.field_allowance ||
                    null;
                  const faDay =
                    empFA2?.override_amount != null
                      ? empFA2.override_amount
                      : faGlobalRate;
                  const faHrRate = faDay / 8;
                  let fieldAllowance = 0;
                  const savedFA = ck?.salary_override?.field_allowance_day;
                  const pn = (ck?.project_number || '').toLowerCase().trim();
                  const defaultFA = matchesProjectPrefix(pn, faDefaultPrefixes);
                  const isFA = savedFA != null ? savedFA : defaultFA;

                  if (faDay && ck && isFA) {
                    const erFA = ck.effectiveRate || ck.rate || 1;
                    const actH = ck.hour_override?.hours ?? ck.totalHour ?? 0;
                    const faHours = Math.min(actH > 0 ? actH : 8, 8);
                    fieldAllowance += Number(
                      (faHours * erFA * faHrRate).toFixed(2),
                    );
                    if (otHours > 0) {
                      fieldAllowance += Number(
                        (otHours * otRate * faHrRate).toFixed(2),
                      );
                    }
                  }

                  rows.push({
                    d,
                    dow,
                    date,
                    checkInTime: ck?.checkInTime || null,
                    checkOutTime: ck?.checkOutTime || null,
                    project: ck?.project_name
                      ? `${ck.project_name}${
                          ck.project_number ? ` (${ck.project_number})` : ''
                        }`
                      : null,
                    projectNumber: ck?.project_number || null,
                    projectId: ck?.project_id ? String(ck.project_id) : null,
                    hours,
                    wage,
                    actualH,
                    actualWage,
                    allowance,
                    fieldAllowance,
                    notes,
                    er,
                    otHours,
                    otRate,
                    otAmt,
                    isManualOt,
                    lateDed,
                    leaveAutoH,
                    leave,
                    isSun,
                    isSat,
                    isManual: !ck && !!validManual,
                    isLeaveOnly: !ck && !validManual && leaveAutoH > 0,
                    isEmpty: false,
                  });
                }
                return rows;
              };

              const buildSsoContent = () => {
                const ssoEnabled = !!ff?.sso_tax?.status;
                const ssoRate = ff?.sso_tax?.number || 5;
                const ssoPaid = ff?.sso_paid || 0;
                const ssoBase = salaryMode === 'work-time' ? (empSum.actualSalaryAmt || 0) : displayAmt;
                const ssoRawCalc = ssoBase * (ssoRate / 100);
                let ssoThisPeriod = ssoRawCalc;
                if (ssoThisPeriod > 0) {
                  if (ssoThisPeriod + ssoPaid > 875)
                    ssoThisPeriod = Math.max(0, 875 - ssoPaid);
                  if (ssoThisPeriod + ssoPaid < 83)
                    ssoThisPeriod = Math.max(0, 83 - ssoPaid);
                }
                // แสดง field งวดที่ 1 เมื่อ: filter 16-30 AND พนักงานรายวัน
                const isPeriod2 = selectedOption === '31';
                const isDaily = emp.emp_type !== 'FULLTIME';
                const showPeriod1Row = isPeriod2 && isDaily;
                return (
                  <Box sx={{ minWidth: 270 }}>
                    {!ssoEnabled ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่ได้เปิดใช้ประกันสังคมสำหรับพนักงานคนนี้
                      </Typography>
                    ) : (
                      <>
                        {showPeriod1Row && (
                          <Box
                            sx={{
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: 1,
                              p: 0.75,
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                fontWeight: 600,
                                display: 'block',
                                mb: 0.5,
                              }}
                            >
                              งวดที่ 1 (1–15)
                            </Typography>
                            {ssoPaid > 0 ? (
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#475569' }}
                                  >
                                    SSO บันทึกแล้ว
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ color: '#b45309' }}
                                  >
                                    ฿{fmt(ssoPaid)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 0.25,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.68rem',
                                    }}
                                  >
                                    เหลือหักได้อีก
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.68rem',
                                    }}
                                  >
                                    875 − {fmt(ssoPaid)} = ฿{fmt(875 - ssoPaid)}
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8', fontStyle: 'italic' }}
                              >
                                ยังไม่บันทึกงวดที่ 1
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#475569' }}
                          >
                            {showPeriod1Row
                              ? 'ฐานค่าแรงงวดที่ 2 (16–สิ้นเดือน)'
                              : 'ฐานค่าแรงงวดนี้'}
                          </Typography>
                          <Typography variant="caption" fontWeight={700}>
                            ฿{fmt(ssoBase)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: showPeriod1Row || ssoPaid > 0 ? 0.5 : 0.75,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#475569' }}
                          >
                            คำนวณ {ssoRate}%
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            ฿{fmt(ssoRawCalc)}
                          </Typography>
                        </Box>
                        {!showPeriod1Row && ssoPaid > 0 && (
                          <>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280' }}
                              >
                                จ่ายแล้วงวดก่อน
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280' }}
                              >
                                +฿{fmt(ssoPaid)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.75,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: '#475569' }}
                              >
                                รวมสะสม
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                ฿{fmt(ssoRawCalc + ssoPaid)}
                              </Typography>
                            </Box>
                          </>
                        )}
                        {showPeriod1Row && ssoPaid > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#475569' }}
                            >
                              รวมสะสมทั้งเดือน
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              ฿{fmt(ssoRawCalc + ssoPaid)}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            borderTop: '1px solid #e5e7eb',
                            pt: 0.75,
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            {showPeriod1Row
                              ? 'SSO หักงวดที่ 2'
                              : 'SSO หักงวดนี้'}
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#b91c1c' }}
                          >
                            −฿{fmt(ssoThisPeriod)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8', fontSize: '0.68rem' }}
                          >
                            เกณฑ์กฎหมาย
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8', fontSize: '0.68rem' }}
                          >
                            83 – 875 บาท/เดือน
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                );
              };

              const buildNetContent = () => {
                const rowData = getValues
                  ? getValues(`salaryData.${fi}`) || {}
                  : {};
                const sal = parseFloat(
                  rowData?.revenue?.salary ?? ff?.totalAmount ?? 0,
                );
                const ot = parseFloat(rowData?.revenue?.overtime || 0);
                const all = parseFloat(
                  rowData?.revenue?.allowances ??
                    ff?.revenue?.allowances ??
                    ff?.total_extra_day ??
                    0,
                );
                const fieldAllow = parseFloat(
                  rowData?.revenue?.field_allowance || 0,
                );
                const bon = parseFloat(rowData?.revenue?.bonus || 0);
                const tax = parseFloat(
                  rowData?.expenses?.tax ?? ff?.expenses?.tax ?? 0,
                );
                const sso = parseFloat(
                  rowData?.expenses?.sso ?? ff?.expenses?.sso ?? 0,
                );
                const late = parseFloat(
                  rowData?.expenses?.late ?? ff?.expenses?.late ?? 0,
                );
                const oth = parseFloat(
                  rowData?.expenses?.other ?? ff?.expenses?.other ?? 0,
                );
                const totalIncome = sal + ot + all + fieldAllow + bon;
                const totalDeduct = tax + sso + late + oth;
                const net = totalIncome - totalDeduct;
                const incomeRows = [
                  { label: 'ค่าแรง', value: sal },
                  ot > 0 ? { label: 'OT', value: ot } : null,
                  all > 0 ? { label: 'เบี้ยเลี้ยง', value: all } : null,
                  fieldAllow > 0
                    ? { label: 'ค่าออกสนาม', value: fieldAllow }
                    : null,
                  bon > 0 ? { label: 'เงินได้อื่นๆ', value: bon } : null,
                ].filter(Boolean);
                const deductRows = [
                  tax > 0 ? { label: 'หักภาษี', value: tax } : null,
                  sso > 0 ? { label: 'ประกันสังคม', value: sso } : null,
                  late > 0 ? { label: 'หักสาย', value: late } : null,
                  oth > 0 ? { label: 'หักอื่นๆ', value: oth } : null,
                ].filter(Boolean);
                return (
                  <Box sx={{ minWidth: 230 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      รายได้
                    </Typography>
                    {incomeRows.map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.25,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#475569' }}>
                          {label}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: '#059669' }}
                        >
                          +{fmt(value)}
                        </Typography>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.25,
                        pt: 0.25,
                        borderTop: '1px dashed #e2e8f0',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        รวมรายได้
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#059669' }}
                      >
                        {fmt(totalIncome)}
                      </Typography>
                    </Box>
                    {deductRows.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 700,
                            display: 'block',
                            mt: 1,
                            mb: 0.5,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          หัก
                        </Typography>
                        {deductRows.map(({ label, value }) => (
                          <Box
                            key={label}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.25,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#475569' }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: '#dc2626' }}
                            >
                              −{fmt(value)}
                            </Typography>
                          </Box>
                        ))}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.25,
                            pt: 0.25,
                            borderTop: '1px dashed #e2e8f0',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            รวมหัก
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#dc2626' }}
                          >
                            −{fmt(totalDeduct)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.75,
                        pt: 0.75,
                        borderTop: '2px solid #334155',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ fontSize: '0.82rem' }}
                      >
                        สุทธิ
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          color: net >= 0 ? '#059669' : '#dc2626',
                          fontSize: '0.85rem',
                        }}
                      >
                        {net >= 0 ? '' : '−'}
                        {fmt(Math.abs(net))}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.5,
                        background: '#f8fafc',
                        borderRadius: 0.75,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', fontSize: '0.65rem' }}
                      >
                        {fmt(totalIncome)} − {fmt(totalDeduct)} = {fmt(net)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildActualSalaryContent = () => {
                let total = 0;
                const isFulltimeEmp = emp.emp_type === 'FULLTIME';
                // deduplicate: keep one checkin per date (last one wins)
                const seenAct = new Set();
                const uniqueAct = [];
                for (const ck of [...(emp.checkins || [])].reverse()) {
                  const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
                  if (!seenAct.has(dk)) {
                    seenAct.add(dk);
                    uniqueAct.unshift(ck);
                  }
                }
                const rows = [
                  ...uniqueAct
                    .map((ck) => {
                      const d2 = dayjs(ck.checkIn);
                      const ckDay = d2.date();
                      const lv = isFulltimeEmp
                        ? leaveByKey[`${empId}-${ckDay}`]
                        : null;
                      if (lv?.period === 'FULL_DAY') return null; // leave ครอบ — ไม่นับ checkin
                      const lbl = `${ckDay}/${month + 1} (${
                        DAY_TH_ABBR[d2.day()]
                      })`;
                      const er = ck.effectiveRate || ck.rate || 1;
                      let actualH =
                        ck.hour_override?.hours ?? ck.totalHour ?? 0;
                      if (lv) actualH = Math.min(actualH, 4); // half-day leave: cap ที่ 4h
                      const amt =
                        (actualH / 8) * er * (emp.salary_per_day || 0);
                      const hasOvr = ck.salary_override?.amount != null;
                      const sub = `${fmt(actualH, 2)}h × ×${er} × ฿${fmt(
                        emp.salary_per_day,
                        0,
                      )}/วัน`;
                      const note = hasOvr
                        ? `ค่าแรงปกติถูกแก้ไขเป็น ${fmt(
                            ck.salary_override.amount,
                          )} โดย ${ck.salary_override.edited_by || 'HR'}`
                        : er > 1
                        ? `[×${er} วันหยุด]`
                        : null;
                      const noteColor = hasOvr ? '#f59e0b' : '#10b981';
                      total += amt;
                      return bRow(lbl, sub, amt, note, noteColor);
                    })
                    .filter(Boolean),
                  // leave rows (FULLTIME only)
                  ...(!isFulltimeEmp
                    ? []
                    : days.reduce((acc, d2) => {
                        const lv = leaveByKey[`${empId}-${d2}`];
                        if (!lv) return acc;
                        const lvH = lv.period === 'FULL_DAY' ? 8 : 4;
                        const lvAmt = (lvH / 8) * (emp.salary_per_day || 0);
                        const d2js = dayjs(
                          `${year}-${String(month + 1).padStart(
                            2,
                            '0',
                          )}-${String(d2).padStart(2, '0')}`,
                        );
                        const lbl = `${d2}/${month + 1} (${
                          DAY_TH_ABBR[d2js.day()]
                        })`;
                        total += lvAmt;
                        acc.push(
                          bRow(
                            lbl,
                            `${lvH}h (ลา${
                              lv.period === 'FULL_DAY' ? 'เต็มวัน' : 'ครึ่งวัน'
                            }) × ฿${fmt(emp.salary_per_day, 0)}/วัน`,
                            lvAmt,
                            'ลา',
                            '#6366f1',
                          ),
                        );
                        return acc;
                      }, [])),
                  ...days.reduce((acc, d2) => {
                    const e2 = manualByKey[`${empId}-${d2}`];
                    if (!e2 || !e2.hours || e2.timestamp_ref) return acc;
                    const amt2 =
                      e2.amount != null
                        ? e2.amount
                        : (e2.hours / 8) * (emp.salary_per_day || 0);
                    total += amt2;
                    acc.push(
                      bRow(
                        `${d2}/${month + 1} (manual)`,
                        `${e2.hours}h ÷ 8 × ${fmt(emp.salary_per_day)}`,
                        amt2,
                        '✎ manual',
                        '#3b82f6',
                      ),
                    );
                    return acc;
                  }, []),
                ];
                return (
                  <Box sx={{ minWidth: 260 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      ใช้ชั่วโมงจริง (ไม่ min ที่ 8h) | hrRate ={' '}
                      {fmt(emp.salary_per_day)} ÷ 8 = {fmt(hrR)}
                    </Typography>
                    {rows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ไม่มีข้อมูล
                      </Typography>
                    ) : (
                      rows
                    )}
                    <Box
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        mt: 0.75,
                        pt: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        รวม
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#065f46' }}
                      >
                        {fmt(total)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildActualNetContent = () => {
                const rowData = getValues
                  ? getValues(`salaryData.${fi}`) || {}
                  : {};
                const actualSal = empSummaryMap[empId]?.actualSalaryAmt || 0;
                const ot = parseFloat(rowData?.revenue?.overtime || 0);
                const all = parseFloat(
                  rowData?.revenue?.allowances ??
                    ff?.revenue?.allowances ??
                    ff?.total_extra_day ??
                    0,
                );
                const fieldAllow = parseFloat(
                  rowData?.revenue?.field_allowance || 0,
                );
                const bon = parseFloat(rowData?.revenue?.bonus || 0);
                const tax = parseFloat(
                  rowData?.expenses?.tax ?? ff?.expenses?.tax ?? 0,
                );
                const sso = parseFloat(
                  rowData?.expenses?.sso ?? ff?.expenses?.sso ?? 0,
                );
                const oth = parseFloat(
                  rowData?.expenses?.other ?? ff?.expenses?.other ?? 0,
                );
                const totalIncome = actualSal + ot + all + fieldAllow + bon;
                const totalDeduct = tax + sso + oth;
                const net = totalIncome - totalDeduct;
                const incomeRows = [
                  { label: 'ค่าแรง (ชม.จริง)', value: actualSal },
                  ot > 0 ? { label: 'OT', value: ot } : null,
                  all > 0 ? { label: 'เบี้ยเลี้ยง', value: all } : null,
                  fieldAllow > 0
                    ? { label: 'ค่าออกสนาม', value: fieldAllow }
                    : null,
                  bon > 0 ? { label: 'เงินได้อื่นๆ', value: bon } : null,
                ].filter(Boolean);
                const deductRows = [
                  tax > 0 ? { label: 'หักภาษี', value: tax } : null,
                  sso > 0 ? { label: 'ประกันสังคม', value: sso } : null,
                  oth > 0 ? { label: 'หักอื่นๆ', value: oth } : null,
                ].filter(Boolean);
                return (
                  <Box sx={{ minWidth: 230 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 700,
                        display: 'block',
                        mb: 0.5,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      รายได้
                    </Typography>
                    {incomeRows.map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.25,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#475569' }}>
                          {label}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: '#059669' }}
                        >
                          +{fmt(value)}
                        </Typography>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.25,
                        pt: 0.25,
                        borderTop: '1px dashed #e2e8f0',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        รวมรายได้
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: '#059669' }}
                      >
                        {fmt(totalIncome)}
                      </Typography>
                    </Box>
                    {deductRows.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 700,
                            display: 'block',
                            mt: 1,
                            mb: 0.5,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          หัก (ไม่รวมหักสาย)
                        </Typography>
                        {deductRows.map(({ label, value }) => (
                          <Box
                            key={label}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.25,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#475569' }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: '#dc2626' }}
                            >
                              −{fmt(value)}
                            </Typography>
                          </Box>
                        ))}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.25,
                            pt: 0.25,
                            borderTop: '1px dashed #e2e8f0',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748b' }}
                          >
                            รวมหัก
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ color: '#dc2626' }}
                          >
                            −{fmt(totalDeduct)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 0.75,
                        pt: 0.75,
                        borderTop: '2px solid #334155',
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ fontSize: '0.82rem' }}
                      >
                        สุทธิ (ชม.จริง)
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          color: net >= 0 ? '#059669' : '#dc2626',
                          fontSize: '0.85rem',
                        }}
                      >
                        {net >= 0 ? '' : '−'}
                        {fmt(Math.abs(net))}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.5,
                        background: '#f8fafc',
                        borderRadius: 0.75,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8', fontSize: '0.65rem' }}
                      >
                        {fmt(totalIncome)} − {fmt(totalDeduct)} = {fmt(net)}
                      </Typography>
                    </Box>
                  </Box>
                );
              };

              const buildFieldAllowContent = () => {
                const faHrRate = faAmt / 8;
                let total = 0;
                const rows = [];
                const seenDates = new Set();
                const uniqueCheckins = [];
                (emp.checkins || []).forEach((ck) => {
                  const dk = dayjs(ck.checkIn).format('YYYY-MM-DD');
                  if (!seenDates.has(dk)) {
                    seenDates.add(dk);
                    uniqueCheckins.push(ck);
                  }
                });
                uniqueCheckins.forEach((ck) => {
                  const savedFA = ck.salary_override?.field_allowance_day;
                  const pn = (ck.project_number || '').toLowerCase().trim();
                  const defaultFA = matchesProjectPrefix(pn, faDefaultPrefixes);
                  const isFA = savedFA != null ? savedFA : defaultFA;
                  if (!isFA) return;
                  const d2 = dayjs(ck.checkIn);
                  const er = ck.effectiveRate || ck.rate || 1;
                  const actualH = ck.hour_override?.hours ?? ck.totalHour ?? 0;
                  const faHours = Math.min(actualH > 0 ? actualH : 8, 8);
                  const fieldAmt = Number((faHours * er * faHrRate).toFixed(2));
                  const day = d2.date();
                  const manEntry = manualByKey[`${empId}-${day}`];
                  const approvedOtH = ck.otRequests?.total_hours || 0;
                  const effH = ck.otEffectiveHours || 0;
                  let otHours = 0;
                  let otRate = 1.5;
                  let otFieldAmt = 0;
                  if (approvedOtH > 0) {
                    if (manEntry?.ot_hours) {
                      otHours = manEntry.ot_hours;
                      otRate = manEntry.ot_rate || 1.5;
                    } else {
                      otHours = approvedOtH;
                      otRate =
                        approvedOtH > 0
                          ? Math.round((effH / approvedOtH) * 100) / 100
                          : 1.5;
                    }
                  } else if (manEntry?.ot_hours) {
                    otHours = manEntry.ot_hours;
                    otRate = manEntry.ot_rate || 1.5;
                  }
                  if (otHours > 0)
                    otFieldAmt = Number(
                      (otHours * otRate * faHrRate).toFixed(2),
                    );
                  const lbl = `${day}/${month + 1} (${DAY_TH_ABBR[d2.day()]})`;
                  const dayTotal = fieldAmt + otFieldAmt;
                  total += dayTotal;
                  rows.push(
                    bRow(
                      lbl,
                      otHours > 0
                        ? `${faHours}h × ×${er} × ฿${fmt(
                            faHrRate,
                          )}/ชม. + OT ${fmt(otHours, 2)}h×${otRate}`
                        : `${faHours}h × ×${er} × ฿${fmt(faHrRate)}/ชม.`,
                      dayTotal,
                      null,
                      '#15803d',
                    ),
                  );
                });
                return (
                  <Box sx={{ minWidth: 260 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#94a3b8', display: 'block', mb: 0.75 }}
                    >
                      {`อัตรา ฿${fmt(faAmt, 0)}/วัน (฿${fmt(
                        faHrRate,
                      )}/ชม.) — mark จาก dialog แต่ละวัน`}
                    </Typography>
                    {rows.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        ยังไม่มีวันที่ mark ค่าออกสนาม
                      </Typography>
                    ) : (
                      rows
                    )}
                    {rows.length > 0 && (
                      <Box
                        sx={{
                          borderTop: '1px solid #e5e7eb',
                          mt: 0.75,
                          pt: 0.75,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          รวม {rows.length} วัน
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: '#15803d' }}
                        >
                          +{fmt(total)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              };

              return (
                <tr
                  key={idx}
                  style={{ background: rowBg }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = BG.rowHover;
                    e.currentTarget
                      .querySelectorAll('td[data-sticky]')
                      .forEach((td) => {
                        td.style.background = BG.rowHover;
                      });
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = rowBg;
                    e.currentTarget
                      .querySelectorAll('td[data-sticky]')
                      .forEach((td) => {
                        td.style.background = rowBg;
                      });
                  }}
                >
                  {/* ── info columns (sticky left) ── */}
                  <td
                    data-sticky="true"
                    style={td0({
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      background: rowBg,
                      width: LW.no,
                      minWidth: LW.no,
                      color: BG.textMuted,
                      fontWeight: 500,
                    })}
                  >
                    {idx + 1}
                  </td>
                  <td
                    data-sticky="true"
                    style={td0({
                      position: 'sticky',
                      left: LW.no,
                      zIndex: 10,
                      background: rowBg,
                      textAlign: 'left',
                      paddingLeft: 12,
                      paddingRight: 8,
                      borderRight: `1px solid ${BG.border}`,
                    })}
                  >
                    <FieldAllowanceBadge faEnabled={faEnabled} faAmt={faAmt} />
                    <div
                      style={{
                        fontWeight: 600,
                        color: BG.text,
                        maxWidth: 164,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <EmployeeName
                        id={emp.employee_id}
                        name={emp.name}
                        style={{ fontWeight: 600, color: BG.text }}
                      />
                    </div>
                    {ff?.role && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: BG.textMuted,
                          maxWidth: 164,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginTop: 1,
                        }}
                      >
                        {ff.role}
                      </div>
                    )}
                    <div
                      onClick={() => {
                        const dailyRows = buildDailyRowsForReport();
                        // sync computed totals → form (เฉพาะ field ที่ admin ยังไม่ override)
                        const mf = getValues(`salaryData.${fi}.manual_fields`) || [];
                        const cOt = dailyRows.reduce((s, r) => s + (r.otAmt || 0), 0);
                        const cFA = dailyRows.reduce((s, r) => s + (r.fieldAllowance || 0), 0);
                        if (!mf.includes('overtime'))
                          setValue(`salaryData.${fi}.revenue.overtime`, cOt.toFixed(2));
                        if (!mf.includes('field_allowance'))
                          setValue(`salaryData.${fi}.revenue.field_allowance`, cFA.toFixed(2));
                        setReportEmp({ emp, fi, dailyRows });
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 5,
                        cursor: 'pointer',
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: 6,
                        padding: '3px 8px',
                        color: '#334155',
                        fontSize: '11px',
                        fontWeight: 600,
                        userSelect: 'none',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#1e293b';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.borderColor = '#1e293b';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f1f5f9';
                        e.currentTarget.style.color = '#334155';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }}
                    >
                      <AssessmentIcon style={{ fontSize: 13 }} />
                      ดูรายงานค่าแรง
                    </div>
                  </td>
                  <td
                    data-sticky="true"
                    style={td0({
                      position: 'sticky',
                      left: LW.no + LW.name,
                      zIndex: 10,
                      background: rowBg,
                      padding: '8px 6px',
                      borderRight: `1px solid ${BG.border}`,
                      minWidth: LW.type,
                    })}
                  >
                    <div
                      style={{
                        borderBottom: `1px solid ${BG.border}`,
                        padding: '4px 2px',
                        marginBottom: 4,
                        fontSize: '12px',
                        color: BG.textMuted,
                        fontWeight: 400,
                      }}
                    >
                      ปกติ
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#EA580C',
                        fontWeight: 500,
                        padding: '2px 0',
                      }}
                    >
                      OT
                    </div>
                  </td>

                  {/* ── SCROLLABLE: day cells ── */}
                  {dayMeta.map(({ day, dow, isWeekend: we, isToday: td }) => {
                    const checkin = checkinByKey[`${empId}-${day}`] || null;
                    const leave = getLeaveForDay(empId, day);
                    const ovHours = checkin?.salary_override?.hours;
                    const ovRate = checkin?.salary_override?.rate;
                    const nh = checkin
                      ? ovHours ?? checkin.totalHour ?? 0
                      : null;
                    const eff =
                      ovRate ?? checkin?.effectiveRate ?? checkin?.rate ?? 1;
                    const nv = nh != null && nh !== 0 ? nh : null;
                    const ov = checkin
                      ? parseFloat(
                          checkin.otRequests?.total_hours?.toFixed(2),
                        ) || null
                      : null;
                    const oeh = checkin?.otEffectiveHours || 0;
                    const isOvr =
                      checkin?.salary_override?.amount != null ||
                      checkin?.time_override != null ||
                      checkin?.project_override != null;
                    const isHourOverridden =
                      checkin?.hour_override?.hours != null;
                    const ovrTip = isOvr
                      ? checkin?.salary_override?.amount != null
                        ? `แก้ไขโดย: ${checkin.salary_override.edited_by}${
                            checkin.salary_override.edited_at
                              ? ` (${dayjs(
                                  checkin.salary_override.edited_at,
                                ).format('DD/MM/YY HH:mm')})`
                              : ''
                          }`
                        : checkin?.time_override != null
                        ? `แก้ไขเวลา โดย: ${checkin.time_override.edited_by}`
                        : `แก้ไขโปรเจค โดย: ${checkin.project_override.edited_by}`
                      : '';
                    const mh = getManualHours(empId, day);
                    const hasMn = mh > 0;
                    const manualOtH = getManualOtHours(empId, day);
                    const manualOtRate =
                      manualOtH > 0
                        ? getEntry(empId, day)?.ot_rate || 1.5
                        : 1.5;
                    const manualOtEffH = manualOtH * manualOtRate;
                    const lc = leave
                      ? LEAVE_COLOR[leave.leave_type] || LEAVE_COLOR.PERSONAL
                      : null;
                    const isHalfLeave = leave && leave.period !== 'FULL_DAY';
                    // ช่วงที่ไม่ได้ลา (ใช้แสดงให้ HR รู้ว่าควรมีการลงเวลาช่วงไหน)
                    const nonLeavePeriod = isHalfLeave
                      ? leave.period === 'MORNING'
                        ? 'บ่าย'
                        : 'เช้า'
                      : null;
                    // ตรวจสอบว่า checkin อยู่ในช่วงลาหรือไม่ (เช้า < 12:00 / บ่าย >= 12:00)
                    const checkinHour = checkin
                      ? dayjs(checkin.checkIn).hour()
                      : null;
                    const isCheckinInLeavePeriod =
                      isHalfLeave && checkin != null
                        ? leave.period === 'MORNING'
                          ? checkinHour < 12
                          : checkinHour >= 12
                        : null;
                    // ชั่วโมงลา auto สำหรับ FULLTIME: เต็มวัน=8h, ครึ่งวัน=4h
                    const leaveAutoH = (() => {
                      if (!leave || emp.emp_type !== 'FULLTIME') return 0;
                      if (leave.period === 'FULL_DAY' && !checkin) return 8;
                      if (
                        leave.period === 'MORNING' &&
                        (!checkin || checkinHour >= 12)
                      )
                        return 4;
                      if (
                        leave.period === 'AFTERNOON' &&
                        (!checkin || checkinHour < 12)
                      )
                        return 4;
                      return 0;
                    })();
                    let cellBg;
                    if (leave && !checkin) cellBg = lc?.bg;
                    else if (isHalfLeave && checkin) cellBg = lc?.bg;
                    else if (isOvr) cellBg = '#fef3c7';
                    else if (hasMn) cellBg = '#dbeafe';
                    else if (td) cellBg = 'rgba(251,191,36,0.08)';
                    else if (we) cellBg = weekendCellBg(dow);
                    const leavePeriodLabel = leave
                      ? leave.period === 'FULL_DAY'
                        ? ' (เต็มวัน)'
                        : leave.period === 'MORNING'
                        ? ' (ครึ่งวันเช้า)'
                        : ' (ครึ่งวันบ่าย)'
                      : '';
                    const leaveTip = leave
                      ? [
                          `ลา${
                            LEAVE_ABBR[leave.leave_type] || leave.leave_type
                          }${leavePeriodLabel}`,
                          leave.reason ? `เหตุผล: ${leave.reason}` : '',
                          isHalfLeave && !checkin
                            ? `⚠ ไม่พบการลงเวลาช่วง${nonLeavePeriod}`
                            : '',
                          isHalfLeave &&
                          checkin &&
                          isCheckinInLeavePeriod === true
                            ? `⚠ ลงเวลาในช่วงลา (${checkin.checkInTime})`
                            : '',
                          isHalfLeave &&
                          checkin &&
                          isCheckinInLeavePeriod === false
                            ? `✓ ลงเวลาช่วง${nonLeavePeriod} (${checkin.checkInTime})`
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' | ')
                      : '';
                    const weekendTone =
                      we && !leave && !checkin && !hasMn && !isOvr && !td;
                    return (
                      <td
                        key={day}
                        style={dayTdBase({
                          background: cellBg,
                          borderRight: `1px solid ${
                            (leave && !checkin) || isHalfLeave
                              ? lc?.border
                              : BG.border
                          }`,
                          boxShadow: td
                            ? 'inset 0 0 0 1px #f59e0b'
                            : leave && !checkin
                            ? `inset 0 0 0 1px ${lc?.border}`
                            : isHalfLeave && checkin
                            ? `inset 3px 0 0 0 ${lc?.border}`
                            : undefined,
                        })}
                      >
                        {leave && !checkin ? (
                          /* ── วันลาอนุมัติ (ไม่มี checkin) ── */
                          <Tooltip
                            title={`${leaveTip} | คลิกขวาเพื่อดูรายละเอียด`}
                            placement="top"
                            arrow
                            disableInteractive
                            enterDelay={300}
                          >
                            <div
                              style={dayCellInner({
                                cursor: 'context-menu',
                                borderBottom: `1px solid ${lc?.border}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                              })}
                              onContextMenu={(e) =>
                                openDv(
                                  e,
                                  null,
                                  emp.name,
                                  null,
                                  emp.salary_per_day,
                                  leave,
                                  leaveAutoH,
                                  day,
                                )
                              }
                            >
                              <span
                                style={{
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  color: lc?.text,
                                  letterSpacing: '-0.02em',
                                }}
                              >
                                {LEAVE_ABBR[leave.leave_type] || 'ลา'}
                              </span>
                              {leaveAutoH > 0 && (
                                <span
                                  style={{
                                    fontSize: '0.6rem',
                                    color: lc?.text,
                                    opacity: 0.9,
                                    marginLeft: 1,
                                  }}
                                >
                                  {leaveAutoH}h
                                </span>
                              )}
                              {isHalfLeave && leaveAutoH === 0 && (
                                <span
                                  style={{
                                    fontSize: '0.55rem',
                                    color: lc?.text,
                                    opacity: 0.7,
                                  }}
                                >
                                  ½
                                </span>
                              )}
                              {isHalfLeave && (
                                <span
                                  style={{
                                    fontSize: '0.6rem',
                                    color: '#dc2626',
                                    fontWeight: 800,
                                    lineHeight: 1,
                                  }}
                                >
                                  ✗
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        ) : checkin ? (
                          /* ── มี checkin ปกติ (อาจมีลาด้วย แสดง badge เล็ก) ── */
                          <Tooltip
                            title={
                              leave
                                ? `${
                                    isOvr ? ovrTip : 'รายละเอียดการลงเวลา'
                                  } | ลา${
                                    LEAVE_ABBR[leave.leave_type]
                                  }${leavePeriodLabel}`
                                : isOvr
                                ? ovrTip
                                : 'คลิกเพื่อดูรายละเอียด / แก้ไขค่าแรง'
                            }
                            placement="top"
                            arrow
                            disableInteractive
                            enterDelay={300}
                          >
                            <div
                              style={dayCellInner({
                                borderBottom: `1px solid ${BG.border}`,
                                cursor: 'pointer',
                                color: isOvr
                                  ? '#b45309'
                                  : weekendTone
                                  ? BG.weekendText
                                  : BG.text,
                                fontWeight: isOvr ? 700 : 500,
                                position: 'relative',
                              })}
                              onContextMenu={(e) =>
                                openDv(
                                  e,
                                  checkin,
                                  emp.name,
                                  getEntry(empId, day),
                                  emp.salary_per_day,
                                  leave,
                                  leaveAutoH,
                                )
                              }
                              onClick={() =>
                                openDetail(
                                  checkin,
                                  emp.name,
                                  emp.salary_per_day,
                                  !!checkin?.skip_late_deduct,
                                  empId,
                                )
                              }
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = isOvr
                                  ? 'rgba(245,158,11,0.15)'
                                  : 'rgba(99,102,241,0.08)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '';
                              }}
                            >
                              {nv != null || leaveAutoH > 0 ? (
                                <>
                                  {leaveAutoH > 0
                                    ? (nv != null ? nv * eff : 0) + leaveAutoH
                                    : nv}
                                  {leaveAutoH === 0 && eff > 1 && nv != null && (
                                    <span
                                      style={{
                                        fontSize: '0.6em',
                                        color: '#9ca3af',
                                        marginLeft: 1,
                                      }}
                                    >
                                      (
                                      {Number.isInteger(nv * eff)
                                        ? nv * eff
                                        : (nv * eff).toFixed(2)}
                                      )
                                    </span>
                                  )}
                                  {leaveAutoH > 0 && (
                                    <span
                                      style={{
                                        fontSize: '0.55em',
                                        color: lc?.text,
                                        marginLeft: 1,
                                      }}
                                    >
                                      +{leaveAutoH}
                                    </span>
                                  )}
                                  {isOvr && (
                                    <span
                                      style={{
                                        fontSize: '0.6em',
                                        marginLeft: 2,
                                      }}
                                    >
                                      ✎
                                    </span>
                                  )}

                                  {!isHourOverridden &&
                                    checkin?.deductionReasons?.includes(
                                      'no_checkout',
                                    ) && (
                                      <span
                                        style={{
                                          fontSize: '0.6em',
                                          color: '#ef4444',
                                          marginLeft: 2,
                                        }}
                                      >
                                        อ
                                      </span>
                                    )}
                                </>
                              ) : (
                                <span style={{ visibility: 'hidden' }}>-</span>
                              )}
                              {leave && (
                                <span
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 1,
                                    fontSize: '0.5rem',
                                    color: lc?.text,
                                    fontWeight: 800,
                                    lineHeight: 1,
                                    background: lc?.bg,
                                    border: `1px solid ${lc?.border}`,
                                    padding: '1px 2px',
                                    borderRadius: 2,
                                  }}
                                >
                                  {isHalfLeave ? '½ลา' : 'ลา'}
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        ) : (
                          /* ── ว่าง: เพิ่มชั่วโมงด้วยตนเอง ── */
                          <Tooltip
                            title={
                              hrMgmt
                                ? hasMn
                                  ? `${mh} ชม. (กดเพื่อแก้ไข)`
                                  : 'กดเพื่อเพิ่มชั่วโมง'
                                : ''
                            }
                            placement="top"
                            arrow
                            disableInteractive
                            enterDelay={300}
                          >
                            <div
                              style={dayCellInner({
                                borderBottom: `1px solid ${BG.border}`,
                                cursor: hrMgmt ? 'pointer' : 'default',
                                color: weekendTone ? BG.weekendText : BG.text,
                              })}
                              onClick={
                                hrMgmt
                                  ? () =>
                                      openAddHours(
                                        empId,
                                        emp.name,
                                        day,
                                        emp.salary_per_day,
                                        emp.checkins?.find(
                                          (c) => (c.extra_day || 0) > 0,
                                        )?.extra_day || 0,
                                      )
                                  : undefined
                              }
                              onMouseEnter={(e) => {
                                if (hrMgmt)
                                  e.currentTarget.style.background =
                                    'rgba(37,99,235,0.08)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '';
                              }}
                            >
                              {hasMn ? (
                                <span
                                  style={{ fontWeight: 700, color: '#1d4ed8' }}
                                >
                                  {mh}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: '#d1d5db',
                                    fontSize: '0.85em',
                                  }}
                                >
                                  —
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={
                            hrMgmt && !(leave && !checkin)
                              ? ov || manualOtH
                                ? 'คลิกเพื่อแก้ไข OT | คลิกขวาเพื่อดูรายละเอียด'
                                : 'คลิกเพื่อเพิ่ม OT'
                              : ov || manualOtH
                              ? 'คลิกขวาเพื่อดูรายละเอียด OT'
                              : ''
                          }
                          placement="bottom"
                          arrow
                          disableInteractive
                          enterDelay={300}
                        >
                          <div
                            style={dayCellInner({
                              fontSize: '12px',
                              cursor:
                                hrMgmt && !(leave && !checkin)
                                  ? 'pointer'
                                  : 'default',
                              color: weekendTone ? BG.weekendText : undefined,
                            })}
                            onClick={
                              hrMgmt && !(leave && !checkin)
                                ? () =>
                                    openAddOt(
                                      empId,
                                      emp.name,
                                      day,
                                      emp.salary_per_day,
                                      ov ? { hours: ov } : null,
                                    )
                                : undefined
                            }
                            onContextMenu={(e) =>
                              (checkin?.otRequests?.total_hours ||
                                manualOtH > 0) &&
                              openOtDv(
                                e,
                                checkin?.otRequests || null,
                                getEntry(empId, day),
                                emp.name,
                                checkin,
                                day,
                              )
                            }
                            onMouseEnter={(e) => {
                              if (hrMgmt && !(leave && !checkin))
                                e.currentTarget.style.background =
                                  'rgba(234,88,12,0.08)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '';
                            }}
                          >
                            {manualOtH > 0 ? (
                              <span
                                style={{ color: '#ea580c', fontWeight: 700 }}
                              >
                                {manualOtH}✎
                                {Math.abs(manualOtEffH - manualOtH) > 0.01 && (
                                  <span
                                    style={{
                                      fontSize: '0.6em',
                                      color: '#9ca3af',
                                      marginLeft: 1,
                                    }}
                                  >
                                    ({manualOtEffH.toFixed(1)})
                                  </span>
                                )}
                              </span>
                            ) : ov ? (
                              <span
                                style={{ color: '#ea580c', fontWeight: 500 }}
                              >
                                {ov}
                                {oeh > 0 && Math.abs(oeh - ov) > 0.01 && (
                                  <span
                                    style={{
                                      fontSize: '0.6em',
                                      color: '#9ca3af',
                                      marginLeft: 1,
                                    }}
                                  >
                                    ({oeh.toFixed(1)})
                                  </span>
                                )}
                              </span>
                            ) : leave && !checkin && !isHalfLeave ? (
                              <span
                                style={{
                                  color: lc?.text,
                                  fontSize: '0.6rem',
                                  opacity: 0.8,
                                }}
                              >
                                {emp.emp_type === 'FULLTIME' ? '8h' : '1d'}
                              </span>
                            ) : leave && !checkin && isHalfLeave ? (
                              <span
                                style={{
                                  color: '#dc2626',
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                }}
                              >
                                {emp.emp_type === 'FULLTIME' ? '4h ' : ''}
                                {nonLeavePeriod}✗
                              </span>
                            ) : isHalfLeave && checkin ? (
                              <span
                                style={{
                                  color: isCheckinInLeavePeriod
                                    ? '#dc2626'
                                    : '#16a34a',
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                }}
                              >
                                {nonLeavePeriod}
                                {isCheckinInLeavePeriod ? '⚠' : '✓'}
                              </span>
                            ) : (
                              <span
                                style={{ color: '#e5e7eb', fontSize: '0.85em' }}
                              >
                                —
                              </span>
                            )}
                          </div>
                        </Tooltip>
                      </td>
                    );
                  })}

                  {/* ── RIGHT STICKY: Working ── */}
                  <td
                    style={{
                      ...td0({
                        borderRight: `1px solid ${BG.border}`,
                        ...(hrMgmt ? stickyRight(R.working) : stickyRight(0)),
                        fontWeight: 500,
                      }),
                      ...(hrMgmt ? {} : {}),
                    }}
                  >
                    <div
                      style={{
                        borderBottom: `1px solid ${BG.border}`,
                        padding: '4px 2px',
                        marginBottom: 4,
                        color: BG.text,
                        fontWeight: 500,
                      }}
                    >
                      {Number.isInteger(displayCheck)
                        ? displayCheck
                        : displayCheck.toFixed(1)}
                      {Math.abs(displayCheckEff - displayCheck) > 0.01 && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: BG.textMuted,
                            marginLeft: 2,
                          }}
                        >
                          (
                          {Number.isInteger(displayCheckEff)
                            ? displayCheckEff
                            : displayCheckEff.toFixed(1)}
                          )
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#EA580C',
                        fontWeight: 500,
                        padding: '2px 0',
                      }}
                    >
                      {displayNetOtH > 0 || emp.totalOtHour ? (
                        <>
                          {displayNetOtH > 0
                            ? displayNetOtH.toFixed(1)
                            : emp.totalOtHour.toFixed(1)}
                          {Math.abs(displayNetOtEffH - displayNetOtH) >
                            0.01 && (
                            <span
                              style={{
                                fontSize: '11px',
                                color: BG.textMuted,
                                marginLeft: 2,
                              }}
                            >
                              ({displayNetOtEffH.toFixed(1)})
                            </span>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </div>
                  </td>

                  {/* ── RIGHT STICKY: financial columns ── */}
                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          borderRight: `1px solid ${BG.border}`,
                          fontWeight: 500,
                          color: BG.text,
                          padding: '8px 8px',
                        }),
                        ...stickyRight(R.dayRate),
                      }}
                    >
                      <div style={{ fontWeight: 500, color: BG.text }}>
                        {fmt(emp.salary_per_day)}
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          color: BG.subText,
                          marginTop: 2,
                        }}
                      >
                        ({fmt(emp.salary_per_day / 8, 2)}/ชม.)
                      </div>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'work-time' && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          fontWeight: 500,
                          padding: '8px 6px',
                        }),
                        ...stickyRight(R.salary),
                        background:
                          manualAmt > 0
                            ? 'rgba(37,99,235,0.05)'
                            : 'transparent',
                        color: manualAmt > 0 ? '#1D4ED8' : BG.text,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-end',
                          gap: 0.25,
                        }}
                      >
                        <Box sx={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 500 }}>
                            {fmt(displayAmt)}
                          </div>
                          <div
                            style={{
                              fontSize: '11px',
                              color: BG.subText,
                              fontWeight: 400,
                              marginTop: 2,
                            }}
                          >
                            {displayCheckEff > 0
                              ? `${
                                  Number.isInteger(displayCheckEff)
                                    ? displayCheckEff
                                    : displayCheckEff.toFixed(2)
                                }×${fmt(emp.salary_per_day, 0)}`
                              : '—'}
                          </div>
                        </Box>
                        {showFormulas && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              '&:hover': { color: '#475569' },
                              mt: '1px',
                              flexShrink: 0,
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรค่าแรง',
                                content: buildSalaryContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'late-deduct' && (
                    <td
                      style={{
                        ...td0({
                          textAlign: 'right',
                          fontWeight: 500,
                          padding: '8px 6px',
                        }),
                        ...stickyRight(R.actualSalary),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-end',
                          gap: 0.25,
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>
                          {fmt(empSummaryMap[empId]?.actualSalaryAmt || 0)}
                        </div>
                        {showFormulas && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              '&:hover': { color: '#475569' },
                              mt: '1px',
                              flexShrink: 0,
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรค่าแรง (ชม.จริง)',
                                content: buildActualSalaryContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '3px' }),
                        ...stickyRight(R.ot),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.overtime`}
                            control={control}
                            defaultValue={
                              (ff?.revenue?.overtime != null
                                ? Number(ff.revenue.overtime).toFixed(2)
                                : null) ??
                              (ff?.totalOtAmount != null
                                ? Number(ff.totalOtAmount).toFixed(2)
                                : null) ??
                              '0.00'
                            }
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput()}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-ot`,
                                    'overtime',
                                    'revenue.overtime',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'revenue.overtime',
                                    'overtime',
                                    `${empId}-ot`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span
                            style={{ fontSize: '0.75rem', color: '#64748b' }}
                          >
                            {fmt(emp.totalOtAmount)}
                          </span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="overtime"
                          />
                        )}
                        {showFormulas && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              flexShrink: 0,
                              '&:hover': { color: '#475569' },
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตร OT',
                                content: buildOtContent(),
                              })
                            }
                          >
                            <span style={{ fontSize: '0.7rem', lineHeight: 1 }}>
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '3px' }),
                        ...stickyRight(R.allow),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.allowances`}
                            control={control}
                            defaultValue={
                              (ff?.revenue?.allowances != null
                                ? Number(ff.revenue.allowances).toFixed(2)
                                : null) ??
                              (ff?.total_extra_day != null
                                ? Number(ff.total_extra_day).toFixed(2)
                                : null) ??
                              '0.00'
                            }
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput()}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-allow`,
                                    'allowances',
                                    'revenue.allowances',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'revenue.allowances',
                                    'allowances',
                                    `${empId}-allow`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="allowances"
                          />
                        )}
                        {showFormulas && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              flexShrink: 0,
                              '&:hover': { color: '#0369a1' },
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรเบี้ยเลี้ยง',
                                content: buildAllowanceContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          padding: '3px',
                          borderRight: `1px solid ${BG.border}`,
                        }),
                        ...stickyRight(R.bonus),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.field_allowance`}
                            control={control}
                            defaultValue={Number(
                              ff?.revenue?.field_allowance || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput()}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-fa`,
                                    'field_allowance',
                                    'revenue.field_allowance',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'revenue.field_allowance',
                                    'field_allowance',
                                    `${empId}-fa`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="field_allowance"
                          />
                        )}
                        {showFormulas && fi !== undefined && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              flexShrink: 0,
                              '&:hover': { color: '#15803d' },
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรค่าออกสนาม',
                                content: buildFieldAllowContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          padding: '6px 4px',
                          borderRight: `1px solid ${BG.border}`,
                        }),
                        ...stickyRight(R.bonus),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.revenue.bonus`}
                            control={control}
                            defaultValue={Number(
                              ff?.revenue?.bonus || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput()}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-bon`,
                                    'bonus',
                                    'revenue.bonus',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'revenue.bonus',
                                    'bonus',
                                    `${empId}-bon`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="bonus"
                          />
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.tax),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.tax`}
                            control={control}
                            defaultValue={Number(
                              CalTax(
                                ff?.salary_per_day * ff?.totalWork,
                                ff?.tax_type,
                              ) || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c')}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-tax`,
                                    'tax',
                                    'expenses.tax',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.tax',
                                    'tax',
                                    `${empId}-tax`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="tax"
                          />
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.sso),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.sso`}
                            control={control}
                            defaultValue={
                              ff?.expenses?.sso ??
                              (CalSSO(
                                ff?.salary_per_day * ff?.totalWork,
                                ff?.sso_tax,
                                ff?.sso_paid,
                              ) ||
                                0)
                            }
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c')}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-sso`,
                                    'sso',
                                    'expenses.sso',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.sso',
                                    'sso',
                                    `${empId}-sso`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="sso"
                          />
                        )}
                        {showFormulas && fi !== undefined && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              flexShrink: 0,
                              '&:hover': { color: '#b91c1c' },
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรประกันสังคม',
                                content: buildSsoContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'work-time' && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.late),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.late`}
                            control={control}
                            defaultValue={Number(
                              ff?.expenses?.late || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c')}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-late`,
                                    'late',
                                    'expenses.late',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.late',
                                    'late',
                                    `${empId}-late`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="late"
                          />
                        )}
                        {showFormulas && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              flexShrink: 0,
                              '&:hover': { color: '#ea580c' },
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรหักสาย',
                                content: buildLateContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({
                          padding: '6px 4px',
                          borderRight: `1px solid ${BG.border}`,
                        }),
                        ...stickyRight(R.other),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        {fi !== undefined && control ? (
                          <Controller
                            name={`salaryData.${fi}.expenses.other`}
                            control={control}
                            defaultValue={Number(
                              ff?.expenses?.other || 0,
                            ).toFixed(2)}
                            render={({ field: cf }) => (
                              <input
                                {...cf}
                                type="number"
                                step="0.01"
                                min="0"
                                style={mkInput('#b91c1c')}
                                onChange={(e) =>
                                  _handleChange(
                                    e,
                                    cf,
                                    `${empId}-oth`,
                                    'other',
                                    'expenses.other',
                                  )
                                }
                                onBlur={(e) =>
                                  _handleBlur(
                                    e,
                                    cf,
                                    'expenses.other',
                                    'other',
                                    `${empId}-oth`,
                                  )
                                }
                              />
                            )}
                          />
                        ) : (
                          <span>-</span>
                        )}
                        {fi !== undefined && control && (
                          <ManualDot
                            control={control}
                            fi={fi}
                            fieldName="other"
                          />
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'work-time' && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.net),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.25,
                        }}
                      >
                        <Box sx={{ textAlign: 'right', fontWeight: 700 }}>
                          {fi !== undefined && (
                            <NetCell
                              control={control}
                              fi={fi}
                              fields={fields}
                            />
                          )}
                        </Box>
                        {showFormulas && fi !== undefined && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              flexShrink: 0,
                              '&:hover': { color: '#7c3aed' },
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรคำนวณสุทธิ',
                                content: buildNetContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && salaryMode !== 'late-deduct' && (
                    <td
                      style={{
                        ...td0({ padding: '6px 4px' }),
                        ...stickyRight(R.actualNet),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.25,
                        }}
                      >
                        <Box sx={{ textAlign: 'right', fontWeight: 700 }}>
                          {fi !== undefined && (
                            <ActualNetCell
                              control={control}
                              fi={fi}
                              fields={fields}
                              actualSalaryAmt={
                                empSummaryMap[empId]?.actualSalaryAmt || 0
                              }
                            />
                          )}
                        </Box>
                        {showFormulas && fi !== undefined && (
                          <IconButton
                            size="small"
                            sx={{
                              p: '1px',
                              color: '#94a3b8',
                              flexShrink: 0,
                              '&:hover': { color: '#7c3aed' },
                            }}
                            onClick={(ev) =>
                              setCalcPopover({
                                anchor: ev.currentTarget,
                                title: 'สูตรคำนวณสุทธิ (ชม.จริง)',
                                content: buildActualNetContent(),
                              })
                            }
                          >
                            <span
                              style={{ fontSize: '0.65rem', lineHeight: 1 }}
                            >
                              ⓘ
                            </span>
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '3px' }),
                        ...stickyRight(R.note),
                      }}
                    >
                      {fi !== undefined && control ? (
                        <Controller
                          name={`salaryData.${fi}.note`}
                          control={control}
                          defaultValue={ff?.note || ''}
                          render={({ field: cf }) => (
                            <input
                              {...cf}
                              type="text"
                              placeholder="หมายเหตุ..."
                              style={{
                                width: '100%',
                                border: `1px solid ${BG.border}`,
                                borderRadius: 4,
                                padding: '2px 6px',
                                fontSize: '0.72rem',
                                outline: 'none',
                                color: '#475569',
                                fontFamily: 'inherit',
                              }}
                              onChange={(e) => {
                                cf.onChange(e);
                                clearTimeout(
                                  adjDebounceRef.current[`${empId}-note`],
                                );
                                adjDebounceRef.current[
                                  `${empId}-note`
                                ] = setTimeout(() => onAdjSave?.(empId), 1200);
                              }}
                              onBlur={(e) => {
                                cf.onBlur(e);
                                clearTimeout(
                                  adjDebounceRef.current[`${empId}-note`],
                                );
                                onAdjSave?.(empId);
                              }}
                            />
                          )}
                        />
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                  )}

                  {hrMgmt && (
                    <td
                      style={{
                        ...td0({ padding: '2px', borderRight: 'none' }),
                        ...stickyRight(R.manage),
                      }}
                    >
                      {fi !== undefined && remove && (
                        <Tooltip title="ลบออกจากรายการ">
                          <IconButton
                            size="small"
                            onClick={() => remove(fi)}
                            sx={{
                              p: 0.25,
                              color: 'error.light',
                              '&:hover': { color: 'error.main' },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {/* ── footer totals row ── */}
            <tr style={{ background: BG.footer }}>
              <td
                colSpan={3 + days.length}
                style={{
                  padding: '10px 12px',
                  textAlign: 'right',
                  fontWeight: 700,
                  fontSize: '14px',
                  color: BG.headerText,
                  background: BG.footer,
                  borderTop: `2px solid ${BG.border}`,
                  borderRight: `1px solid ${BG.border}`,
                }}
              >
                รวมทั้งหมด
              </td>
              <td
                style={{
                  padding: '10px 8px',
                  textAlign: 'center',
                  fontWeight: 700,
                  lineHeight: 1.5,
                  background: BG.footer,
                  borderTop: `2px solid ${BG.border}`,
                  borderRight: `1px solid ${BG.border}`,
                }}
              >
                <div
                  style={{
                    color: BG.headerText,
                    fontSize: '14px',
                    fontWeight: 700,
                  }}
                >
                  {totalsBase.work > 0
                    ? Number.isInteger(totalsBase.work)
                      ? totalsBase.work
                      : totalsBase.work.toFixed(1)
                    : '—'}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#EA580C',
                    fontWeight: 500,
                    marginTop: 2,
                  }}
                >
                  0.0
                </div>
              </td>
              {/* 1. Day Rate */}
              {hrMgmt && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                />
              )}
              {/* 2. Salary */}
              {hrMgmt && salaryMode !== 'work-time' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: BG.headerText,
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  {fmt(totalsBase.salary)}
                </td>
              )}
              {/* 3. Actual Salary */}
              {hrMgmt && salaryMode !== 'late-deduct' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: BG.headerText,
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  {fmt(totalsBase.actualSalary)}
                </td>
              )}
              {/* 4. OT */}
              {hrMgmt && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: BG.headerText,
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  {fmt(totalsBase.ot)}
                </td>
              )}
              {/* 5-9. Allowance, Field, Bonus, Tax, SSO */}
              {hrMgmt &&
                [0, 1, 2, 3, 4].map((i) => (
                  <td
                    key={`ft-allow-sso-${i}`}
                    style={{
                      background: BG.footer,
                      borderTop: `2px solid ${BG.border}`,
                      borderRight: `1px solid ${BG.border}`,
                    }}
                  />
                ))}
              {/* 10. Late Deduct */}
              {hrMgmt && salaryMode !== 'work-time' && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                />
              )}
              {/* 11. Other Deduct */}
              {hrMgmt && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                />
              )}
              {/* 12. Net */}
              {hrMgmt && salaryMode !== 'work-time' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalNetCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                  />
                </td>
              )}
              {/* 13. Actual Net */}
              {hrMgmt && salaryMode !== 'late-deduct' && (
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'right',
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                >
                  <TotalActualNetCell
                    control={control}
                    fields={fields}
                    fieldIndexMap={fieldIndexMap}
                    timestamp={timestamp}
                    empSummaryMap={empSummaryMap}
                  />
                </td>
              )}
              {/* 14. Note */}
              {hrMgmt && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: `1px solid ${BG.border}`,
                  }}
                />
              )}
              {/* 15. Manage */}
              {hrMgmt && (
                <td
                  style={{
                    background: BG.footer,
                    borderTop: `2px solid ${BG.border}`,
                    borderRight: 'none',
                  }}
                />
              )}
            </tr>
          </tbody>
        </table>
      </Box>

      {/* ── LEGEND ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 2,
          py: 1.25,
          flexWrap: 'wrap',
          alignItems: 'center',
          mt: 0.5,
          borderRadius: '0 0 8px 8px',
          bgcolor: '#F9FAFB',
          borderTop: `1px solid ${BG.border}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: BG.textMuted, fontWeight: 500, mr: 0.5 }}
        >
          สัญลักษณ์:
        </Typography>
        {[
          { color: '#fef9c3', border: '#d97706', label: 'แก้ไขค่าแรงแล้ว' },
          {
            color: '#dbeafe',
            border: '#3b82f6',
            label: 'เพิ่มชั่วโมงด้วยตนเอง',
          },
          {
            color: 'rgba(251,191,36,0.14)',
            border: '#f59e0b',
            label: 'วันนี้',
          },
          { color: BG.saturdayBg, border: '#FECACA', label: 'เสาร์' },
          { color: BG.sundayBg, border: '#FCA5A5', label: 'อาทิตย์' },
          {
            color: LEAVE_COLOR.SICK.bg,
            border: LEAVE_COLOR.SICK.border,
            label: 'ลาป่วย',
          },
          {
            color: LEAVE_COLOR.PERSONAL.bg,
            border: LEAVE_COLOR.PERSONAL.border,
            label: 'ลากิจ',
          },
          {
            color: LEAVE_COLOR.VACATION.bg,
            border: LEAVE_COLOR.VACATION.border,
            label: 'ลาพักร้อน',
          },
        ].map(({ color, border, label }) => (
          <Box
            key={label}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '3px',
                bgcolor: color,
                border: `1.5px solid ${border}`,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: BG.textMuted, fontSize: '11px' }}
            >
              {label}
            </Typography>
          </Box>
        ))}
        <Typography
          variant="caption"
          sx={{ color: BG.textMuted, fontStyle: 'italic', fontSize: '11px' }}
        >
          ✎ = มีการแก้ไขค่าแรง
        </Typography>
      </Box>

      {/* ── Calculation breakdown Popover ── */}
      <Popover
        open={!!calcPopover.anchor}
        anchorEl={calcPopover.anchor}
        onClose={closeCp}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { p: 1.75, maxWidth: 380, boxShadow: 4, borderRadius: 2 },
          },
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1, color: '#1e293b' }}
        >
          {calcPopover.title}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {calcPopover.content}
      </Popover>

      {/* ── OT Detail Dialog ── */}
      <Dialog
        open={otDvOpen}
        onClose={() => setOtDvOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          },
        }}
      >
        {(() => {
          const ot = otDvData?.otReq;
          const mn = otDvData?.manualEntry;
          const checkin = otDvData?.checkin;
          const hasApproved = ot?.total_hours > 0;
          const hasManual = mn?.ot_hours > 0;
          const hrRate = (checkin?.salary_per_day || 0) / 8;

          // approved OT
          const otAmt = hasApproved
            ? ot.total_price || ot.total_hours * (ot.rate || 1.5) * hrRate
            : 0;
          const otRateNum = hasApproved ? ot.rate || 1.5 : 0;
          const otRateLabel =
            otRateNum >= 3 ? '×3.0' : otRateNum >= 2 ? '×2.0' : '×1.5';
          const otRateSub =
            otRateNum >= 3
              ? 'วันหยุดนักขัตฤกษ์'
              : otRateNum >= 2
              ? 'วันหยุด'
              : 'วันทำงาน';

          // manual OT
          const mnRate = hasManual ? mn.ot_rate || 1.5 : 0;
          const mnAmt = hasManual
            ? mn.ot_amount != null
              ? mn.ot_amount
              : mn.ot_hours * mnRate * hrRate
            : 0;
          const mnRateLabel =
            mnRate >= 3 ? '×3.0' : mnRate >= 2 ? '×2.0' : '×1.5';
          const mnRateSub =
            mnRate >= 3
              ? 'วันหยุดนักขัตฤกษ์'
              : mnRate >= 2
              ? 'วันหยุด'
              : 'วันทำงาน';

          const dateStr = otDvData?.day
            ? `${String(otDvData.day).padStart(2, '0')}/${String(
                month + 1,
              ).padStart(2, '0')}/${year}`
            : '';

          return (
            <>
              {/* ── Header gradient ── */}
              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, #ea580c 0%, #f97316 60%, #fb923c 100%)',
                  px: 3,
                  pt: 2.5,
                  pb: 3,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* decorative circles */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.08)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -30,
                    right: 40,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.06)',
                  }}
                />

                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.85rem', lineHeight: 1 }}>
                          ⏱
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: '#fff',
                          letterSpacing: '0.01em',
                        }}
                      >
                        รายละเอียด OT
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        color: 'rgba(255,255,255,0.9)',
                        fontWeight: 600,
                        mt: 0.25,
                      }}
                    >
                      {otDvData?.empName}
                    </Typography>
                    {dateStr && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: 'rgba(255,255,255,0.7)',
                          mt: 0.25,
                        }}
                      >
                        📅 {dateStr}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 0.5,
                    }}
                  >
                    {hasApproved && (
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          borderRadius: 10,
                          border: '1px solid rgba(255,255,255,0.3)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            color: '#fff',
                            letterSpacing: '0.04em',
                          }}
                        >
                          ✓ อนุมัติ
                        </Typography>
                      </Box>
                    )}
                    {hasManual && (
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          bgcolor: 'rgba(255,255,255,0.15)',
                          borderRadius: 10,
                          border: '1px solid rgba(255,255,255,0.25)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            color: '#fff',
                            letterSpacing: '0.04em',
                          }}
                        >
                          ✎ HR
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Stack>

                {/* ── Key stats row ── */}
                {(hasApproved || hasManual) && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {[
                      {
                        label: 'ชั่วโมง OT',
                        value: hasApproved
                          ? `${ot.total_hours}`
                          : `${mn.ot_hours}`,
                        unit: 'ชม.',
                      },
                      {
                        label: 'อัตรา',
                        value: hasApproved ? otRateLabel : mnRateLabel,
                        unit: hasApproved ? otRateSub : mnRateSub,
                      },
                      {
                        label: 'ค่า OT',
                        value: `฿${(hasApproved ? otAmt : mnAmt).toLocaleString(
                          'th-TH',
                          {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          },
                        )}`,
                        unit: '',
                      },
                    ].map(({ label, value, unit }) => (
                      <Box
                        key={label}
                        sx={{
                          flex: 1,
                          bgcolor: 'rgba(255,255,255,0.15)',
                          borderRadius: 1.5,
                          p: 1,
                          textAlign: 'center',
                          backdropFilter: 'blur(4px)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: 'rgba(255,255,255,0.75)',
                            fontWeight: 500,
                            letterSpacing: '0.04em',
                            mb: 0.25,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 800,
                            color: '#fff',
                            lineHeight: 1.1,
                          }}
                        >
                          {value}
                        </Typography>
                        {unit && (
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              color: 'rgba(255,255,255,0.7)',
                              mt: 0.1,
                            }}
                          >
                            {unit}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <DialogContent sx={{ p: 0 }}>
                {/* ── OT อนุมัติ ── */}
                {hasApproved &&
                  (() => {
                    const timeStr =
                      ot.startTime && ot.endTime
                        ? `${dayjs(ot.startTime).format('HH:mm')} — ${dayjs(
                            ot.endTime,
                          ).format('HH:mm')}`
                        : null;
                    const projectStr = checkin?.project_name
                      ? `${checkin.project_name}${
                          checkin.project_number
                            ? ` (${checkin.project_number})`
                            : ''
                        }`
                      : null;
                    const requesterName =
                      ot.created_by?.display_name || ot.employee_name || null;

                    const rows = [
                      projectStr && {
                        icon: '🏗',
                        label: 'โครงการ',
                        value: projectStr,
                      },
                      timeStr && {
                        icon: '🕐',
                        label: 'เวลา OT',
                        value: timeStr,
                      },
                      ot.description && {
                        icon: '📝',
                        label: 'รายละเอียด',
                        value: ot.description,
                      },
                      requesterName && {
                        icon: '👤',
                        label: 'ผู้ขอ OT',
                        value: requesterName,
                      },
                      ot.createdAt && {
                        icon: '📅',
                        label: 'วันที่ขอ',
                        value: dayjs(ot.createdAt).format('DD MMM YY, HH:mm'),
                      },
                      ot.last_edited_by?.display_name && {
                        icon: '✎',
                        label: 'แก้ไขโดย',
                        value: `${ot.last_edited_by.display_name}${
                          ot.last_edited_at
                            ? ` · ${dayjs(ot.last_edited_at).format(
                                'DD/MM/YY',
                              )}`
                            : ''
                        }`,
                      },
                    ].filter(Boolean);

                    return (
                      <Box sx={{ px: 2.5, pt: 2, pb: hasManual ? 1.5 : 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: '#ea580c',
                            letterSpacing: '0.06em',
                            fontSize: '0.65rem',
                          }}
                        >
                          OT APPROVED
                        </Typography>
                        <Stack spacing={0} sx={{ mt: 1 }}>
                          {rows.map(({ icon, label, value }, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                py: 1,
                                borderBottom:
                                  i < rows.length - 1
                                    ? '1px solid #f3f4f6'
                                    : 'none',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  mt: '1px',
                                  flexShrink: 0,
                                  width: 18,
                                  textAlign: 'center',
                                }}
                              >
                                {icon}
                              </Typography>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: '0.65rem',
                                    color: '#9ca3af',
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                  }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.82rem',
                                    color: '#111827',
                                    fontWeight: 500,
                                    mt: 0.1,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {value}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Stack>

                        {/* amount breakdown pill */}
                        <Box
                          sx={{
                            mt: 1.5,
                            px: 2,
                            py: 1.25,
                            bgcolor: '#fff7ed',
                            borderRadius: 1.5,
                            border: '1px solid #fed7aa',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '0.72rem', color: '#9a3412' }}
                          >
                            {ot.total_hours} ชม. × {otRateLabel} × ฿
                            {hrRate.toFixed(2)}/ชม.
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.9rem',
                              fontWeight: 800,
                              color: '#c2410c',
                            }}
                          >
                            ฿
                            {otAmt.toLocaleString('th-TH', {
                              minimumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}

                {/* ── Divider between sections ── */}
                {hasApproved && hasManual && (
                  <Box sx={{ px: 2.5 }}>
                    <Divider sx={{ borderStyle: 'dashed' }}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#9ca3af', fontSize: '0.65rem', px: 0.5 }}
                      >
                        และ
                      </Typography>
                    </Divider>
                  </Box>
                )}

                {/* ── Manual OT โดย HR ── */}
                {hasManual &&
                  (() => (
                    <Box sx={{ px: 2.5, pt: hasApproved ? 1.5 : 2, pb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: '#1d4ed8',
                          letterSpacing: '0.06em',
                          fontSize: '0.65rem',
                        }}
                      >
                        HR MANUAL OT
                      </Typography>
                      <Stack spacing={0} sx={{ mt: 1 }}>
                        {[
                          {
                            icon: '⏱',
                            label: 'ชั่วโมง OT',
                            value: `${mn.ot_hours} ชม.`,
                          },
                          {
                            icon: '📊',
                            label: 'อัตรา',
                            value: `${mnRateLabel} ${mnRateSub}`,
                          },
                          {
                            icon: '👤',
                            label: 'บันทึกโดย',
                            value: mn.edited_by || '—',
                          },
                        ].map(({ icon, label, value }, i, arr) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              py: 1,
                              borderBottom:
                                i < arr.length - 1
                                  ? '1px solid #f3f4f6'
                                  : 'none',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.85rem',
                                mt: '1px',
                                flexShrink: 0,
                                width: 18,
                                textAlign: 'center',
                              }}
                            >
                              {icon}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.65rem',
                                  color: '#9ca3af',
                                  fontWeight: 600,
                                  letterSpacing: '0.04em',
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.82rem',
                                  color: '#111827',
                                  fontWeight: 500,
                                  mt: 0.1,
                                }}
                              >
                                {value}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                      <Box
                        sx={{
                          mt: 1.5,
                          px: 2,
                          py: 1.25,
                          bgcolor: '#eff6ff',
                          borderRadius: 1.5,
                          border: '1px solid #bfdbfe',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{ fontSize: '0.72rem', color: '#1e40af' }}
                        >
                          {mn.ot_hours} ชม. × {mnRateLabel} × ฿
                          {hrRate.toFixed(2)}/ชม.
                          {mn.ot_amount != null && (
                            <span style={{ marginLeft: 4, opacity: 0.7 }}>
                              (กำหนดเอง)
                            </span>
                          )}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 800,
                            color: '#1d4ed8',
                          }}
                        >
                          ฿
                          {mnAmt.toLocaleString('th-TH', {
                            minimumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  ))()}

                {!hasApproved && !hasManual && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '2rem', mb: 1 }}>🕐</Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      ไม่มีข้อมูล OT
                    </Typography>
                  </Box>
                )}
              </DialogContent>

              <Box
                sx={{
                  px: 3,
                  pb: 2.5,
                  pt: 0.5,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={() => setOtDvOpen(false)}
                  variant="contained"
                  size="small"
                  sx={{
                    bgcolor: '#ea580c',
                    '&:hover': { bgcolor: '#c2410c' },
                    borderRadius: 1.5,
                    px: 2.5,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    boxShadow: '0 2px 8px rgba(234,88,12,0.35)',
                  }}
                >
                  ปิด
                </Button>
              </Box>
            </>
          );
        })()}
      </Dialog>

      {/* ── Employee Salary Report Modal ── */}
      {reportEmp && (
        <EmployeeSalaryReport
          open={!!reportEmp}
          onClose={() => setReportEmp(null)}
          emp={reportEmp.emp}
          month={month}
          year={year}
          manualWorkData={manualWorkData}
          leaveData={leaveData}
          payrollRow={
            reportEmp.fi !== undefined
              ? getValues
                ? getValues(`salaryData.${reportEmp.fi}`) ||
                  fields[reportEmp.fi]
                : fields[reportEmp.fi]
              : undefined
          }
          attendanceRules={attendanceRules}
          days={days}
          selectedOption={selectedOption}
          precomputedRows={reportEmp.dailyRows}
          salaryMode={salaryMode}
          onProjectClick={(pid) => setPopupProjectId(pid)}
          onResetAll={({ ot, fieldAllowance }) => {
            if (!reportEmp || reportEmp.fi === undefined) return;
            const fi = reportEmp.fi;
            const empId = String(reportEmp.emp.employee_id);
            const av = autoValuesRef.current[empId] || {};
            // ใช้ค่าจาก popup (computed) สำหรับ OT + field_allowance
            setValue(`salaryData.${fi}.revenue.overtime`, Number(ot).toFixed(2));
            setValue(`salaryData.${fi}.revenue.field_allowance`, Number(fieldAllowance).toFixed(2));
            // field อื่น (bonus, allowances) reset กลับ auto
            setValue(`salaryData.${fi}.revenue.bonus`, Number(av.bonus ?? 0).toFixed(2));
            setValue(`salaryData.${fi}.revenue.allowances`, Number(av.allowances ?? 0).toFixed(2));
            // ล้าง manual_fields ทั้งหมด
            setValue(`salaryData.${fi}.manual_fields`, []);
            onAdjSave?.(empId);
          }}
        />
      )}

      {/* Project Popup — เปิดจาก EmployeeSalaryReport คลิก JS number */}
      {popupProjectId && (
        <Dialog
          open={!!popupProjectId}
          onClose={() => setPopupProjectId(null)}
          maxWidth="xl"
          fullWidth
          sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
          PaperProps={{ sx: { mt: '40px', maxHeight: 'calc(100vh - 56px)', minWidth: '50vw' } }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProjectPopup
              propId={popupProjectId}
              onClose={() => setPopupProjectId(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default React.memo(MergedPayrollTable);
